// Composition root (Warring Isles). Boots Pixi, generates the hex world, wires the
// race-select screen, the turn-based UI (top bar + region panel), and a
// render-on-demand loop. Phase A: see the world, pick a race, select regions,
// end rounds. Economy/units/combat/tech/diplomacy/AI arrive in later phases.

import './ui/styles.css';
import { Ticker } from 'pixi.js';
import { audio } from './audio/audio';
import { initPlatform, noteMatchStarted, shouldShowInterstitial, type Lifecycle } from './platform/crazygames';
import { itchIdentity, itchLogin, itchLogout, itchSyncProfile, initItchCloud } from './platform/itch';
import { loadGame, saveGame, savedCampaignIndex, clearSave, loadProfile, recordResult, recordOnlineResult, markOnboarded, addElmas, markCampaignGuided, purchaseCosmetic, equipCosmetic, recordCampaignStars, claimDaily } from './platform/storage';
import { CAMPAIGN_GUIDES } from './ui/campaignGuides';
import { CAMPAIGN, ACT_NAMES, goalText, ELMAS_PER_STAR, starsFor, isUnlocked, currentLevel, totalStars, beatenCount, type LevelKit } from './meta/campaign';
import { buildCampaignLevel } from './meta/scenarios';
import { newlyUnlocked, completed, nextLocked, achReward, type Achievement } from './game/achievements';
import { matchElmas, matchAdBonus } from './meta/economy';
import { FRAMES, AVATARS, SETS_ALL, DEFAULT_SET, frameClass, frameEffect, avatarSrc, type CatalogItem } from './meta/catalog';
import { startTutorial } from './ui/tutorial';
import { attachEffectFrame, motionAllowed } from './ui/effectFrame';
import { createApp } from './render/app';
import { createLayers } from './render/layers';
import { createCamera } from './render/Camera';
import { buildHexMapView } from './render/HexMapView';
import { loadSprites } from './render/sprites';
import {
  unitMaxHp,
  unitMoves,
  createGame,
  createMatch,
  endTurn,
  runTurnGen,
  turnOrderOf,
  regionCounts,
  conquerableTotal,
  capitalsHeld,
  factionIncome,
  canBuildMarket,
  startMarket,
  canBuildHarbor,
  startHarbor,
  harborCost,
  canBuildBarracks,
  canMuster,
  barracksCost,
  islandHasHarbor,
  isCoastal,
  marketCost,
  canTrain,
  parleys,
  canStillAct,
  sharesWater,
  SEA_RANGE,
  hasArrow,
  startTrain,
  unitCost,
  unitUnlocked,
  unitCap,
  unitCount,
  UNIT_TURNS,
  unitTargets,
  voyageTargets,
  moveOrAttack,
  previewCombat,
  TECHS,
  techById,
  techStatus,
  canResearch,
  startResearch,
  runAiTurn,
  NO_WAR_ROUNDS,
  STOCK_CAP,
  lastStandSoftFor,
  proposeAlliance,
  proposeTrade,
  tradeTakers,
  executeTrade,
  respondOffer,
  checkTradeOffer,
  areAllied,
  atWar,
  declareWar,
  makePeace,
  canDeclareWar,
  canMakePeace,
  peaceCooldownLeft,
  warCooldownLeft,
  allyCooldownLeft,
  checkGameEnd,
  aiTakeover,
  reclaimSeat,
} from './game/engine';
import { hexDistance } from './game/hex';
import type { TechBranch } from './game/engine';
import { RACES, type RaceDef } from './game/factions';
import { RES_ICON, RES_NAME, NEUTRAL, BUILDING, impassable, BUILDING_ICON, BUILDING_NAME, UNIT_ICON, UNIT_NAME } from './game/types';
import type { CombatPreview, CombatResult } from './game/engine';
import { applyCommand, type Command, type CommandResult } from './game/command';
import { hashState, restoreSnapshot, snapshotState } from './game/snapshot';
import { joinRoom, makeRoomCode, type NetRoom, type RoomCallbacks } from './net/room';
import { CLOSE_KICKED, CLOSE_REPLACED, type LobbyState, type MatchSettings, type ServerMsg } from './net/protocol';
import type { FactionId, GameState, RegionId, UnitType, RegionSnapshot, ReplayEvent, DiploOffer, Difficulty, MapKind, Voyage } from './game/types';
import { MAP_SEEDS, pickMapSeed } from './game/mapseeds';
/** DEV GALLERY (?maplab): every curated board from game/mapseeds.ts, grouped by configuration. Picking
 *  a card stashes wi.maplab and reloads clean; the boot honours the flag and starts that exact seed.
 *  Ships in prod on purpose: it is reachable only by typing the query, and it leaks nothing (players
 *  get these same boards from the pools anyway). Styled inline so it depends on no page CSS. */
function showMapLab(): void {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'position:fixed;inset:0;overflow:auto;background:#0f2233;color:#f0e2c4;font-family:Nunito,Segoe UI,sans-serif;z-index:9999;padding:32px';
  const h = document.createElement('h1');
  h.textContent = '🗺️ Map Lab · curated boards';
  h.style.cssText = 'margin:0 0 4px;color:#f5d78e;font-size:26px';
  const sub = document.createElement('div');
  sub.textContent = 'Pick a board to start it solo with size-typical seats (3/4/5). Custom Battle and Multiplayer draw randomly from these same pools.';
  sub.style.cssText = 'opacity:.8;margin-bottom:20px;font-size:14px';
  wrap.append(h, sub);
  const order: Array<[string, string]> = [
    ['isles/8', 'Isles · Small (3 seats)'],
    ['isles/10', 'Isles · Medium (4 seats)'],
    ['isles/12', 'Isles · Large (5 seats)'],
    ['land/8', 'Mainland · Small (3 seats)'],
    ['land/10', 'Mainland · Medium (4 seats)'],
    ['land/12', 'Mainland · Large (5 seats)'],
  ];
  for (const [key, label] of order) {
    const pool = MAP_SEEDS[key];
    if (!pool) continue;
    const head = document.createElement('h2');
    head.textContent = label;
    head.style.cssText = 'margin:18px 0 8px;color:#e0a93f;font-size:17px';
    wrap.appendChild(head);
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap';
    for (const entry of pool) {
      const card = document.createElement('button');
      card.style.cssText = 'flex:0 0 250px;text-align:left;background:#241a10;border:1px solid #5a4426;border-radius:10px;padding:12px 14px;color:#f0e2c4;cursor:pointer';
      const nm = document.createElement('div');
      nm.textContent = entry.name;
      nm.style.cssText = 'font-weight:800;color:#f5d78e;margin-bottom:4px';
      const bl = document.createElement('div');
      bl.textContent = entry.blurb;
      bl.style.cssText = 'font-size:12.5px;opacity:.85;min-height:34px';
      const sd = document.createElement('div');
      sd.textContent = 'seed ' + entry.seed;
      sd.style.cssText = 'font-size:11px;opacity:.5;margin-top:6px';
      card.append(nm, bl, sd);
      card.addEventListener('mouseenter', () => (card.style.borderColor = '#e0a93f'));
      card.addEventListener('mouseleave', () => (card.style.borderColor = '#5a4426'));
      card.addEventListener('click', () => {
        const [kind, size] = key.split('/');
        sessionStorage.setItem('wi.maplab', JSON.stringify({ seed: entry.seed, kind, size: Number(size), heart: !!entry.heart }));
        location.href = location.pathname; // strip ?maplab and boot into the pick
      });
      row.appendChild(card);
    }
    wrap.appendChild(row);
  }
  const back = document.createElement('button');
  back.textContent = '◀ Back to the game';
  back.style.cssText = 'margin-top:26px;background:#d9a743;border:0;border-radius:8px;padding:10px 18px;font-weight:800;cursor:pointer';
  back.addEventListener('click', () => (location.href = location.pathname));
  wrap.appendChild(back);
  document.body.appendChild(wrap);
}


// One random seed per page load, used only by the guided flows (tutorial funnel, express rematch).
// REAL matches (Instant/Custom/MP lobby) draw a CURATED board instead: see game/mapseeds.ts. The RNG
// lives only in the UI layer either way; the engine stays fully deterministic for any given seed.
const SEED = (Math.random() * 0x7fffffff) >>> 0;
const HEX_SIZE = 32;
// One-shot session flags around the tutorial's reload boundaries (a reload is the cheap safe route out of
// a chapter, given how much live game state the page holds). 'wi.postTutorial' boots straight into a real
// match, 'wi.showChapters' lands the boot on the chapter list, 'wi.funnelChapter' (value = chapter id)
// chains the first-run funnel from one chapter into the next without ever showing the list.
const takeSessionFlag = (key: string): string | null => {
  const v = sessionStorage.getItem(key);
  if (v != null) sessionStorage.removeItem(key);
  return v;
};
const setSessionFlag = (key: string, value = '1'): void => sessionStorage.setItem(key, value);
// Set once initPlatform() resolves, so the menu-reachable meta screens (Profile / Shop) can read the
// CrazyGames account (login / username / avatar) without threading `lifecycle` through every call.
let platform: Lifecycle | undefined;
// Match setup (chosen on the New Game screen; these are the defaults). Playtesting: a lone player vs 4 allied
// AIs on big islands is eliminated ~85% of the time; 3-way (you + two rivals) on mid-size islands roughly halves
// elimination, makes wins reachable, and fits one sitting. Island size matters a LOT: smaller islands are far
// more winnable (less slow neutral-grabbing, quicker to contest).
// DEFAULTS raised to 4 factions / Large for the CrazyGames launch: average playtime is a scored KPI and the
// 3/medium/total-conquest match ran ~4.7min vs ~7-9min at 4/large (all-AI round measurement, scripts/
// lengthtest.ts). The anti-snowball pact + Last Stand shield carry the difficulty; Small/3 stays selectable.
const DEFAULT_FACTIONS = 4;
const MAP_SIZES: ReadonlyArray<{ label: string; size: number }> = [
  // one step up across the board (7/9/11 -> 8/10/12): at 7 tiles every island was too small for ANY
  // terrain character (cliffs need 8+), so the small boards were interchangeable. Campaign levels keep
  // their own sizes; these are the free-play options.
  { label: 'Small', size: 8 },
  { label: 'Medium', size: 10 },
  { label: 'Large', size: 12 },
];
// legacy sizes (7/9/11) can still arrive from an old MP worker's room settings or persisted rooms:
// map each to the shipped size above it so labels, curated pools and worldgen always agree
const normalizeIslandSize = (s: number): number => ([8, 10, 12].includes(s) ? s : s <= 8 ? 8 : s <= 10 ? 10 : 12);
// TRUE on the real CrazyGames host (or under the ?cgboot test override): gates the portal-only
// first-minute funnel cuts. itch and our own mirrors keep the classic flow.
const onCrazyGames = (): boolean => window.location.hostname.includes('crazygames') || new URLSearchParams(window.location.search).has('cgboot');
const DEFAULT_MAP_INDEX = 2; // Large (playtime KPI; see DEFAULT_FACTIONS note above)
// Win condition is FIXED at total conquest (dominationPct 1): take every region, or wipe out every rival.
// The old 60% option ended matches in ~3 minutes; CrazyGames Basic Launch scores average playtime, and a
// single short match per session was starving it, so the choice was removed everywhere (solo, hotseat, MP).

async function main(): Promise<void> {
  const appRoot = document.getElementById('app');
  const uiRoot = document.getElementById('ui');
  if (!appRoot || !uiRoot) throw new Error('missing #app or #ui root');

  audio.init(); // arms the first-gesture unlock; menu music is queued below and starts on that click
  if (import.meta.env.DEV) (window as unknown as { __audio: typeof audio }).__audio = audio;
  // a soft wooden click on any interactive control: real buttons, plus the clickable non-button
  // widgets (research tech nodes `.clickable`, the "You" chip, overview region rows). Game-action
  // sounds (build/move/attack) fire separately, on the map.
  const CLICKABLE_SEL = 'button:not(:disabled), [class*="clickable"], .ov-idcell';
  document.addEventListener(
    'pointerdown',
    (e) => {
      if ((e.target as HTMLElement | null)?.closest(CLICKABLE_SEL)) audio.sfx('click');
    },
    true,
  );
  // suppress the native browser context menu everywhere (menus, panels, the whole page). Right-click has no
  // menu role in the game; the map keeps its own right-click shortcut (deselect) via its own handler below.
  document.addEventListener('contextmenu', (e) => e.preventDefault());

  // start Pixi (renderer chunk fetch + WebGL context) in parallel with the SDK init below: the two are
  // independent, and serializing them added their sum to every boot's path to gameplayStart. The catch
  // pre-marks the promise handled so a WebGL failure during the SDK wait cannot fire a spurious
  // unhandledrejection; the await further down still rethrows exactly as before.
  const appPromise = createApp(appRoot);
  void appPromise.catch(() => {});
  const lifecycle = await initPlatform();
  platform = lifecycle; // expose to the menu-reachable Profile / Shop screens
  // an itch-linked device quietly refreshes its cloud profile at boot (pull-merge-push) and arms the
  // debounced push on every later profile write; guests and CrazyGames sessions no-op instantly
  if (!onCrazyGames()) initItchCloud();
  // CrazyGames "play with friends": a mid-session invite click reloads into that room (the boot flow
  // then auto-joins wi.cgJoin). A reload is the clean way to drop whatever the player was in and land
  // fresh in the friend's lobby.
  lifecycle.onJoinRoom((code) => {
    setSessionFlag('wi.cgJoin', code);
    window.location.reload();
  });
  lifecycle.loadingStart();

  const app = await appPromise;
  const layers = createLayers();
  app.stage.addChild(layers.world);
  // All map input is handled by native canvas pointer listeners (see below) that hit-test with view.pick(),
  // NOT by Pixi's federated events, so nothing on the map should ever be an event target. Disable Pixi's
  // event system entirely: no overlay sprite, unit, badge, or label can ever intercept or swallow a click,
  // and hit-testing is skipped for a small perf win. A tile is selectable/actionable from ANY pixel over it.
  app.stage.eventMode = 'none';
  app.stage.interactiveChildren = false;
  const camera = createCamera(app.canvas);
  const view = buildHexMapView(layers, HEX_SIZE, () => me()); // lazily: me() exists by first render

  let needsRender = true;
  const requestRender = (): void => {
    needsRender = true;
  };

  // load the InvokeAI-generated race sprites in the background; the map redraws with real
  // artwork once they arrive (vector icons show until then).
  void loadSprites(loadProfile().equipped.skin).then((store) => {
    view.setSprites(store);
    requestRender();
  });

  // --- render-on-demand loop ---
  const ticker = new Ticker();
  ticker.add(() => {
    if (view.animateSelection(ticker.deltaMS)) needsRender = true; // pulse the active-unit tile
    if (camera.dirty) {
      camera.apply(layers.world);
      camera.dirty = false;
      // island names hold a constant screen size and fade away at fighting zoom (player feedback:
      // a name must never sit over a unit, a health bar or a camp someone is trying to read)
      view.setLabelZoom(camera.getView().scale, fitScale());
      needsRender = true;
    }
    if (needsRender) {
      app.render();
      needsRender = false;
    }
  });
  ticker.start();

  lifecycle.loadingStop();

  // --- main menu → race selection ---
  audio.playMusic('menu'); // queued behind the first gesture (autoplay policy); plays through race select
  const saved = loadGame(); // a valid mid-game save (null if none) → the menu offers "Continue"
  // a persistent backdrop behind every setup screen, so the menu → race-select → game fades never flash the
  // empty blue canvas; it is removed the moment the game is ready to render.
  const menuBackdrop = el('div', 'menu-backdrop');
  uiRoot.appendChild(menuBackdrop);
  // Everyone starts at the main menu. A brand-new player pressing Play gets ONE gentle offer of the
  // guided tutorial (never forced; either answer marks the device onboarded so it never asks again);
  // "How to Play" on the menu stays as the replay entry.
  // The setup phase is a loop: main menu → (New Game → race select) → game. Backing out of race select
  // returns here to the menu instead of trapping the player in setup, so "New Game" is never a one-way door.
  let game: GameState;
  let resumed = false;
  const tutorialMode = false as boolean; // the chapter system is gone (Conquest Trail 2.0): the campaign guides teach in-level; flag kept so the many guards below stay readable
  // instant-play dropped a brand-new device straight onto the board: offer the lesson with a dismissible
  // in-game card once the opening reveal is done, instead of a dialog that blocks the first frame
  // Conquest Trail: index of the campaign level being played, or null for a free-play match. Drives the
  // star rating + "Next level" button on the results screen.
  let campaignIndex: number | null = null;
  // Conquest Trail 2.0: which systems this campaign level's UI leaves OPEN. The trail teaches one
  // system per level, so an untaught system's buttons must not exist on that level's screen; free
  // play (campaignIndex null) and levels without a kit open everything.
  const levelKit = (): Required<LevelKit> => {
    const k = campaignIndex != null ? CAMPAIGN[campaignIndex]?.kit : undefined;
    return { market: k?.market ?? true, barracks: k?.barracks ?? true, harbor: k?.harbor ?? true, research: k?.research ?? true, diplomacy: k?.diplomacy ?? true };
  };
  /** Build a campaign level's match: fixed seed and setup, so a level is the same battlefield for
   *  everyone; staged levels then get their scenario dressing (castles, garrisons) applied on top. */
  const campaignGame = (i: number): GameState => buildCampaignLevel(i);
  let net: OnlineStart | null = null; // live online session (room + fixed local seat); null = local play
  // online: the socket is down. dispatch() refuses while set, because anything played now would fork the
  // local state from the room's timeline and be discarded by the eventual rejoin resync anyway.
  let netDown = false;
  // a FIXED, deterministic scenario per chapter (always the Orcs, the chapter's vetted seed, player
  // forced first in the turn order so no AI opening-replay plays before the guided steps begin)
  // rolling out of the tutorial: its completion button promised "your first conquest", so land straight
  // in a fresh match as the race they just learned with (no menu, no re-choosing)
  const maplabPick = takeSessionFlag('wi.maplab');
  if (new URLSearchParams(location.search).has('maplab')) {
    showMapLab(); // the gallery owns the screen; every exit is a reload, so the boot simply stops here
    return;
  }
  // the first-run funnel chains chapter → chapter across a reload without ever showing the list
  // finishing (or skipping) a chapter from the menu path lands back on the chapter list
  // "Play Again" express rematch: the results screen stamped the finished match's setup here before
  // reloading, so the player lands straight in a fresh match (same race + settings, new map) instead of
  // being dropped back through menu -> race select -> settings at their highest-intent moment.
  const quickNext = takeSessionFlag('wi.quickNext');
  // campaign: the results screen's "Next level" / "Retry" stamps the index here and reloads, so the player
  // goes straight from one isle to the next with no menu in between (the whole point of a progression run)
  const campNext = takeSessionFlag('wi.campaignLevel');
  // same rule as quickNext below: a pending friend-invite join (wi.cgJoin, settable during the results
  // interstitial) ALWAYS outranks a queued campaign level - honouring campNext would swallow the invite
  // AND leave the join flag stale to auto-join a dead room later. With cgJoin present, campNext is
  // already consumed above, so we simply fall through to the join branch.
  if (campNext != null && CAMPAIGN[Number(campNext)] && !sessionStorage.getItem('wi.cgJoin')) {
    campaignIndex = Number(campNext);
    game = campaignGame(campaignIndex);
  } else if (maplabPick) {
    // the ?maplab gallery picked a board: boot that exact seed with size-typical seats (dev/demo flow)
    const cfg = JSON.parse(maplabPick) as { seed: number; kind: MapKind; size: number; heart?: boolean };
    const fc = cfg.size <= 8 ? 3 : cfg.size <= 10 ? 4 : 5;
    game = createGame(cfg.seed, 'human', fc, cfg.size, 1, 'normal', cfg.kind, true, !!cfg.heart, true);
  } else if (quickNext && !sessionStorage.getItem('wi.cgJoin')) {
    // a pending friend-invite join ALWAYS outranks an express rematch: the invite can land during the
    // Play Again interstitial (both flags set on this boot), and honouring quickNext would swallow the
    // player's most recent, deliberate intent AND leave wi.cgJoin stale to auto-join a dead room later.
    // With cgJoin present, quickNext is already consumed above, so we simply fall through to the join.
    const [race, fc, isz, , diff, mk] = quickNext.split(','); // 4th field (dominationPct) ignored: always total conquest now
    // "fresh map" means a fresh CURATED board: draw from the pool like every other free-play entry,
    // so the rematch keeps cliffs/heart character instead of falling back to a flat random world
    const nextKind: MapKind = mk === 'land' ? 'land' : 'isles'; // 6th field absent on stale flags -> classic isles
    const nextSize = [7, 8, 9, 10, 11, 12].includes(Number(isz)) ? Number(isz) : 10;
    const nextBoard = pickMapSeed(nextKind, nextSize, Math.random());
    game = createGame(
      nextBoard.seed,
      RACES.some((r) => r.key === race) ? race! : 'orc',
      Math.min(5, Math.max(3, Number(fc) || 3)),
      nextSize,
      1,
      diff === 'hard' ? 'hard' : 'normal',
      nextKind,
      true,
      nextBoard.heart ?? false,
      true,
    );
  } else {
    // CrazyGames "play with friends": an Instant-Multiplayer launch or an invite link (fresh load, or a
    // mid-session invite that reloaded into wi.cgJoin) jumps straight into online, skipping the menu. The
    // result is consumed on the first loop iteration below (so `game` is always assigned via the loop).
    // A mid-session invite / Rematch sets wi.cgJoin then reloads: a fresh, DELIBERATE re-entry, consumed
    // here so it fires exactly once.
    const cgJoinFlag = takeSessionFlag('wi.cgJoin');
    // The launch-context signals (isInstantMultiplayer, the invite-link param) are re-read IDENTICALLY on
    // every reload of the same iframe URL, so without a guard a game-over "Leave" -> reload would trap the
    // player re-entering online forever. Honour them once per tab session, then only an explicit wi.cgJoin
    // flag re-enters online.
    const cgLaunched = sessionStorage.getItem('wi.cgAutoEntered') === '1';
    const cgLaunchJoin = cgLaunched ? null : (platform?.joinRoomCode() ?? null);
    const cgInstant = !cgLaunched && !!platform?.isInstantMultiplayer();
    const cgJoin = cgJoinFlag ?? cgLaunchJoin;
    let autoStart: Awaited<ReturnType<typeof showOnlineLobby>> = null;
    if (cgJoin || cgInstant) {
      sessionStorage.setItem('wi.cgAutoEntered', '1'); // consumed: a later in-session reload won't auto-enter again
      autoStart = await showOnlineLobby(uiRoot, cgJoin ? { join: cgJoin } : { create: true });
    }
    // A fresh load lands on the MAIN MENU, on every platform. (An auto-into-a-match experiment was
    // tried twice for the portals' first-minute metric and rolled back both times: skipping the menu
    // entirely reads as broken, the player never learns what the game IS. The portal funnel is cut
    // INSIDE the menu instead: on CrazyGames, a first-timer's Play goes straight into Level 1.)
    for (;;) {
      if (autoStart) {
        game = autoStart.game;
        net = autoStart;
        break;
      }
      const choice = await showMainMenu(uiRoot, !!saved);
    if (choice === 'continue' && saved) {
      game = saved; // resume exactly where the player left off (skips race select + the opening reveal)
      resumed = true;
      const ci = savedCampaignIndex(); // keep the save's Conquest Trail identity (stars/unlock/Next)
      if (ci != null && CAMPAIGN[ci]) campaignIndex = ci;
      break;
    }
    if (choice === 'online') {
      // online lobby: resolves with the host-built, hash-verified starting state + our fixed seat
      const started = await showOnlineLobby(uiRoot);
      if (!started) continue; // backed out → re-show the menu
      game = started.game;
      net = started;
      break;
    }
    if (typeof choice === 'object' && choice.kind === 'campaign') {
      // no tutorial offer, no dialogs: the trail's Level 1 IS the lesson now (Conquest Trail 2.0),
      // and its in-level guide walks the first play on every platform
      campaignIndex = choice.level;
      game = campaignGame(choice.level);
      break;
    }
    if (choice === 'quick') {
      // Quick Battle: zero setup screens - a random race and the default match (4 factions, large
      // isles, total conquest). Gameplay is seconds away instead of three decision screens away.
      const race = RACES[(Math.random() * RACES.length) | 0]!.key;
      const board = pickMapSeed('isles', 12, Math.random());
      game = createGame(board.seed, race, 4, 12, 1, 'normal', 'isles', true, board.heart ?? false, true);
      break;
    }
    // 'new': pick a race + match settings. selectRace resolves null when the player backs out to the menu.
    const setup = await selectRace(uiRoot);
    if (!setup) continue; // ◀ Main Menu on the race screen → loop back and re-show the menu
    if (setup.humans > 1) {
      // local hotseat: N humans share this device (symmetric 'mp' rules). Player 1 keeps their race pick;
      // the other human seats are dealt distinct races from the seed, announced on each turn handoff.
      const slots = [{ race: setup.race, human: true }, ...Array.from({ length: setup.humans - 1 }, () => ({ human: true }))];
      const hotBoard = pickMapSeed(setup.mapKind, setup.islandSize, Math.random());
      game = createMatch({ seed: hotBoard.seed, factionCount: setup.factions, islandSize: setup.islandSize, dominationPct: setup.dominationPct, difficulty: setup.difficulty, mode: 'mp', mapKind: setup.mapKind, cliffs: true, heart: hotBoard.heart ?? false, capitals: true, slots });
    } else {
      const soloBoard = pickMapSeed(setup.mapKind, setup.islandSize, Math.random());
      game = createGame(soloBoard.seed, setup.race, setup.factions, setup.islandSize, setup.dominationPct, setup.difficulty, setup.mapKind, true, soloBoard.heart ?? false, true);
    }
    break;
      }
  }
  menuBackdrop.remove(); // the game is about to render; drop the setup backdrop so the live map shows
  audio.playMusic('game'); // crossfade the menu bed into the gameplay bed as the map + opening reveal begin

  // --- who the UI is presenting, and the single action funnel ---
  // me(): the LOCAL human seat. Online: the fixed seat the lobby dealt us. Hotseat ('mp' on one device):
  // the human whose turn it is (the device is passed around). Solo: always the player faction.
  const me = (): FactionId => net?.seat ?? (game.mode === 'mp' ? game.turnOf : game.playerFaction);
  // Every local human action goes through this one funnel as a serializable Command (legality is
  // re-checked inside the engine on EVERY client). Online, an accepted command is relayed to the room
  // from exactly here; remote commands arrive through the room and are applied via applyCommand directly.
  // Has the player COMMITTED to this match? A fresh match may only overwrite an existing campaign save
  // once its owner actually plays it (first successful command). Resuming that campaign, or booting with
  // no save at risk, is committed from the start. Guards persist() below against Quick Battle misclicks.
  let saveCommitted = resumed || !saved;
  const dispatch = (cmd: Command): CommandResult => {
    if (netDown) return { ok: false }; // connection lost: refuse BEFORE applying, never fork local state
    // An AI-phase advance / replay is playing back: the player is a spectator, not acting. Refuse acting
    // commands so a click on a still-open Realm panel during the replay never executes out of turn. This
    // matters especially in SOLO (applyCommand has no turn-gate there, so without this a mid-replay click
    // would really spend resources); in MP it also guards the window where the next actor is us.
    // respondOffer is exempt: it is legal off-turn and only ever answered from a toast, never mid-replay.
    if (advancing && cmd.kind !== 'respondOffer') return { ok: false };
    // Online, an offer answer is legal OFF-TURN, so applying it optimistically here would race the
    // acting player's in-flight commands and fork the shared order (their client applies [their cmd,
    // our answer], ours applied [our answer, their cmd]). Send it to the room only; it applies when the
    // echo comes back in seq order, exactly like a remote command.
    if (net && cmd.kind === 'respondOffer') {
      const o = game.offers.find((x) => x.id === cmd.offerId);
      if (!o || o.to !== me()) return { ok: false }; // dead click (offer already gone): send nothing
      net.room.send({ t: 'cmd', cmd });
      return { ok: true };
    }
    const r = applyCommand(game, me(), cmd);
    if (r.ok) saveCommitted = true; // the player played this match: it may own the save slot from here on
    if (r.ok && net) net.room.send({ t: 'cmd', cmd });
    return r;
  };
  const colorOf = (fid: FactionId): string => `#${game.factions[fid]!.color.toString(16).padStart(6, '0')}`;
  const meEquipped = loadProfile().equipped; // the device profile's avatar+frame, fixed for this match
  // avatar to show for a faction in the banners/HUD: the solo player's cosmetic, or the free race-default.
  // Hotseat uses race defaults for EVERY seat (the humans share one device profile, so it marks no one).
  const factionCosmetic = (fac: { race: string; isPlayer: boolean }): { avatar: string; frame: string } =>
    fac.isPlayer && game.mode !== 'mp' ? { avatar: meEquipped.avatar, frame: meEquipped.frame } : { avatar: `av_${fac.race}`, frame: 'fr_default' };

  view.render(game);
  // Map labels in world space (they pan/zoom with the map), set once: nothing here ever moves.
  // An archipelago names every ISLAND; a mainland is ONE landmass, so it gets ONE name, not a
  // province caption per corner. The campaign objective is labelled too, so the prize is findable.
  {
    const worldOf = (ids: readonly RegionId[]): { x: number; y: number }[] =>
      ids.map((rid) => {
        const rg = game.regions[rid]!;
        return { x: HEX_SIZE * Math.sqrt(3) * (rg.q + rg.r / 2), y: HEX_SIZE * 1.5 * rg.r };
      });
    const capLabel = (pts: { x: number; y: number }[], text: string): { x: number; y: number; text: string } => {
      // sit the name clear ABOVE the land: the top tile's vertex is ~1 hex above its centre and the
      // beach halo reaches ~1.1 hexes, so lift the (bottom-anchored) label to ~1.55 hexes for a gap.
      // Anchor X over the island's OWN top row (not the centroid): on jittered layouts the centroid
      // can hang the name over a neighbouring island's tiles and camps.
      const minY = Math.min(...pts.map((p) => p.y));
      const topRow = pts.filter((p) => p.y <= minY + HEX_SIZE * 0.5);
      return { x: topRow.reduce((a, p) => a + p.x, 0) / topRow.length, y: minY - HEX_SIZE * 1.55, text };
    };
    const labels: { x: number; y: number; text: string }[] = [];
    if (game.mapKind === 'land') {
      labels.push(capLabel(worldOf(game.regions.filter((r) => !impassable(r.terrain)).map((r) => r.id)), continentName(game)));
    } else {
      for (const isl of game.islands) labels.push(capLabel(worldOf(isl.regions), islandName(game, isl.id)));
    }
    if (game.objective) {
      const t = game.regions[game.objective.region]!;
      labels.push({
        x: HEX_SIZE * Math.sqrt(3) * (t.q + t.r / 2),
        y: HEX_SIZE * 1.5 * t.r - HEX_SIZE * 1.62,
        text: `🎯 ${game.objective.name}`,
      });
    }
    view.setIslandLabels(labels);
    view.setLabelZoom(camera.getView().scale, fitScale()); // fresh labels adopt the current zoom at once
  }
  // War Regalia skin: ONLY the player's own units render with the skin sprites, and only when they have
  // EQUIPPED their race's set. Solo-only: in hotseat the humans share the device profile, so skinning one
  // seat with it would be arbitrary; everyone renders base units (networked MP can revisit per-player).
  const playerSkinned = game.mode !== 'mp' && loadProfile().equipped.skin === `set_${game.factions[game.playerFaction]?.race ?? ''}`;
  view.setSkinnedFaction(playerSkinned ? game.playerFaction : null);
  fitCamera(); // sets the zoom-out floor + the "whole map fits" scale used by the opening reveal below
  // start the opening zoomed in on the first acting human's capital island; the reveal (hold, then zoom
  // out) plays at the end of setup once the camera/tween helpers exist
  const openCap = game.regions.find((r) => r.owner === me() && r.isCapital);
  if (openCap) camera.focusOn(HEX_SIZE * Math.sqrt(3) * (openCap.q + openCap.r / 2), HEX_SIZE * 1.5 * openCap.r, 1.9);
  requestRender();
  lifecycle.gameplayStart();
  if (!tutorialMode) noteMatchStarted(); // counts real matches this session -> drives the "not the first match" ad gate

  // --- UI ---
  const hud = mountHud(uiRoot);
  const panel = mountRegionPanel(uiRoot);
  const menu = mountMenu(uiRoot);
  const overview = mountOverview(uiRoot);
  const feed = mountFeed(uiRoot);
  const offerToasts = mountOfferToasts(uiRoot);
  // Standings panel (top-left). Wrap hud.refresh so the panel updates from the same single choke point
  // every existing hud.refresh(game) site already uses, without editing all of them. The open region
  // panel is re-rendered too, so its production affordability re-checks after ANY resource change (e.g.
  // a trade that finally gives you the Stone an Archer needs) instead of staying stuck disabled.
  const stats = mountStatsPanel(uiRoot, () => me(), (fid) => net?.seatNames[fid] ?? null);
  const baseHudRefresh = hud.refresh.bind(hud);
  hud.refresh = (g: GameState): void => {
    baseHudRefresh(g);
    stats.refresh(g);
    panel.show(g); // no-op/hides when nothing is selected; a pure re-render otherwise (never calls hud.refresh)
  };
  hud.refresh(game);

  // --- shared post-advance sequence: runs after EVERY applied endTurn (our own button, a hotseat pass,
  // or a remote player's relayed endTurn), so all paths animate + refresh + hash identically ---
  let netTurnSeq = net?.initialTurnSeq ?? 0; // counts applied endTurns; identical on every client (a rejoin resumes mid-count)
  async function advanceTurnUx(prevActor: FactionId, newOffers: number[]): Promise<void> {
    advancing = true;
    // the ended turn's countdown must not keep running over the AI replay (player report: the clock
    // "kept counting on the AI's turn"): suspend it NOW, and updateTurnBanner below re-arms it fresh
    // for the next seat once the replay has played out
    suspendTurnTimer();
    try {
      await advanceTurnUxInner(prevActor, newOffers);
    } finally {
      advancing = false;
    }
  }
  async function advanceTurnUxInner(prevActor: FactionId, newOffers: number[]): Promise<void> {
    // EVERY advance funnels through here (our End Turn button, the turn timer, a relayed rival end, an
    // AFK forceEnd), so close all the interactive / production UI now: while it is not our turn the player
    // should only WATCH the moves play out, never have a Realm panel, Research/Diplomacy menu or a tile's
    // build/train menu open (which also removed the last way to click production off-turn). The End Turn
    // button closes these too for instant feedback; doing it here as well covers the non-button paths.
    selectRegion(null); // the region panel that opens on a tile click, + any map highlights
    overview.close(); // the Realm overview
    menu.close(); // Research / Diplomacy
    await playReplay(); // animate what the AIs did during the advance
    if (tutorialMode) {
      // chapters inject their own teachable offers explicitly; stray AI diplomacy raised during the
      // advance (trade offers, war/peace messages) would talk over the lesson, so drop it here
      game.offers.length = 0;
      game.messages.length = 0;
    }
    // hotseat: the device changes hands between local humans; gate on a handoff screen first
    if (game.mode === 'mp' && !net && game.phase === 'playing' && me() !== prevActor) {
      selectRegion(null);
      await showHandoff();
    }
    await showMessages(); // war declarations, filtered to the local seat
    updateTurnBanner(); // your turn, or "waiting for X" online
    offerToasts.show(newOffers.filter((id) => game.offers.find((o) => o.id === id)?.to === me()));
    view.render(game);
    hud.refresh(game);
    menu.refresh();
    overview.refresh();
    feed.show(game.log);
    if (net) {
      // lockstep guard: everyone reports a state hash for this turn; the room flags any mismatch.
      // EVERY client also offers a fresh reconnect snapshot (cheap, single-digit KB); the room accepts
      // it only from its computed provider (host preferred, else the lowest connected seat), so the
      // snapshot keeps refreshing even after the host leaves for good.
      netTurnSeq += 1;
      net.room.send({ t: 'hash', turnSeq: netTurnSeq, hash: hashState(game), turnOf: game.turnOf });
      net.room.send({ t: 'snapshotPush', snapshot: snapshotState(game), turnSeq: netTurnSeq });
    }
    if (game.phase === 'over') {
      await playEndSequence();
      showGameOver();
    } else persist(); // checkpoint at the start of the next local turn (local games only)
  }

  // --- online: apply the peers' relayed commands in arrival (seq) order, one at a time ---
  if (net) {
    const session = net;
    const queue: ServerMsg[] = [];
    let pumping = false;
    // decisive game end reached OUTSIDE an endTurn advance (a remote move crossing the domination line
    // inside applyCommand, or the last-human takeover crowning the leader): run the shared end sequence
    // exactly once; both steps are idempotent-guarded, so extra calls are no-ops.
    const maybeShowEnd = async (): Promise<void> => {
      if (game.phase === 'over' && !document.querySelector('.gameover-overlay')) {
        await playEndSequence();
        showGameOver();
      }
    };
    // elimination watcher: the engine already rejects all input from a fallen seat, but without a card
    // the player just stares at a dead board. Persistent (not a 5s feed line), with a way out.
    let wasEliminated = game.factions[session.seat]?.eliminated ?? false;
    const checkElimination = (): void => {
      const nowElim = game.factions[session.seat]?.eliminated ?? false;
      if (nowElim && !wasEliminated && !document.querySelector('.gameover-overlay') && !document.querySelector('.elim-overlay')) {
        const overlay = el('div', 'msg-overlay elim-overlay');
        const card = el('div', 'msg-card');
        card.append(el('div', 'msg-head', '💀 You have been eliminated'), el('div', 'msg-body', 'Your last region has fallen. Watch how the war ends, or leave the match.'));
        const btns = el('div', 'msg-btns');
        const spectate = el('button', 'rpanel__btn msg-btn', 'Spectate');
        spectate.addEventListener('click', () => overlay.remove());
        const leaveBtn = el('button', 'rpanel__btn rpanel__btn--cancel msg-btn', 'Leave match');
        leaveBtn.addEventListener('click', () => {
          sessionStorage.removeItem('wi.activeRoom');
          session.room.close();
          window.location.reload();
        });
        btns.append(spectate, leaveBtn);
        card.append(btns);
        overlay.appendChild(card);
        uiRoot!.appendChild(overlay);
      }
      wasEliminated = nowElim;
    };
    // --- reconnect: a persistent card owns the screen while `netDown` locks dispatch ---
    let connOverlay: HTMLElement | null = null;
    let connHead: HTMLElement | null = null;
    let connBody: HTMLElement | null = null;
    let tries = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const hideConnCard = (): void => {
      connOverlay?.remove();
      connOverlay = null;
    };
    const showConnCard = (title: string, sub: string): void => {
      if (!connOverlay) {
        connOverlay = el('div', 'msg-overlay conn-overlay');
        const card = el('div', 'msg-card');
        connHead = el('div', 'msg-head');
        connBody = el('div', 'msg-body');
        const btns = el('div', 'msg-btns');
        const retryBtn = el('button', 'rpanel__btn msg-btn', 'Retry now');
        retryBtn.addEventListener('click', () => {
          if (retryTimer) clearTimeout(retryTimer);
          retryTimer = null;
          tries = 0;
          attemptReconnect();
        });
        const leaveBtn = el('button', 'rpanel__btn rpanel__btn--cancel msg-btn', 'Leave match');
        leaveBtn.addEventListener('click', () => {
          sessionStorage.removeItem('wi.activeRoom');
          session.room.close();
          window.location.reload();
        });
        btns.append(retryBtn, leaveBtn);
        card.append(connHead, connBody, btns);
        connOverlay.appendChild(card);
        uiRoot!.appendChild(connOverlay);
      }
      connHead!.textContent = title;
      connBody!.textContent = sub;
    };
    const attemptReconnect = (): void => {
      retryTimer = null;
      if (game.phase !== 'playing' || !netDown) return;
      if (tries >= 6) {
        showConnCard('Could not reconnect.', 'The match room did not answer. Retry, or leave the match.');
        return;
      }
      tries += 1;
      showConnCard('Connection lost. Reconnecting...', `Attempt ${tries} of 6`);
      // same token (src/net/room netToken) → the room recognises us and answers with a 'rejoin'
      // payload, which the pump below applies as a wholesale resync (that unlocks netDown)
      joinRoom(session.room.roomCode, displayName(), session.callbacks, loadProfile().equipped)
        .then((r) => {
          session.room = r;
        })
        .catch(() => {
          retryTimer = setTimeout(attemptReconnect, 2500);
        });
    };
    let resyncPending = false; // a hashMismatch already asked the room for its canonical snapshot
    // our seat is AI-run (takeover) while this tab is still CONNECTED: the reconnect-path reclaim can
    // never fire for it, so a persistent card (and the visibility hook below) sends 'reclaimSeat' instead
    let seatLost = false;
    let takeoverOverlay: HTMLElement | null = null;
    const hideTakeoverCard = (): void => {
      takeoverOverlay?.remove();
      takeoverOverlay = null;
    };
    const showTakeoverCard = (): void => {
      if (takeoverOverlay || document.querySelector('.gameover-overlay')) return;
      takeoverOverlay = el('div', 'msg-overlay takeover-overlay');
      const card = el('div', 'msg-card');
      card.append(el('div', 'msg-head', '🤖 The AI has taken your seat'), el('div', 'msg-body', 'You missed two turns, so the AI is playing for you. Take back command to keep playing yourself.'));
      const btns = el('div', 'msg-btns');
      const back = el('button', 'rpanel__btn msg-btn', '⚔ Take back command');
      back.addEventListener('click', () => session.room.send({ t: 'reclaimSeat' }));
      const leaveBtn = el('button', 'rpanel__btn rpanel__btn--cancel msg-btn', 'Leave match');
      leaveBtn.addEventListener('click', () => {
        sessionStorage.removeItem('wi.activeRoom');
        session.room.close();
        window.location.reload();
      });
      btns.append(back, leaveBtn);
      card.append(btns);
      takeoverOverlay.appendChild(card);
      uiRoot!.appendChild(takeoverOverlay);
    };
    document.addEventListener('visibilitychange', () => {
      // a frozen-but-connected tab woke up: ask for the seat back immediately, no click needed
      if (!document.hidden && seatLost && !netDown) session.room.send({ t: 'reclaimSeat' });
      // and snap the countdown to the room clock at once: a background tab throttles the interval,
      // so without this the chip shows a stale number until the next (delayed) tick
      if (!document.hidden && session.clock && !replaying && !animating && !advancing) armTurnTimer();
    });
    const pump = async (): Promise<void> => {
      if (pumping) return;
      pumping = true;
      try {
        while (queue.length) {
          const m = queue.shift()!;
          try {
            await handleNetMsg(m);
          } catch (err) {
            // one bad frame must not wedge this pump for the rest of the match: the per-turn hashes
            // surface any divergence it caused, and the hashMismatch resync below heals it
            console.error('[net] event failed', err);
          }
        }
      } finally {
        pumping = false;
      }
    };
    const handleNetMsg = async (m: ServerMsg): Promise<void> => {
        // own echoes are skipped (already applied at dispatch) EXCEPT respondOffer, which is legal
        // off-turn and therefore only ever applied here, in relay order, on every client incl. its sender
        if (m.t === 'cmd' && (m.from !== session.room.playerId || m.cmd.kind === 'respondOffer')) {
          if (typeof (m.cmd as { kind?: unknown } | null)?.kind !== 'string') return; // malformed frame: drop, never wedge
          while (replaying || animating || advancing) await delay(80); // let any local animation/advance settle first
          const preBoard: Board = m.cmd.kind === 'move' ? game.regions.map((r) => ({ owner: r.owner, unit: r.unit ? { ...r.unit } : null, buildings: [...r.buildings] })) : [];
          // diplomacy commands can raise an offer aimed at US: snapshot the ids so a fresh one can be
          // toasted the moment it lands, instead of hiding until the Diplomacy panel is opened
          const offersBefore = m.cmd.kind === 'endTurn' || m.cmd.kind === 'move' ? null : new Set(game.offers.map((o) => o.id));
          // our own deferred offer answer is applying NOW: float the resource delta from here (the
          // click site skipped it, because at click time nothing had changed yet)
          const ownRespondFlash = m.from === session.room.playerId && m.cmd.kind === 'respondOffer' ? captureStock() : null;
          const r = applyCommand(game, m.seat, m.cmd); // NOT dispatch: never re-relay a relayed command
          if (!r.ok) return; // engine rejected it; the per-turn hashes catch real divergence
          if (m.cmd.kind === 'endTurn') {
            await advanceTurnUx(m.seat, r.newOffers ?? []);
          } else if (m.cmd.kind === 'move' && r.combat) {
            // a remote player's move marches/fires with the same glide the local player gets, and the
            // camera flies to the action first: spectators SEE every march instead of hearing
            // off-screen swords (player request)
            animating = true;
            // a BOARDING plays at the origin shore (the walk down to the boat): fly THERE, not to the
            // empty far beach the voyage will only reach next turn (that read as attacking empty sand)
            const aw = regionWorld(r.combat.kind === 'sail' ? m.cmd.from : m.cmd.to);
            await panTo(aw.x, aw.y, 300);
            await animateMoveVisual(m.seat, m.cmd.from, m.cmd.to, preBoard, r.combat);
            animating = false;
            view.render(game);
            hud.refresh(game);
            requestRender();
            await maybeShowEnd(); // a decisive remote capture ends the game on OUR screen too, right now
          } else {
            if (offersBefore) {
              const fresh = game.offers.filter((o) => o.to === session.seat && !offersBefore.has(o.id)).map((o) => o.id);
              if (fresh.length) offerToasts.show(fresh); // a human's trade/alliance/peace ask surfaces immediately
            }
            view.render(game);
            hud.refresh(game);
            ownRespondFlash?.(); // after the refresh, so the delta floats over the updated numbers
            menu.refresh();
            overview.refresh();
            requestRender();
          }
          checkElimination();
        } else if (m.t === 'forceEnd') {
          // the room timed the acting seat out: everyone applies the endTurn on its behalf
          while (replaying || animating || advancing) await delay(80);
          const r = applyCommand(game, m.seat, { kind: 'endTurn' });
          if (r.ok) {
            feed.show([{ kind: 'info', text: `⏱ ${session.seatNames[m.seat] ?? game.factions[m.seat]?.name ?? '?'} ran out of time; their turn was ended.` }]);
            await advanceTurnUx(m.seat, r.newOffers ?? []);
            await maybeShowEnd(); // advanceTurnUx already ends an over game; this is a guarded no-op then
          }
          checkElimination();
        } else if (m.t === 'takeover') {
          // repeated timeouts / disconnect: the seat plays as AI from the next round on
          aiTakeover(game, m.seat);
          feed.show([{ kind: 'info', text: `🤖 ${session.seatNames[m.seat] ?? game.factions[m.seat]?.name ?? '?'} is now controlled by the AI.` }]);
          if (m.seat === session.seat) {
            seatLost = true; // OUR seat, on a socket that never dropped: offer the way back
            showTakeoverCard();
          }
          view.render(game);
          hud.refresh(game);
          requestRender();
          await maybeShowEnd(); // the LAST human's takeover crowns the leader inside the engine
        } else if (m.t === 'reclaim') {
          // a taken-over seat's owner returned: the seat is human again from this point in the stream
          reclaimSeat(game, m.seat);
          feed.show([{ kind: 'info', text: `🔌 ${session.seatNames[m.seat] ?? game.factions[m.seat]?.name ?? '?'} is back in command.` }]);
          if (m.seat === session.seat) {
            seatLost = false;
            hideTakeoverCard();
          }
          view.render(game);
          hud.refresh(game);
          updateTurnBanner();
          requestRender();
        } else if (m.t === 'rejoin') {
          // we reconnected mid-match: throw away the local state and resync wholesale from the room
          while (replaying || animating || advancing) await delay(80);
          const g2 = restoreSnapshot(m.snapshot);
          const ends = applyNetEvents(g2, m.cmds);
          game = g2;
          netTurnSeq = m.turnSeq + ends;
          netDown = false; // the resync landed: input may flow again
          tries = 0;
          resyncPending = false; // a wholesale resync supersedes any in-flight desync heal
          hideConnCard();
          selectRegion(null);
          view.render(game);
          hud.refresh(game);
          menu.refresh();
          overview.refresh();
          updateTurnBanner();
          requestRender();
          feed.show([{ kind: 'info', text: '🔌 Reconnected; game state resynced.' }]);
          checkElimination();
          // the rejoin buffer may contain our takeover (and the hello-path reclaim rides right behind
          // it), so sync the seat-lost card with what the resynced state actually says about our seat
          seatLost = !(game.factions[session.seat]?.isPlayer ?? true);
          if (seatLost) showTakeoverCard();
          else hideTakeoverCard();
          if (game.phase === 'over') {
            await playEndSequence();
            showGameOver();
          }
        } else if (m.t === 'welcome') {
          // a reconnect handshake answered. started=true → the 'rejoin' resync follows on this same
          // socket (netDown stays locked until it applies). started=false means the room LOST the match
          // and answered as a fresh lobby (should be impossible now that started rooms persist, but
          // never trust it): stop retrying, there is no live match behind this socket.
          if (!m.lobby.started && netDown) {
            if (retryTimer) clearTimeout(retryTimer);
            retryTimer = null;
            tries = 6;
            sessionStorage.removeItem('wi.activeRoom');
            showConnCard('The match room was lost.', 'The server no longer remembers this match.');
          }
        } else if (m.t === 'hashMismatch') {
          // a fork was detected. Playing on in different worlds is the one failure lockstep must never
          // shrug at: EVERY client asks the room for its canonical snapshot and adopts it (below), so
          // the table converges on one shared state no matter whose sim drifted.
          if (!resyncPending) {
            resyncPending = true;
            feed.show([{ kind: 'info', text: '⚠ Sync mismatch detected; resyncing...' }]);
            session.room.send({ t: 'snapshotRequest' });
          }
        } else if (m.t === 'snapshot') {
          // the room's canonical state (requested after a hashMismatch): adopt it wholesale, replay the
          // events relayed since it was stored (same catch-up as a rejoin, else we'd land one turn
          // behind the live table), then report a fresh hash so convergence is actually verified and
          // the room's per-turn quorum keeps running
          resyncPending = false;
          while (replaying || animating || advancing) await delay(80);
          const g2 = restoreSnapshot(m.snapshot);
          const ends = applyNetEvents(g2, m.cmds ?? []);
          game = g2;
          netTurnSeq = m.turnSeq + ends;
          selectRegion(null);
          view.render(game);
          hud.refresh(game);
          menu.refresh();
          overview.refresh();
          updateTurnBanner();
          requestRender();
          feed.show([{ kind: 'info', text: '🔄 Game state resynced.' }]);
          session.room.send({ t: 'hash', turnSeq: netTurnSeq, hash: hashState(game), turnOf: game.turnOf });
          checkElimination();
          seatLost = !(game.factions[session.seat]?.isPlayer ?? true);
          if (seatLost) showTakeoverCard();
          else hideTakeoverCard();
          await maybeShowEnd();
        } else if (m.t === 'error') {
          // the room closed itself (every seat abandoned): nothing left to rejoin, drop the remembered code
          if (m.code === 'abandoned') {
            sessionStorage.removeItem('wi.activeRoom');
            feed.show([{ kind: 'info', text: `⚠ ${m.msg}` }]);
          }
        } else if (m.t === 'peer') {
          const nm = session.names[m.playerId] ?? 'A player';
          feed.show([{ kind: 'info', text: m.connected ? `🔌 ${nm} reconnected.` : `⚠ ${nm} disconnected.` }]);
        } else if (m.t === 'rematch') {
          // the room reset to a fresh lobby: reconnect into the SAME room's lobby (the group re-forms). The
          // cgJoin auto-join path reconnects on boot; wi.activeRoom is cleared since the match is gone.
          setSessionFlag('wi.cgJoin', session.room.roomCode);
          sessionStorage.removeItem('wi.activeRoom');
          window.location.reload();
        }
    };
    session.callbacks.onMessage = (m) => {
      // the room's turn clock is pure display state: apply it AT RECEIVE TIME (the queue can sit
      // behind a long replay await, which would corrupt the measured skew) and snap the chip at once
      // when nothing is animating; a mid-replay clock is picked up by the post-replay re-arm instead
      if (m.t === 'clock') {
        session.clock = { deadlineAt: m.deadlineAt, skewMs: m.now - Date.now() };
        if (!replaying && !animating && !advancing) armTurnTimer();
        return;
      }
      queue.push(m);
      void pump();
    };
    session.callbacks.onClose = (code?: number) => {
      if (game.phase !== 'playing') return;
      if (code === CLOSE_REPLACED) {
        // a NEWER connection with our token took the seat (this tab was duplicated): this socket must
        // not fight it in a reconnect loop - the seat lives on in the other tab now
        netDown = true;
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = null;
        tries = 6;
        showConnCard('This match moved to another tab.', 'Another window with the same seat took over the connection. Close this tab, or play on there.');
        return;
      }
      if (netDown) {
        // a reconnect socket died after its welcome but before the resync landed: keep the loop alive
        if (!retryTimer && tries < 6) retryTimer = setTimeout(attemptReconnect, 2500);
        return;
      }
      netDown = true; // locks dispatch until the rejoin resync applies
      resyncPending = false; // a snapshot reply lost with this socket must not lock out future heals
      tries = 0;
      feed.show([{ kind: 'info', text: '⚠ Connection lost; reconnecting...' }]);
      showConnCard('Connection lost. Reconnecting...', 'Attempt 1 of 6');
      retryTimer = setTimeout(attemptReconnect, 1000);
    };
    // remember the live match for this tab: an accidental refresh lands on the lobby's Rejoin button
    sessionStorage.setItem('wi.activeRoom', session.room.roomCode);
    // report the freshly-hydrated state (turnSeq 0 on a fresh start, mid-count on a rejoin): an immediate
    // cross-client desync check, and it arms the room's AFK timer with the current turn's owner
    session.room.send({ t: 'hash', turnSeq: netTurnSeq, hash: hashState(game), turnOf: game.turnOf });
  }

  // --- mid-game save/resume: persist now (so "Continue" is available immediately), after every End Turn,
  // and whenever the tab is hidden (catches a close/switch mid-turn). A finished game clears its save. ---
  // The save is a SINGLE slot, and Quick Battle is one click from the menu: a fresh match must not stomp
  // an existing campaign save until the player actually PLAYS it (saveCommitted, set by dispatch's first
  // successful command). Until then every persist() no-ops - including the tab-hide one - so backing out
  // (or a misclick) keeps the old campaign resumable.
  const persist = (): void => { if (saveCommitted && !tutorialMode && !net) saveGame(game, campaignIndex); }; // no tutorial saves; online resumes via the room snapshot (Phase 3), not local Continue
  persist();
  // snapshot the player's resources now; call the returned fn AFTER an action to float whatever changed over the
  // HUD (green +N gains rising, red -N losses sinking), e.g. a trade settling or a capital being plundered.
  const captureStock = (): (() => void) => {
    const P = me();
    const before = [...(game.factions[P]?.stock ?? [])];
    return () => {
      const after = game.factions[P]?.stock ?? [];
      hud.flashDelta(after.map((v, i) => v - (before[i] ?? 0)));
    };
  };
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) persist();
  });

  // --- turn banner: a persistent top-centre pill showing whose turn it is: "YOUR TURN" while you play,
  // then each AI faction (colour-coded) as the replay steps through their moves, back to you at the end. ---
  const turnBanner = el('div', 'turn-banner');
  uiRoot.appendChild(turnBanner);
  function setTurnBanner(text: string, colorHex: string, avatarId?: string, frameId?: string): void {
    turnBanner.textContent = '';
    if (avatarId) {
      const av = framedAvatar(avatarId, frameId ?? 'fr_default', 26);
      av.classList.add('turn-banner__avatar');
      turnBanner.append(av);
    }
    turnBanner.append(el('span', 'turn-banner__text', text));
    turnBanner.style.setProperty('--fc', colorHex);
    turnBanner.classList.remove('is-pop'); // restart the little pop each time the turn-holder changes
    void turnBanner.offsetWidth;
    turnBanner.classList.add('is-pop');
  }
  // "ELVES' TURN", not "ELVES'S TURN": names already ending in S take a bare apostrophe
  const possTurn = (name: string): string => `${name.toUpperCase()}${name.toUpperCase().endsWith('S') ? "'" : "'S"} TURN`;
  // the banner last showed SOMEONE ELSE (AI replay, remote player, hotseat handoff): the next
  // "YOUR TURN" earns a herald chime. Never armed at match start, so the opening stays quiet.
  let turnAway = false;
  const showYourTurn = (): void => {
    const f = game.factions[me()]!;
    const cos = factionCosmetic(f);
    if (turnAway) {
      turnAway = false;
      audio.sfx('yourturn');
    }
    // hotseat names the seat so whoever holds the device knows it's them; solo keeps the classic line
    setTurnBanner(game.mode === 'mp' ? `⚔  ${f.name.toUpperCase()} - YOUR TURN` : '⚔  YOUR TURN', colorOf(f.id), cos.avatar, cos.frame);
  };
  // online: while a REMOTE human is on turn, show who we're waiting for (their player name + faction colour)
  const showWaiting = (): void => {
    turnAway = true;
    const f = game.factions[game.turnOf]!;
    const nm = net?.seatNames[f.id] ?? f.name;
    setTurnBanner(`⏳  ${possTurn(nm)}`, colorOf(f.id), `av_${f.race}`, 'fr_default');
  };
  // online per-move timer: a countdown chip under the banner. The ACTING client ends its own turn at
  // zero; the room only force-ends frozen/closed tabs after extra grace (see party/index.ts AFK timer).
  let timerBox: HTMLElement | null = null;
  let timerIv: ReturnType<typeof setInterval> | null = null;
  let lastTickSec = -1; // last whole second we played the countdown tick for (so it fires once per second, not per 500ms poll)
  let revealDone = false; // the opening reveal blocks input; the countdown must not run (and reset) under it
  // Freeze the countdown between a turn ENDING and the next one being armed (the AI-replay window):
  // the old interval carried the ENDED turn's deadline and visibly kept counting through the replay.
  const suspendTurnTimer = (): void => {
    if (timerIv) {
      clearInterval(timerIv);
      timerIv = null;
    }
    if (timerBox) {
      timerBox.textContent = '⏱ …';
      timerBox.classList.remove('is-low');
    }
  };
  const armTurnTimer = (): void => {
    if (!net) return;
    if (!revealDone) return; // updateTurnBanner re-arms right after the reveal finishes
    if (!timerBox) {
      timerBox = el('div', 'turn-timer');
      uiRoot!.appendChild(timerBox);
    }
    if (timerIv) {
      clearInterval(timerIv);
      timerIv = null;
    }
    if (game.phase !== 'playing') {
      timerBox.classList.remove('is-on');
      return;
    }
    // the ROOM's clock is the truth (protocol 'clock'): every client renders the same absolute
    // deadline, so a skipped replay or a backgrounded tab can never desync the visible countdown.
    // Fallback (clock not yet seen: the very first turn's report round-trip): the local full timer.
    let leftMs = net.timerSec * 1000;
    if (net.clock) {
      const serverLeft = net.clock.deadlineAt - (Date.now() + net.clock.skewMs);
      leftMs = Math.min(net.timerSec * 1000, Math.max(1000, serverLeft));
    }
    const deadline = Date.now() + leftMs;
    lastTickSec = -1; // a fresh turn: re-arm the per-second countdown tick
    timerBox.classList.add('is-on');
    const tick = (): void => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      timerBox!.textContent = `⏱ ${left}s`;
      timerBox!.classList.toggle('is-low', left <= 5);
      // a clock tick each of the last 5 seconds, but ONLY on our own turn (it's our clock running out;
      // on a rival's turn the countdown still shows but stays silent). Guarded so the 500ms poll fires
      // the sound once per whole second, not twice. The replaying/animating/advancing gate matters because
      // a stale interval from the previous seat's turn keeps running through the pre-turn AI replay (the
      // engine has already advanced turnOf to us by then), which would phantom-tick over the replay.
      if (left >= 1 && left <= 5 && left !== lastTickSec && game.turnOf === me() && game.phase === 'playing' && !replaying && !animating && !advancing) {
        lastTickSec = left;
        audio.sfx('tick');
      }
      if (left > 0) return;
      if (game.turnOf === me() && game.phase === 'playing') {
        if (replaying || animating || advancing) return; // keep ticking; retry once everything settles
        if (netDown) return; // never auto-end a turn we cannot relay; the room's grace timer owns AFK
        const r = dispatch({ kind: 'endTurn' });
        if (r.ok) {
          clearInterval(timerIv!);
          timerIv = null;
          void advanceTurnUx(me(), r.newOffers ?? []);
        }
      } else {
        clearInterval(timerIv!); // not our turn: the acting client (or the room's grace) handles it
        timerIv = null;
      }
    };
    tick();
    timerIv = setInterval(tick, 500);
  };
  const updateTurnBanner = (): void => {
    if (net && game.turnOf !== me()) showWaiting();
    else showYourTurn();
    armTurnTimer(); // every turn transition passes through here, so the online timer follows along
  };
  updateTurnBanner();

  // --- selection + unit movement ---
  let unitMoveFrom: RegionId | null = null;
  // the CURRENT selection's clickable move targets (land + attack + sea), for the hover ring: only a
  // tile the click would actually act on may light up white under the cursor
  let moveTargets: Set<RegionId> | null = null;

  // index of one of OUR stalled boats awaiting new orders (null = none). A stalled boat is a unit with
  // its own selection state: click it, then click a lit shore, and it sails and lands in one action.
  let boatSelected: number | null = null;

  /** Send a held-up boat to a new shore and play the crossing: it sails the bow, then lands (or is
   *  thrown back and holds station again). */
  async function animateReroute(index: number, v: Voyage, toId: RegionId): Promise<void> {
    const anchor = view.boatWorldOf(v.from, v.dest); // where it sits right now, so the sail starts there
    const race = game.factions[v.owner]?.race ?? '';
    const col = game.factions[v.owner]?.color ?? 0x888888;
    const res = dispatch({ kind: 'reroute', voyage: index, to: toId }).voyage ?? null;
    if (!res) {
      view.render(game);
      requestRender();
      return;
    }
    audio.sfx(res.defenderDied || res.attackerDied || !res.captured ? 'attack' : 'move');
    view.beginSail(v.from, res.to, race, v.unit.type, col, playerSkinned && v.owner === game.playerFaction, anchor ?? undefined);
    await tween((t) => {
      view.updateSail(res.landed ? t : Math.sin(t * Math.PI) * 0.55);
      requestRender();
    }, res.landed ? 700 : 620);
    view.endSail();
    view.flash(res.to, res.defenderDied ? 0xff5a5a : 0xffe27a);
    view.render(game);
    requestRender();
    await delay(260);
    view.clearFlash();
    hud.refresh(game);
    requestRender();
    if (!res.landed) feed.show([{ kind: 'info', text: '⛵ The landing was thrown back. Your boat holds station.' }]);
    checkGameEnd(game);
    if (game.phase === 'over') {
      await playEndSequence();
      showGameOver();
    } else persist();
  }

  function selectRegion(id: RegionId | null): void {
    game.selectedRegion = id;
    boatSelected = null; // picking a tile drops any boat order in progress
    view.setVoyageArrow(null); // any tile (de)selection dismisses a selected boat's course arrow
    const reg = id != null ? game.regions[id] : null;
    const active = !!(reg && reg.unit && reg.unit.owner === me() && canStillAct(reg.unit));
    view.setSelected(id, active); // active → the tile pulses so you can see which unit is up
    if (active) {
      unitMoveFrom = id;
      const t = unitTargets(game, id!);
      view.setHighlights(t.move, [...t.melee, ...t.ranged], t.sea);
      moveTargets = new Set([...t.move, ...t.melee, ...t.ranged, ...t.sea]); // the hover ring lights only REAL targets
    } else {
      unitMoveFrom = null;
      view.setHighlights([], [], []);
      moveTargets = null;
    }
    panel.show(game);
    requestRender();
  }

  // Why did clicking `toId` with the unit on `fromId` NOT attack? Returns a one-line reason to surface to the
  // player (or null if the click wasn't an attack attempt, e.g. tapping your own/neutral tile to inspect it).
  // Only fires on an INVALID target, so reaching a check means that precondition is the blocker.
  function attackBlockedReason(fromId: RegionId, toId: RegionId): string | null {
    const from = game.regions[fromId];
    const to = game.regions[toId];
    if (!from?.unit || !to) return null;
    const me = from.unit.owner;
    const foe = to.unit && to.unit.owner !== me ? to.unit.owner : to.owner >= 0 && to.owner !== me ? to.owner : -1;
    // a crossing is any attempt at a tile this unit cannot walk to: another island on the archipelago,
    // or a far shore of the mainland. The port is the gangway, so the guidance names the tile, not the island.
    const otherIsland = game.mapKind !== 'land' ? to.island !== from.island : !from.neighbors.includes(toId) && isCoastal(game, to) && isCoastal(game, from);
    if (foe < 0 && !otherIsland) return null; // an own/neutral walkable tile, not an attack attempt, just let them inspect it
    if (otherIsland) {
      if (!from.buildings.includes(BUILDING.Harbor)) return '⚓ Crossings start at a port. Move this unit onto one of your Harbor tiles, or build a Harbor on this coast.';
      if (game.mapKind !== 'land' && !game.islands[from.island]?.seaLinks.includes(to.island)) return '🌊 That island is too far to reach by sea from here.';
      if (!isCoastal(game, to)) return '⛵ Sea landings hit a beach. Aim at a coastal tile, then march inland.';
      if (game.mapKind === 'land') {
        // two different rejections share this branch: the shore is on ANOTHER WATER (a boat never
        // crosses land), or it is simply too far along the same coast. Say which one it is.
        if (hexDistance(from, to) <= SEA_RANGE && !sharesWater(game, from, to)) return '🌊 A boat cannot cross land: that shore is on a different water than your port.';
        return '🌊 That shore is beyond your boats. Pick a coast closer to your port.';
      }
    }
    if (foe >= 0) {
      const name = game.factions[foe]?.name ?? 'them';
      if (areAllied(game, me, foe)) return `🕊️ You are allied with ${name}. Break the alliance in Diplomacy before you can attack.`;
      if (!atWar(game, me, foe)) return `⚔️ You are not at war with ${name}. Declare war in Diplomacy first, then attack.`;
      if (!otherIsland) return 'Move a unit right next to their soldier to strike it (an Archer can hit from 2 tiles).';
    }
    return null;
  }

  // left-click: move/attack with the selected unit onto a highlighted target, else select
  app.canvas.addEventListener('pointerup', (e) => {
    if (e.button !== 0 || camera.didPan || replaying || animating) return;
    const w = camera.screenToWorld(e.offsetX, e.offsetY);
    const id = view.pick(w.x, w.y);
    // a STALLED boat of ours is selected: this click may be its new landing order
    if (id != null && boatSelected != null) {
      const targets = voyageTargets(game, boatSelected);
      if (targets.includes(id)) {
        const idx = boatSelected;
        const v = game.voyages[idx];
        boatSelected = null;
        view.setHighlights([], [], []);
        view.setVoyageArrow(null);
        if (v) {
          animating = true;
          void animateReroute(idx, v, id).finally(() => {
            animating = false;
          });
        }
        return;
      }
    }
    if (id == null) {
      // clicked open water: a boat, maybe? Boats float off-tile, so pick() can never find them.
      const boat = view.boatAt(w.x, w.y);
      if (boat != null) {
        selectRegion(null); // drop any tile selection (also clears a previous boat's arrow)
        view.setVoyageArrow(boat); // then show THIS boat's curved course to its landing beach
        const v = game.voyages[boat];
        if (v) {
          const mine = v.owner === me();
          const who = mine ? `your ${UNIT_NAME[v.unit.type]}` : `${game.factions[v.owner]?.name ?? 'an enemy'} ${UNIT_NAME[v.unit.type]}`;
          if (mine && v.stalled) {
            // a held-up crossing takes new orders: light its legal shores and wait for the click
            boatSelected = boat;
            const shores = voyageTargets(game, boat);
            view.setHighlights([], [], shores);
            moveTargets = new Set(shores);
            feed.show([{ kind: 'info', text: `⛵ ${who} (${v.unit.hp}/${unitMaxHp(v.unit.type)} HP) is held offshore. Click any lit shore to land there now.` }]);
          } else {
            boatSelected = null;
            feed.show([{ kind: 'info', text: `⛵ At sea: ${who} (${v.unit.hp}/${unitMaxHp(v.unit.type)} HP) lands at the marked beach next turn.` }]);
          }
        }
        requestRender();
        return;
      }
      boatSelected = null;
    }
    if (id != null && unitMoveFrom != null && unitMoveFrom !== id) {
      const t = unitTargets(game, unitMoveFrom);
      if (t.move.includes(id) || t.melee.includes(id) || t.ranged.includes(id) || t.sea.includes(id)) {
        const from = unitMoveFrom;
        // note an enemy capital we're about to storm, so we can announce the plunder once it lands
        const tgt = game.regions[id];
        const capName = tgt?.isCapital && tgt.owner >= 0 && tgt.owner !== me() ? game.factions[tgt.owner]?.name : null;
        animating = true;
        view.setSelected(null);
        view.setHighlights([], [], []);
        unitMoveFrom = null;
        const flashLoot = captureStock(); // storming a capital plunders its treasury: float whatever we gain after
        void animatePlayerMove(from, id).finally(async () => {
          animating = false; // reset even if the glide throws, so input never stays dead
          hud.refresh(game);
          flashLoot(); // shows the plundered resources over the HUD (nothing if this capture wasn't a plunder)
          menu.refresh(); // plundering a capital changes your Gold: keep research affordability + offers in sync
          selectRegion(id);
          if (capName && game.regions[id]!.owner === me()) {
            feed.show([{ kind: 'diplo', text: `🏰 You stormed ${capName}'s capital and plundered their treasury!` }]);
          }
          // a decisive capture (last rival eliminated, or crossing the domination line) ends the game now,
          // without forcing the player to run a pointless End Turn / AI phase first
          checkGameEnd(game);
          if (game.phase === 'over') {
            await playEndSequence();
            showGameOver();
          } else persist();
        });
        return;
      }
      // not a valid target: if the player was clearly trying to attack or cross the sea, tell them WHY instead
      // of silently switching to inspect the tile, and keep their unit selected so they can act elsewhere
      const reason = attackBlockedReason(unitMoveFrom, id);
      if (reason) {
        feed.show([{ kind: 'info', text: reason }]);
        return;
      }
    }
    selectRegion(id);
  });
  app.canvas.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    selectRegion(null);
  });

  // --- pre-attack combat forecast: while your unit is selected, hovering an enemy tile shows the exact
  // outcome (damage, retaliation, who dies, capture) BEFORE you commit, the combat maths are deterministic,
  // so the preview is guaranteed truthful. Reads previewCombat(), which never mutates state. ---
  const forecastBox = el('div', 'combat-forecast');
  uiRoot.appendChild(forecastBox);
  let forecastFor: RegionId | null = null;
  const hideForecast = (): void => {
    forecastFor = null;
    forecastBox.classList.remove('is-on');
  };
  const rpsMeta: Record<CombatPreview['rps'], [string, string]> = {
    strong: ['Strong', 'cf__rps--strong'],
    weak: ['Weak', 'cf__rps--weak'],
    even: ['Even', 'cf__rps--even'],
  };
  const forecastHtml = (p: CombatPreview, targetTerrain?: string, targetHasBarracks = false): string => {
    const ai = UNIT_ICON[p.attackerType];
    const di = p.defenderType != null ? UNIT_ICON[p.defenderType] : '';
    const [rpsLabel, rpsCls] = rpsMeta[p.rps];
    // a sea target is a BOARDING now: the assault itself lands at your next turn start, and the world
    // gets a turn in between, so the numbers are a projection, not a promise
    const sea = p.kind === 'sea';
    let resultCls = 'cf__result--even';
    let resultTxt = sea ? 'Projected: both survive' : 'Both survive';
    if (p.attackerDies) {
      resultCls = 'cf__result--bad';
      resultTxt = sea ? '✖ Projected: you lose this unit' : '✖ You lose this unit';
    } else if (p.defenderDies && p.captures) {
      resultCls = 'cf__result--good';
      resultTxt = sea ? '✔ Projected: kill and capture' : '✔ Kill and capture';
    } else if (p.defenderDies) {
      resultCls = 'cf__result--good';
      resultTxt = sea ? '✔ Projected: kill' : '✔ Kill (hold ground)';
    }
    const dmg = `<div class="cf__row">You hit <b>${p.dealt}</b> · ${di} ${p.defenderHpBefore}→<b>${p.defenderHpAfter}</b>${p.defenderDies ? ' 💀' : ''}</div>`;
    const back =
      p.retaliation != null
        ? `<div class="cf__row">Counter <b>${p.retaliation}</b> · ${ai} ${p.attackerHpBefore}→<b>${p.attackerHpAfter}</b>${p.attackerDies ? ' 💀' : ''}</div>`
        : `<div class="cf__row cf__muted">No counter (out of reach)</div>`;
    const cap = p.isCapital ? `<div class="cf__cap">🏰 Capital · tougher, plunder on capture</div>` : '';
    const gate = targetTerrain === 'gate' ? `<div class="cf__cap">🏰 Gatehouse: melee is blunted by the walls. Archers shoot OVER them.</div>` : '';
    const camp = targetHasBarracks ? `<div class="cf__cap">🛡️ Barracks: a fortified camp. Its defender takes 1 less.</div>` : '';
    const sail = sea ? `<div class="cf__cap">⛵ Boards now, the assault lands at your NEXT turn start. The defender may heal or move first.</div>` : '';
    return `<div class="cf__head">${ai} vs ${di} <span class="cf__rps ${rpsCls}">${rpsLabel}</span></div>${dmg}${back}${cap}${gate}${camp}${sail}<div class="cf__result ${resultCls}">${resultTxt}</div>`;
  };
  const positionForecast = (clientX: number, clientY: number): void => {
    const pad = 18;
    const r = forecastBox.getBoundingClientRect();
    let x = clientX + pad;
    let y = clientY + pad;
    if (x + r.width > window.innerWidth - 8) x = clientX - r.width - pad;
    if (y + r.height > window.innerHeight - 8) y = clientY - r.height - pad;
    forecastBox.style.left = `${Math.max(8, x)}px`;
    forecastBox.style.top = `${Math.max(8, y)}px`;
  };
  app.canvas.addEventListener('pointermove', (e) => {
    if ((unitMoveFrom == null && !boatSelected) || replaying || animating) {
      if (view.setHoverTarget(null)) requestRender();
      return hideForecast();
    }
    const w = camera.screenToWorld(e.offsetX, e.offsetY);
    const id = view.pick(w.x, w.y);
    // the white hover ring: EXACTLY the tile the click would act on lights up under the cursor
    // (player report: "I thought I clicked that tile"), and only over genuine targets
    if (view.setHoverTarget(id != null && id !== unitMoveFrom && moveTargets?.has(id) ? id : null)) requestRender();
    if (unitMoveFrom == null) return; // a selected BOAT gets the ring but has no combat forecast
    if (id == null || id === unitMoveFrom) return hideForecast();
    const to = game.regions[id];
    if (!to || !to.unit) return hideForecast(); // only forecast an actual fight; a bloodless move reads from the highlight
    const p = previewCombat(game, unitMoveFrom, id, me());
    if (!p || p.defenderType == null) return hideForecast();
    if (id !== forecastFor) {
      forecastBox.innerHTML = forecastHtml(p, to.terrain, to.buildings.includes(BUILDING.Barracks));
      forecastFor = id;
    }
    forecastBox.classList.add('is-on');
    positionForecast(e.clientX, e.clientY);
  });
  app.canvas.addEventListener('pointerleave', () => {
    if (view.setHoverTarget(null)) requestRender(); // the cursor left the map: no phantom ring
    hideForecast();
  });
  window.addEventListener('pointerdown', hideForecast, true); // any click may move/deselect, drop the box


  // self-heal: if the button was released off-window (no pointerup reaches us), the first move back in with
  // no button held drops the glow, so the cursor can never get stuck in the pressed (gold) variant
  document.addEventListener('pointermove', (e) => { if (e.buttons === 0) swingOff(); }, true);

  function fitScale(): number {
    const b = view.bounds();
    const pad = 80;
    return Math.min(app.screen.width / (b.maxX - b.minX + pad), app.screen.height / (b.maxY - b.minY + pad));
  }
  function fitCamera(): void {
    const b = view.bounds();
    const cx = (b.minX + b.maxX) / 2;
    const cy = (b.minY + b.maxY) / 2;
    const scale = fitScale();
    camera.setMinScale(scale * 0.85); // don't let the player zoom out much past "the whole map fits"
    camera.setWorldBounds(b.minX, b.minY, b.maxX, b.maxY); // and don't let them drag the map far into open ocean
    camera.focusOn(cx, cy, scale);
  }
  // keep the zoom-out floor in step with the window (the fit scale depends on screen size); recompute
  // after Pixi has resized its renderer, but don't recentre so a panned/zoomed view isn't yanked
  window.addEventListener('resize', () => {
    requestAnimationFrame(() => {
      camera.setMinScale(fitScale() * 0.85);
      requestRender();
    });
  });

  // --- animated replay of the AI turn (watch what happened, step by step) ---
  let replaying = false;
  let animating = false; // a player move is gliding, block further input until it lands
  let advancing = false; // an end-turn advance UX (replay+banners) is running; serialize all advance triggers
  let ending = false; // the end-of-game cinematic is playing, freeze input until the Victory/Defeat screen
  // factions whose war/ally-break sting already played DURING the replay (synced with them turning red),
  // so showMessages does not re-play the sound when its deferred modal appears. Filled per replay.
  const stungThisTurn = new Set<FactionId>();
  const delay = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
  const AI_STEP_PAUSE = 500; // a clear ~half-second beat between each AI move in the replay, so the player can follow it
  const tween = (fn: (t: number) => void, dur: number): Promise<void> =>
    new Promise((resolve) => {
      const t0 = performance.now();
      const step = (): void => {
        const t = Math.min(1, (performance.now() - t0) / dur);
        fn(t);
        if (t >= 1) resolve();
        else requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  // world-space centre of a region (for the replay camera to follow the action)
  const regionWorld = (regId: RegionId): { x: number; y: number } => {
    const r = game.regions[regId]!;
    return { x: HEX_SIZE * Math.sqrt(3) * (r.q + r.r / 2), y: HEX_SIZE * 1.5 * r.r };
  };
  const REPLAY_ZOOM = 1.6; // zoom in enough that an island's action reads, without losing context
  // smoothly pan (and zoom to REPLAY_ZOOM) so a region sits centred
  const panTo = (wx: number, wy: number, dur: number): Promise<void> => {
    const cw = app.canvas.clientWidth || app.screen.width;
    const ch = app.canvas.clientHeight || app.screen.height;
    const from = camera.screenToWorld(cw / 2, ch / 2);
    const startScale = camera.getView().scale;
    return tween((t) => {
      const e = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
      camera.focusOn(from.x + (wx - from.x) * e, from.y + (wy - from.y) * e, startScale + (REPLAY_ZOOM - startScale) * e);
      requestRender();
    }, dur);
  };
  // smoothly pan AND zoom to an arbitrary target scale (used by the opening reveal to zoom out to the whole map)
  const flyTo = (wx: number, wy: number, targetScale: number, dur: number): Promise<void> => {
    const cw = app.canvas.clientWidth || app.screen.width;
    const ch = app.canvas.clientHeight || app.screen.height;
    const from = camera.screenToWorld(cw / 2, ch / 2);
    const startScale = camera.getView().scale;
    return tween((t) => {
      const e = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
      camera.focusOn(from.x + (wx - from.x) * e, from.y + (wy - from.y) * e, startScale + (targetScale - startScale) * e);
      requestRender();
    }, dur);
  };
  type Board = RegionSnapshot[];
  // Apply a unit combat/move event to the replay board, reproduces the engine's resulting state.
  function applyUnitEvent(board: Board, ev: Extract<ReplayEvent, { kind: 'unit' }>): void {
    const mover = { type: ev.unitType, owner: ev.faction, movesLeft: 0, hp: ev.attackerHp };
    if (ev.combat === 'sail') {
      board[ev.from]!.unit = null; // boarded a boat: the tile empties; the boat itself renders from live state
      return;
    }
    if (ev.combat === 'sea' && !ev.captured) {
      // an amphibious attack that did NOT take the beach: the attacker is offshore (or sunk), so it never
      // reappears on its origin tile; only the defender's fate shows on the board
      if (ev.defenderDied) board[ev.to]!.unit = null;
      else if (board[ev.to]!.unit) board[ev.to]!.unit!.hp = ev.defenderHp;
      return;
    }
    if (ev.combat === 'moved' || ev.combat === 'captured' || ev.captured) {
      // the unit advances into the tile (plain move, or a kill that captures)
      board[ev.to]!.unit = mover;
      if (ev.captured || ev.combat === 'captured') board[ev.to]!.owner = ev.faction;
      board[ev.from]!.unit = null;
      return;
    }
    // no advance: the attacker stays put (or died); the defender takes damage (or a ranged kill)
    board[ev.from]!.unit = ev.attackerDied ? null : mover;
    if (ev.defenderDied) board[ev.to]!.unit = null;
    else if (board[ev.to]!.unit) board[ev.to]!.unit!.hp = ev.defenderHp;
  }

  // Glide the player's own unit fromId → toId (same feel as the AI replay) instead of snapping instantly.
  async function animatePlayerMove(fromId: RegionId, toId: RegionId): Promise<void> {
    const fromReg = game.regions[fromId];
    if (!fromReg || !fromReg.unit) {
      dispatch({ kind: 'move', from: fromId, to: toId });
      view.render(game);
      requestRender();
      return;
    }
    const board: Board = game.regions.map((r) => ({ owner: r.owner, unit: r.unit ? { ...r.unit } : null, buildings: [...r.buildings] }));
    const res = dispatch({ kind: 'move', from: fromId, to: toId }).combat ?? null;
    if (!res) {
      view.render(game);
      requestRender();
      return;
    }
    await animateMoveVisual(me(), fromId, toId, board, res);
  }

  // The glide/arrow visual for ANY seat's move: the local player's own action or, online, a remote
  // player's relayed move (so a peer's units march instead of teleporting). Pure presentation: the state
  // was already mutated before this runs; `board` is the pre-move snapshot the animation starts from.
  /** Boats to draw while a single action animates against a rewound board: every live voyage EXCEPT
   *  the one this animation is currently carrying (that unit is on screen in the animation layer). */
  const sailingNow = (seat: FactionId, fromId: RegionId, toId: RegionId, kind: CombatResult['kind']): Voyage[] =>
    kind === 'sail' ? game.voyages.filter((v) => !(v.owner === seat && v.from === fromId && v.dest === toId)) : [...game.voyages];

  async function animateMoveVisual(seat: FactionId, fromId: RegionId, toId: RegionId, board: Board, res: CombatResult): Promise<void> {
    const race = game.factions[seat]?.race ?? '';
    const unitType = res.attackerType;
    const hitColor = res.defenderDied ? 0xff5a5a : 0xffe27a;
    // a bloodless advance (plain move, empty tile, or boarding a boat) marches; a real fight rings steel
    audio.sfx(res.kind === 'moved' || res.kind === 'captured' || res.kind === 'sail' ? 'move' : 'attack');
    if (res.kind === 'sail') {
      // boarding: the unit walks off its tile down to the boat waiting on the water
      const bf = board[fromId];
      if (bf) bf.unit = null;
      view.render(game, board, sailingNow(seat, fromId, toId, res.kind));
      requestRender();
      view.beginBoard(fromId, toId, race, unitType, playerSkinned && seat === game.playerFaction);
      await tween((t) => {
        view.updateMove(t);
        requestRender();
      }, 340);
      view.render(game); // live state: the boat (with its passenger) now floats offshore
      view.endMove();
      requestRender();
      return;
    }
    if (res.kind === 'ranged') {
      // the archer looses a shot from where it stands: an arrow arcs to the target, then the tile flashes
      view.render(game, board, sailingNow(seat, fromId, toId, res.kind)); // pre-hit board: the target is still standing as the arrow flies
      requestRender();
      view.beginArrow(fromId, toId);
      await tween((t) => {
        view.updateArrow(t);
        requestRender();
      }, 300);
      view.endArrow();
      view.flash(toId, hitColor); // impact
      requestRender();
      await delay(180);
      view.clearFlash();
      // an archer DUEL trades volleys: the defender's return arrow flies back and stings the shooter
      // too (player report: both sides took damage but only one arrow ever showed)
      if (res.retaliated) {
        view.render(game); // the target's fate is settled before the return shot leaves the bow
        requestRender();
        view.beginArrow(toId, fromId);
        await tween((t) => {
          view.updateArrow(t);
          requestRender();
        }, 300);
        view.endArrow();
        view.flash(fromId, res.attackerDied ? 0xff5a5a : 0xffe27a);
        requestRender();
        await delay(180);
        view.clearFlash();
      }
    } else {
      const advance = res.captured || res.kind === 'moved';
      const bf = board[fromId];
      if (bf) bf.unit = null; // lift the mover off its source tile for the glide
      view.render(game, board, sailingNow(seat, fromId, toId, res.kind));
      requestRender();
      view.beginMove(fromId, toId, race, unitType, playerSkinned && seat === game.playerFaction); // skins are solo-only
      if (advance) {
        await tween((t) => {
          view.updateMove(t);
          requestRender();
        }, 300);
      } else {
        await tween((t) => {
          view.updateMove(Math.sin(t * Math.PI) * 0.45); // a clash that doesn't take the tile, lunge & recoil
          requestRender();
        }, 280);
      }
      // draw the settled unit FIRST, then remove the glider in the same frame, the unit never blinks out
      view.render(game);
      view.endMove();
      requestRender();
      if (res.kind !== 'moved') {
        view.flash(toId, hitColor);
        requestRender();
        await delay(200);
        view.clearFlash();
        requestRender();
      }
      return;
    }
    view.render(game);
    requestRender();
  }

  async function playReplay(): Promise<void> {
    const before = game.replayBefore;
    const events = game.replay;
    // a fresh advance: no hostile sting has fired yet. Clear BEFORE the empty-replay early return, else a
    // stale id would keep showMessages silent for a genuinely new war/ally-break on an empty-replay turn.
    stungThisTurn.clear();
    if (!before.length || !events.length) return;
    replaying = true;
    view.setSelected(null); // stop any active-unit pulse while the AI turn plays back
    view.setHighlights([], [], []);
    unitMoveFrom = null;
    const savedView = camera.getView(); // restore the player's view after the replay
    let skip = false;
    // ONLINE tables watch the same replay in real time (designer call): no skip. Skipping let one
    // player act seconds before the others even saw the moves; solo and hotseat keep the shortcut.
    const skipHandler = (): void => void (skip = !net);
    const hint = el('div', 'replay-hint');
    if (!net) {
      app.canvas.addEventListener('pointerdown', skipHandler);
      hint.textContent = 'Click to skip ▶';
      uiRoot!.appendChild(hint);
    }
    let bannerFaction = -1; // the persistent turn banner is reused to announce each AI faction
    let lastPan: { x: number; y: number } | null = null;

    const board: Board = before.map((s) => ({ owner: s.owner, unit: s.unit ? { ...s.unit } : null, buildings: [...s.buildings] }));
    // The fleet as it stood BEFORE this advance: take today's boats, then undo the advance's own sea
    // events (drop the ones that boarded during it, restore the ones that landed). The replay then
    // mutates this list as those events animate, so a boat is on screen exactly while it is at sea.
    const boats: Voyage[] = game.voyages.map((v) => ({ unit: { ...v.unit }, owner: v.owner, from: v.from, dest: v.dest }));
    for (const ev of events) {
      if (ev.kind !== 'unit') continue;
      if (ev.combat === 'sail') {
        const i = boats.findIndex((b) => b.owner === ev.faction && b.from === ev.from && b.dest === ev.to);
        if (i >= 0) boats.splice(i, 1);
      } else if (ev.combat === 'sea' && (ev.captured || ev.attackerDied)) {
        boats.push({ unit: { type: ev.unitType as UnitType, owner: ev.faction, movesLeft: 0, hp: Math.max(1, ev.attackerHp) }, owner: ev.faction, from: ev.from, dest: ev.to });
      }
    }
    view.render(game, board, boats);
    requestRender();
    await delay(220);

    // PRODUCTION COMPLETIONS (buildings AND fresh troops) are not shown at all (designer call: the
    // per-faction "look what everyone built" tour right before the player's turn read as losing the
    // turn). They are applied to the replay board SILENTLY up front, so the moves that follow animate
    // over the correct ground, and everything simply stands there when control returns.
    const moves = events.filter((e): e is Extract<ReplayEvent, { kind: 'unit' }> => e.kind === 'unit');
    for (const e of events) {
      if (e.kind === 'build') board[e.region]!.buildings.push(e.building);
      else if (e.kind === 'train') board[e.region]!.unit = { type: e.unitType, owner: e.faction, movesLeft: unitMoves(e.unitType), hp: unitMaxHp(e.unitType) };
    }

    // war / ally-break stings fire at the DECLARING faction's segment (synced with it turning red), not at
    // the after-replay modal; showMessages then stays silent for anyone already stung here. Suppressed in
    // TUTORIAL (its purge drops stray AI diplomacy so it never talks over the lesson) and in HOTSEAT (the
    // replay is watched by the PREVIOUS human, but me() here is the NEXT one, so a sting would play to the
    // wrong seat) - both let showMessages handle the sound at the right time instead.
    const localSeat = me();
    const stingInReplay = !tutorialMode && !(game.mode === 'mp' && !net);
    const warFrom = stingInReplay ? new Set(game.messages.filter((m) => m.kind === 'war' && m.to === localSeat).map((m) => m.from)) : new Set<FactionId>();
    const breakFrom = stingInReplay ? new Set(game.messages.filter((m) => m.kind === 'allyBroken' && m.to === localSeat).map((m) => m.from)) : new Set<FactionId>();

    // pan/zoom to the action when it moved far enough to leave the view (keeps the camera following the
    // sequence). Distance-based, not island-based: a single-landmass land map has ONE island id, and an
    // id check there would pan once and never follow again.
    const panToRegion = async (regId: RegionId): Promise<void> => {
      const w = regionWorld(regId);
      if (lastPan && Math.hypot(w.x - lastPan.x, w.y - lastPan.y) < HEX_SIZE * 6) return;
      lastPan = { x: w.x, y: w.y };
      await panTo(w.x, w.y, 260);
    };
    // announce whose segment is playing (faction-coloured banner) + fire its hostile sting once. `label`
    // overrides the default turn text (used for the local seat's own finished buildings when it is NOT
    // actually their turn next, so the banner never falsely reads "YOUR TURN").
    const announce = async (fid: FactionId, label?: string): Promise<void> => {
      if (fid === bannerFaction && label === undefined) return;
      bannerFaction = fid;
      if (fid !== me()) turnAway = true; // someone else held the banner: arm the your-turn chime
      const actor = game.factions[fid];
      if (actor) {
        const c = factionCosmetic(actor);
        const text = label ?? (actor.isPlayer ? '⚔  YOUR TURN' : `⚔  ${possTurn(actor.name)}`);
        setTurnBanner(text, `#${actor.color.toString(16).padStart(6, '0')}`, c.avatar, c.frame);
      }
      if (!stungThisTurn.has(fid)) {
        if (warFrom.has(fid)) { audio.sfx('war'); stungThisTurn.add(fid); }
        else if (breakFrom.has(fid)) { audio.sfx('allybreak'); stungThisTurn.add(fid); }
      }
      await delay(520); // a beat so you register whose segment is about to play before it does
    };
    for (const ev of moves) {
      if (skip) break;
      if (ev.faction !== bannerFaction) {
        await announce(ev.faction);
        if (skip) break;
      }
      // a BOARDING happens at the origin shore: pan THERE, or the walk-to-boat plays off-screen and an
      // AI's crossing reads as a teleport. Every other action pans to its destination as before.
      await panToRegion(ev.kind === 'unit' && ev.combat === 'sail' ? ev.from : ev.to);
      if (skip) break;
      {
        const race = game.factions[ev.faction]?.race ?? '';
        const hitColor = ev.defenderDied ? 0xff5a5a : 0xffe27a;
        // an unopposed landing on an EMPTY beach is a march, not a battle (player report: sailing to
        // empty land played the attack sting): nobody stood there, nobody fell, nothing to ring steel about
        const peacefulLanding = ev.combat === 'sea' && ev.captured && !ev.defenderDied && !ev.attackerDied && ev.defenderHp <= 0;
        const bloodless = ev.combat === 'moved' || ev.combat === 'captured' || ev.combat === 'sail' || peacefulLanding;
        audio.sfx(bloodless && !ev.defenderDied ? 'move' : 'attack');
        if (ev.combat === 'sail') {
          // boarding: the unit walks off its tile down to a boat on the water (the boat itself renders
          // from live state once the replay settles; during the rewind we just show the walk)
          board[ev.from]!.unit = null;
          view.render(game, board, boats);
          requestRender();
          view.beginBoard(ev.from, ev.to, race, ev.unitType, playerSkinned && ev.faction === game.playerFaction);
          await tween((t) => {
            view.updateMove(t);
            requestRender();
          }, 320);
          view.endMove();
          applyUnitEvent(board, ev);
          // the boarding finished: this boat joins the fleet on screen, passenger and all
          boats.push({ unit: { type: ev.unitType as UnitType, owner: ev.faction, movesLeft: 0, hp: Math.max(1, ev.attackerHp) }, owner: ev.faction, from: ev.from, dest: ev.to });
          view.render(game, board, boats);
          requestRender();
        } else if (ev.combat === 'ranged') {
          // the archer stays put and looses a shot: an arrow arcs to the target, then the tile flashes
          view.render(game, board, boats); // target still standing while the arrow is in flight
          requestRender();
          view.beginArrow(ev.from, ev.to);
          await tween((t) => {
            view.updateArrow(t);
            requestRender();
          }, 300);
          view.endArrow();
          view.flash(ev.to, hitColor); // impact
          requestRender();
          applyUnitEvent(board, ev);
          view.render(game, board, boats);
          requestRender();
          await delay(160);
          view.clearFlash();
          requestRender();
          // the duel's return volley: the defending archer's arrow flies back at the shooter
          if (ev.retaliated) {
            view.beginArrow(ev.to, ev.from);
            await tween((t) => {
              view.updateArrow(t);
              requestRender();
            }, 300);
            view.endArrow();
            view.flash(ev.from, ev.attackerDied ? 0xff5a5a : 0xffe27a);
            requestRender();
            await delay(160);
            view.clearFlash();
            requestRender();
          }
        } else if (ev.combat === 'sea') {
          // the landing: BOAT and passenger sail the course bow together; at the shore the boat melts
          // away and the soldier steps onto the sand (or lunges and recoils if the beach holds).
          // Lift this boat out of the static fleet first, or it would sit at anchor while its own
          // sprite sails; a repelled landing puts it back afterwards.
          const advance = ev.captured;
          const bi = boats.findIndex((b) => b.owner === ev.faction && b.dest === ev.to);
          const sailing = bi >= 0 ? boats.splice(bi, 1)[0]! : null;
          view.render(game, board, boats);
          requestRender();
          const ownerCol = game.factions[ev.faction]?.color ?? 0x888888;
          view.beginSail(ev.from, ev.to, race, ev.unitType, ownerCol, playerSkinned && ev.faction === game.playerFaction);
          if (advance) {
            await tween((t) => {
              view.updateSail(t);
              requestRender();
            }, 700);
          } else {
            await tween((t) => {
              view.updateSail(Math.sin(t * Math.PI) * 0.55); // repelled: the boat closes on the beach and falls back
              requestRender();
            }, 620);
          }
          view.endSail();
          view.flash(ev.to, hitColor); // a landing always stirs the beach (capture gold, combat red)
          requestRender();
          applyUnitEvent(board, ev);
          // repelled and still alive: the boat falls back to its anchor and stays on screen
          if (sailing && !ev.captured && !ev.attackerDied) boats.push({ ...sailing, unit: { ...sailing.unit, hp: Math.max(1, ev.attackerHp) }, dest: ev.to });
          view.render(game, board, boats);
          requestRender();
          await delay(300);
          view.clearFlash();
          requestRender();
        } else {
          const advance = ev.combat === 'moved' || ev.combat === 'captured' || ev.captured;
          board[ev.from]!.unit = null; // lift off the source tile for the animation
          view.render(game, board, boats);
          requestRender();
          view.beginMove(ev.from, ev.to, race, ev.unitType, playerSkinned && ev.faction === game.playerFaction);
          if (advance) {
            await tween((t) => {
              view.updateMove(t);
              requestRender();
            }, 340);
          } else {
            // a clash that does not take the tile: lunge partway and recoil
            await tween((t) => {
              view.updateMove(Math.sin(t * Math.PI) * 0.45);
              requestRender();
            }, 300);
          }
          view.endMove();
          if (ev.combat !== 'moved') {
            view.flash(ev.to, hitColor);
            requestRender();
          }
          applyUnitEvent(board, ev);
          view.render(game, board, boats);
          requestRender();
          await delay(ev.combat === 'moved' ? 130 : 300);
          view.clearFlash();
          requestRender();
        }
      }
      // a clear beat between the AI's moves so the player can actually follow each one (click-to-skip stays instant)
      if (!skip) await delay(AI_STEP_PAUSE);
    }
    // (production completions were applied silently up front: no per-faction showcase, no banners,
    // no camera tour before control returns - see the designer note above the moves split)
    view.endMove();
    view.clearFlash();
    app.canvas.removeEventListener('pointerdown', skipHandler);
    hint.remove();
    // glide back to the player's pre-replay view instead of snapping to it (no jump-cut when control returns);
    // if they skipped the replay they want speed, so jump straight back
    if (skip) {
      camera.setView(savedView);
    } else {
      const cw = app.canvas.clientWidth || app.screen.width;
      const ch = app.canvas.clientHeight || app.screen.height;
      await flyTo((cw / 2 - savedView.x) / savedView.scale, (ch / 2 - savedView.y) / savedView.scale, savedView.scale, 460);
    }
    view.render(game); // settle on the real final state
    requestRender();
    replaying = false;
  }

  // Non-blocking diplomacy offers: an AI's trade / alliance / peace ask arrives as a card in the bottom-right
  // stack AFTER its turn (not a mid-sequence modal), so the map and the top resource bar stay visible while the
  // player weighs it. Answer in place, or dismiss (✕) and handle it later from the glowing Diplomacy button.
  // Cards are pruned once their offer is gone (answered from the panel, or expired).
  function mountOfferToasts(root: HTMLElement): { show: (ids: number[]) => void } {
    const OFFER_TOAST_MS = 13000; // how long a toast lingers before it auto-hides (the offer still lives on the Diplomacy button)
    const wrap = el('div', 'offer-toasts');
    root.appendChild(wrap);
    const timers = new Map<HTMLElement, ReturnType<typeof setInterval>>();
    const kill = (card: HTMLElement): void => {
      const t = timers.get(card);
      if (t != null) clearInterval(t);
      timers.delete(card);
      card.remove();
    };
    const refresh = (): void => {
      hud.refresh(game);
      menu.refresh();
      overview.refresh();
    };
    const hasCard = (id: number): boolean => [...wrap.children].some((c) => Number((c as HTMLElement).dataset.offerId) === id);
    const build = (offerId: number): void => {
      const o = game.offers.find((x) => x.id === offerId);
      const from = o ? game.factions[o.from] : undefined;
      if (!o || !from) return;
      const card = el('div', 'offer-toast');
      card.dataset.offerId = String(o.id);
      card.style.setProperty('--fc', `#${from.color.toString(16).padStart(6, '0')}`);
      // a colour-coded TYPE banner so an alliance reads apart from a trade from a peace deal at a glance
      const kindMeta = {
        alliance: { icon: '⭐', label: 'Alliance offer', cls: 'msg-type--alliance' },
        peace: { icon: '🕊️', label: 'Peace offer', cls: 'msg-type--peace' },
        trade: { icon: '🤝', label: 'Trade offer', cls: 'msg-type--trade' },
      }[o.kind];
      const banner = el('div', `msg-type ${kindMeta.cls}`);
      banner.append(el('span', 'msg-type-ic', kindMeta.icon), el('span', 'msg-type-label', kindMeta.label));
      const closeX = el('button', 'offer-toast__close', '✕');
      closeX.title = 'Dismiss, still answerable from the Diplomacy button';
      closeX.addEventListener('click', () => kill(card)); // leaves the offer pending in Diplomacy
      const head = el('div', 'offer-toast__head');
      head.append(banner, closeX);
      card.append(head, el('div', 'msg-from', `from ${from.name}`));
      // for a trade, show the exchange as a bold chip (green = you receive, red = you give), the whole ask at a glance
      if (o.kind === 'trade' && o.give && o.want) {
        const swap = el('div', 'msg-swap');
        swap.append(
          el('span', 'msg-swap-in', `+${o.give.amt} ${RES_ICON[o.give.res]}`),
          el('span', 'msg-swap-arrow', '⇄'),
          el('span', 'msg-swap-out', `−${o.want.amt} ${RES_ICON[o.want.res]}`),
        );
        card.append(swap);
      } else {
        // alliance / peace carry no swap chip, so a short line spells out the ask
        card.append(el('div', 'msg-body', o.kind === 'alliance' ? 'Neither of us could attack the other while it holds.' : 'Ends our war.'));
      }
      const btns = el('div', 'msg-btns');
      const acc = el('button', 'rpanel__btn msg-btn', 'Accept');
      // whether the trade can actually go through right now, re-checked live. It can fail because the player
      // spent their side, OR because the PROPOSER already spent what it promised (then the fault is theirs, not
      // the player's, so never blame the player for goods they clearly have).
      const syncAccept = (): void => {
        const chk = checkTradeOffer(game, o);
        acc.disabled = !chk.ok;
        acc.title = chk.ok
          ? ''
          : chk.playerShort && o.want
            ? `You need ${o.want.amt}${RES_ICON[o.want.res]} to accept this trade`
            : chk.atWar
              ? `You are at war with ${from.name}`
              : `${from.name} can no longer honour this trade`;
      };
      syncAccept();
      acc.addEventListener('click', () => {
        if (!checkTradeOffer(game, o).ok) return syncAccept(); // conditions changed (either side can't pay / now at war), block
        const flash = captureStock();
        if (dispatch({ kind: 'respondOffer', offerId: o.id, accept: true }).ok) {
          if (!net) flash(); // float the +gained / -given resources over the HUD (online the trade settles on the relay echo, which floats its own delta)
          kill(card);
          refresh();
        }
      });
      const rej = el('button', 'rpanel__btn rpanel__btn--cancel msg-btn', 'Reject');
      rej.addEventListener('click', () => {
        dispatch({ kind: 'respondOffer', offerId: o.id, accept: false });
        kill(card);
        refresh();
      });
      btns.append(acc, rej);
      card.append(btns);
      // a countdown bar (in the offerer's colour) that empties over OFFER_TOAST_MS, then auto-hides the toast.
      // Hovering pauses it so a card can't vanish mid-read; the live tick also keeps the Accept button honest.
      const bar = el('div', 'offer-toast__bar');
      const fill = el('div', 'offer-toast__bar-fill');
      bar.append(fill);
      card.append(bar);
      let elapsed = 0;
      let paused = false;
      card.addEventListener('mouseenter', () => (paused = true));
      card.addEventListener('mouseleave', () => (paused = false));
      const STEP = 100;
      const timer = setInterval(() => {
        // the offer left game.offers (answered from the Diplomacy panel, or expired at turn-end): prune this card too
        if (!game.offers.some((x) => x.id === o.id)) {
          kill(card);
          return;
        }
        if (!paused) elapsed += STEP;
        fill.style.width = `${Math.max(0, 1 - elapsed / OFFER_TOAST_MS) * 100}%`;
        syncAccept();
        if (elapsed >= OFFER_TOAST_MS) kill(card);
      }, STEP);
      timers.set(card, timer);
      wrap.appendChild(card);
    };
    return {
      show(ids: number[]): void {
        // drop cards whose offer no longer exists (answered from the Diplomacy panel, or expired)
        for (const card of [...wrap.children]) {
          if (!game.offers.some((o) => o.id === Number((card as HTMLElement).dataset.offerId))) kill(card as HTMLElement);
        }
        for (const id of ids) if (!hasCard(id)) build(id);
        while (wrap.childElementCount > 6 && wrap.firstElementChild) kill(wrap.firstElementChild as HTMLElement); // cap the stack; the rest live on the Diplomacy button
      },
    };
  }

  // --- hotseat handoff: a full-screen gate between two local humans' turns, so the device can be passed
  // without the next seat's board being shown mid-swap. Resolves when the incoming player taps Start. ---
  function showHandoff(): Promise<void> {
    return new Promise((resolve) => {
      const f = game.factions[me()]!;
      const overlay = el('div', 'msg-overlay handoff-overlay');
      const card = el('div', 'msg-card handoff-card');
      card.style.setProperty('--fc', colorOf(f.id));
      const av = framedAvatar(`av_${f.race}`, 'fr_default', 64);
      av.classList.add('handoff-avatar');
      card.append(av, el('div', 'msg-head', `${f.name}, it's your turn!`), el('div', 'msg-body', 'Pass the device to the next commander.'));
      const go = el('button', 'rpanel__btn msg-btn', '▶ Start turn');
      go.addEventListener('click', () => {
        overlay.remove();
        resolve();
      });
      card.appendChild(go);
      overlay.appendChild(card);
      uiRoot!.appendChild(overlay);
    });
  }

  // --- after the replay: show any war declarations on the player, one at a time (offers were already
  // prompted mid-sequence, so only war notifications remain in game.messages) ---
  async function showMessages(): Promise<void> {
    const shown: GameState['messages'] = [];
    for (const msg of game.messages) {
      if (msg.kind !== 'war' && msg.kind !== 'allyBroken') continue;
      if (msg.to !== undefined && msg.to !== me()) continue; // hotseat: only the seat holding the device
      shown.push(msg);
      await new Promise<void>((resolve) => {
        const from = game.factions[msg.from];
        if (!from) {
          resolve();
          return;
        }
        // the sting normally fired during the replay (synced with the declarer turning red); only play it
        // here for a source that had no replay segment to sync to (e.g. a remote human's declaration)
        if (!stungThisTurn.has(msg.from)) audio.sfx(msg.kind === 'war' ? 'war' : 'allybreak');
        const overlay = el('div', 'msg-overlay');
        const card = el('div', 'msg-card');
        card.style.setProperty('--fc', `#${from.color.toString(16).padStart(6, '0')}`);
        const done = (): void => {
          overlay.remove();
          resolve();
        };
        const closeX = el('button', 'msg-close', '✕');
        closeX.addEventListener('click', done);
        card.appendChild(closeX);
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) done();
        });
        if (msg.kind === 'war')
          card.append(
            el('div', 'msg-head', `⚔️ ${from.name} declared war!`),
            el('div', 'msg-body', `${from.name} mobilises against you, they can strike from next turn. Rally your defences.`),
          );
        else
          card.append(
            el('div', 'msg-head', `💔 ${from.name} broke your alliance!`),
            el('div', 'msg-body', `${from.name} has torn up your pact. The truce is over: guard your borders, and remember this when they come asking again.`),
          );
        const ok = el('button', 'rpanel__btn msg-btn', msg.kind === 'war' ? 'To arms!' : 'So be it');
        ok.addEventListener('click', done);
        card.appendChild(ok);
        overlay.appendChild(card);
        uiRoot!.appendChild(overlay);
      });
    }
    // consume what was displayed. War entries are rebuilt fresh at each walk anyway, but the engine now
    // PRESERVES allyBroken entries across walks (so they survive long enough to be shown at all) - left
    // in place, the betrayal card would re-show after every turn for the rest of the match. `messages`
    // is excluded from snapshot/hash, so a UI-side clear can never desync lockstep.
    if (shown.length) game.messages = game.messages.filter((m) => !shown.includes(m));
  }

  // --- top bar: round, player resources, End Turn ---
  function mountHud(root: HTMLElement): { refresh: (g: GameState) => void; flashSpend: (cost: number[]) => void; flashDelta: (delta: number[]) => void } {
    const bar = el('div', 'hud');
    const brand = el('div', 'hud__brand', 'WARRING ISLES');
    // persistent "this is you" chip: colour swatch + your race, so the player never loses track of their
    // side. Rebuilt by fillYouChip so a hotseat handoff can swap it to the seat now holding the device.
    const you = el('div', 'hud__you');
    let youFid: FactionId | null = null; // which seat the chip currently shows (skip pointless rebuilds)
    const fillYouChip = (): void => {
      if (youFid === me()) return;
      youFid = me();
      const meFac = game.factions[youFid]!;
      const meRace = RACES.find((r) => r.key === meFac.race);
      you.textContent = '';
      const cos = factionCosmetic(meFac);
      const youAvatar = framedAvatar(cos.avatar, cos.frame, 30); // solo: the cosmetic avatar; hotseat: the seat's race
      youAvatar.classList.add('hud__you-avatar');
      const youSwatch = el('span', 'hud__you-swatch'); // keep the faction-colour dot so the map colour is never lost
      youSwatch.style.background = `#${meFac.color.toString(16).padStart(6, '0')}`;
      you.append(youAvatar, youSwatch, el('span', 'hud__you-label', `You · ${meFac.name}`));
      you.title = `You command the ${meRace?.colorName ?? ''} ${meFac.name}. Click to fly to your capital.`;
    };
    fillYouChip();
    you.classList.add('hud__you--clickable');
    you.addEventListener('click', () => {
      if (replaying || animating) return;
      const cap = game.regions.find((r) => r.owner === me() && r.isCapital);
      if (cap) void panTo(regionWorld(cap.id).x, regionWorld(cap.id).y, 520); // smooth glide, not a jump-cut
    });
    const round = el('div', 'hud__round');
    const dom = el('div', 'hud__dom');
    dom.title = game.objective
      ? `Objective: take ${game.objective.name}. Taking it wins the level outright.`
      : `Your share of the conquerable tiles (cliffs and walls do not count). Win at ${Math.round(game.dominationPct * 100)}%, or lead by round ${game.roundLimit}`;
    const army = el('div', 'hud__army');
    army.title = 'Army supply: units you field / your cap. Build a Harbor to raise the cap by 1.';
    // appears only when you are cornered (few regions left): your defenders take less damage, hold and fight back
    const lastStand = el('div', 'hud__laststand', '🛡 Last Stand');
    lastStand.title = 'Besieged: your units take less damage while you hold this few regions. Dig in and counter-attack.';
    const stocks = el('div', 'hud__stocks');
    const stockEls = RES_ICON.map((ic, i) => {
      const s = el('div', 'hud__stock');
      s.title = `${RES_NAME[i] ?? ''}: storage caps at ${STOCK_CAP}; income beyond that is lost, so spend it on units, buildings or research`;
      const val = el('span', 'hud__stock-val');
      const inc = el('span', 'hud__stock-inc');
      s.append(el('span', 'hud__stock-icon', ic), val, inc);
      stocks.appendChild(s);
      return { chip: s, val, inc };
    });
    const spacer = el('div', 'hud__spacer');
    // icon + label split so a narrow (CrazyGames embed) viewport can collapse the buttons to icon-only
    // via CSS (.hud__btn-lbl hidden) - otherwise the whole action row overflows off the right edge and
    // End Turn / Menu become unreachable
    const navBtn = (cls: string, ico: string, lbl: string): HTMLButtonElement => {
      const b = el('button', cls) as HTMLButtonElement;
      b.append(el('span', 'hud__btn-ico', ico), el('span', 'hud__btn-lbl', lbl));
      return b;
    };
    const realmBtn = navBtn('hud__techbtn', '🗺️', 'Realm');
    realmBtn.dataset.tut = 'realm'; // stable hook for the tutorial spotlight (all three share .hud__techbtn)
    realmBtn.title = 'Overview of all your tiles: what each is producing, which are idle, issue or cancel orders';
    realmBtn.addEventListener('click', () => overview.toggle());
    const techBtn = navBtn('hud__techbtn', '🔬', 'Research');
    // a state badge (MAX / spinning gear / idle-zzz) that refresh updates WITHOUT wiping the icon+label spans
    // - overwriting the whole button would defeat the narrow-viewport icon-only collapse
    const techBadge = el('span', 'hud__btn-badge');
    techBtn.append(techBadge);
    techBtn.dataset.tut = 'research';
    techBtn.title = 'Research tech';
    techBtn.addEventListener('click', () => menu.toggle('research'));
    const diploBtn = navBtn('hud__techbtn', '🤝', 'Diplomacy');
    const diploBadge = el('span', 'hud__btn-badge'); // pending-offers count; updated in place (keeps icon+label)
    diploBtn.append(diploBadge);
    diploBtn.dataset.tut = 'diplomacy';
    diploBtn.title = 'Alliances & trade';
    // Conquest Trail 2.0 gates: on a campaign level that has not taught these systems yet, the
    // buttons simply do not exist (less to parse for a brand-new player; free play shows everything)
    if (!levelKit().research) techBtn.style.display = 'none';
    if (!levelKit().diplomacy) diploBtn.style.display = 'none';
    diploBtn.addEventListener('click', () => menu.toggle('diplomacy'));
    const endBtn = el('button', 'hud__endturn') as HTMLButtonElement;
    endBtn.append(el('span', 'hud__btn-lbl', 'End Turn'), el('span', 'hud__btn-ico', '▶'));
    endBtn.addEventListener('click', () => {
      if (replaying || animating || advancing) return; // don't end the turn while a move glides or an advance is mid-flight
      selectRegion(null); // drop any selection/highlights before the AI acts (also closes the region panel)
      overview.close(); // close the Realm overview if it's open, so the AI replay isn't hidden behind it
      menu.close(); // close Research / Diplomacy if it's open
      endBtn.disabled = true;
      void (async () => {
        // No ads mid-match: interrupting a turn-based game every turn drives players out. The only interstitial
        // is shown between games (on Play Again), the one break that never interrupts a decision. See showGameOver.
        // We DON'T interrupt with a blocking modal per AI offer either: new offers surface as non-blocking
        // corner cards once control returns (see advanceTurnUx).
        const prevActor = me();
        const r = dispatch({ kind: 'endTurn' });
        if (!r.ok) {
          endBtn.disabled = false; // e.g. online + not our turn: nothing advanced
          return;
        }
        await advanceTurnUx(prevActor, r.newOffers ?? []);
        // online: while a rival acts the button IS dead, so it must look dead too (player request);
        // hud.refresh keeps this in sync on every later state change
        endBtn.disabled = !!net && game.turnOf !== me();
      })();
    });
    const menuBtn = el('button', 'hud__menubtn', '☰');
    menuBtn.title = 'Menu: Resume, Settings, Main Menu or Surrender';
    menuBtn.addEventListener('click', () => {
      lifecycle.gameplayStop(); // the pause menu is not gameplay (CrazyGames lifecycle)
      openGameMenu(
        root,
        () => { persist(); window.location.reload(); },
        () => {
          if (net) {
            // online surrender: leave the room for good. There is no engine resign command yet; closing
            // the socket starts the room's AFK clock, which force-ends and then AI-takes-over the seat,
            // so the remaining players are unblocked without us.
            sessionStorage.removeItem('wi.activeRoom');
            platform?.leftRoom(); // clear CrazyGames' room/invite state for us
            net.room.close();
          } else if (!tutorialMode) {
            // abandon the run, don't offer to resume it from the main menu. NEVER inside a tutorial
            // chapter: chapters don't persist, so the save in storage is the player's REAL campaign
            // (a veteran replaying a lesson from How to Play), and surrendering the lesson must not
            // delete it - the same invariant the chapter-finish handler guards (see onFinish).
            clearSave();
          }
          window.location.reload(); // reload starts back at the main menu
        },
        () => lifecycle.gameplayStart(), // menu dismissed back into play
      );
    });
    bar.append(brand, you, round, dom, army, lastStand, stocks, spacer, realmBtn, techBtn, diploBtn, endBtn, menuBtn);
    root.appendChild(bar);
    let peakRegions = 0; // so the Last Stand badge only fires when you've grown then been pushed back, not at the 1-tile start
    let lastResState = ''; // last rendered research-button state (idle/active/max), so its spinner isn't restarted every refresh
    // floating signed tags off each changed resource's HUD chip: red "-N" sinking (a cost) or green "+N" rising
    // (a gain). Shared by production spends, trades, plunder, and any other player resource change.
    const emitDeltas = (delta: number[]): void => {
      delta.forEach((d, i) => {
        if (!d) return;
        const gain = d > 0;
        const rect = (stockEls[i]?.val ?? stocks).getBoundingClientRect();
        const fx = el('div', `spend-fx${gain ? ' spend-fx--gain' : ''}`, `${gain ? '+' : '-'}${Math.abs(d)}${RES_ICON[i]}`);
        fx.style.left = `${rect.left + rect.width / 2}px`;
        fx.style.top = `${rect.top + rect.height / 2}px`; // start on the resource chip, then drift (down for a cost, up for a gain)
        document.body.appendChild(fx);
        setTimeout(() => fx.remove(), 1750);
      });
    };
    const hud = {
      refresh(g: GameState): void {
        fillYouChip(); // hotseat: the acting seat may have changed at a handoff
        const P0 = me();
        round.textContent = `Round ${Math.min(g.round, g.roundLimit)} / ${g.roundLimit}`; // a cap finish briefly sits at LIMIT+1 internally
        const share = Math.round(((regionCounts(g)[P0] ?? 0) / conquerableTotal(g)) * 100);
        dom.textContent = g.objective ? `🎯 ${g.objective.name}` : `🚩 ${share}% / ${Math.round(g.dominationPct * 100)}%`;
        const cap = unitCap(g, P0);
        const cnt = unitCount(g, P0);
        army.textContent = `⚔️ ${cnt} / ${cap}`;
        army.classList.toggle('is-full', cnt >= cap);
        // the count includes troops that are NOT on the board (still building, or crossing the sea), so
        // the tooltip breaks it down: "12/12" with six soldiers in sight otherwise reads as a bug
        {
          const fielded = g.regions.filter((r) => r.unit && r.unit.owner === P0).length;
          const building = g.regions.filter((r) => r.owner === P0 && r.production?.kind === 'unit').length;
            const waiting = g.regions.filter((r) => r.owner === P0 && r.reserve != null).length;
          const sailing = g.voyages.filter((v) => v.owner === P0).length;
          const parts = [`${fielded} on the map`];
          if (building) parts.push(`${building} in production`);
            if (waiting) parts.push(`${waiting} in reserve`);
          if (sailing) parts.push(`${sailing} at sea`);
          let crownNote = '';
          if (g.capitalRule) {
            const crowns = capitalsHeld(g, P0);
            crownNote = crowns === 0 ? ' You hold NO capital: a kingdom in exile fields 2 fewer. Retake a throne.' : crowns > 1 ? ` Crowns held: ${crowns} (+${crowns - 1} cap; every conquered capital feeds the war machine).` : ' Conquering a rival capital adds +1 cap; losing your own costs dearly.';
          }
          army.title = `Army ${cnt} of ${cap}: ${parts.join(', ')}. Cap: 3 base, +1 per Barracks, +1 per 3 regions.${crownNote} Recruits also cost +1 per 3 soldiers you field, and each extra copy of a building costs more: a grown realm budgets, it does not just click.`;
        }
        const myR = regionCounts(g)[P0] ?? 0;
        peakRegions = Math.max(peakRegions, myR);
        // a genuine "cornered after growing" moment (you had more than this and got pushed back), not the
        // opening. Difficulty-aware: on Hard the shield only starts at 2 regions, and showing the badge
        // at 3-4 there would promise a damage reduction the engine is not giving.
        const lsSoft = lastStandSoftFor(g.difficulty);
        lastStand.classList.toggle('is-on', g.phase === 'playing' && myR > 0 && myR <= lsSoft && peakRegions > lsSoft);
        const myOffers = g.offers.filter((o) => o.to === P0).length; // only the offers THIS seat can answer
        diploBadge.textContent = myOffers ? `(${myOffers})` : ''; // update the badge only; keep the icon + label spans
        diploBtn.classList.toggle('has-offers', myOffers > 0);
        // research button state: 💤 idle (nothing queued) · spinning ⚙ (researching now) · MAX (all techs done)
        const resState = TECHS.every((t) => techStatus(g, P0, t.id) === 'done')
          ? 'max'
          : g.factions[P0]!.research
            ? 'active'
            : 'idle';
        if (resState !== lastResState) {
          lastResState = resState;
          techBtn.classList.toggle('is-researching', resState === 'active'); // a soft glowing gradient while it runs
          // update only the state badge (keeps the icon + label spans intact so the narrow-viewport collapse works)
          techBadge.innerHTML =
            resState === 'max'
              ? '<span class="hud__rmax">MAX</span>'
              : resState === 'active'
                ? '<span class="hud__rspin">⚙</span>'
                : '<span class="hud__ridle">💤</span>';
        }
        const meF = g.factions[P0]!;
        const income = factionIncome(g, P0);
        RES_ICON.forEach((_, i) => {
          const amt = meF.stock[i] ?? 0;
          const capped = amt >= STOCK_CAP; // at storage cap → income is wasted, so flag it instead of showing +N
          stockEls[i]!.val.textContent = String(amt);
          stockEls[i]!.chip.classList.toggle('is-capped', capped);
          stockEls[i]!.inc.textContent = capped ? 'MAX' : income[i] ? `+${income[i]}` : '';
        });
        // light up End Turn once there is nothing left to do: no unit can still act and no owned tile
        // is sitting idle (empty with no production queued)
        const P = P0;
        let movable = false;
        let idle = false;
        for (const r of g.regions) {
          if (r.owner !== P) continue;
          if (r.unit && canStillAct(r.unit)) {
            const t = unitTargets(g, r.id);
            if (t.move.length || t.melee.length || t.ranged.length || t.sea.length) movable = true;
          }
          if (!r.unit && !r.production) idle = true;
        }
        endBtn.classList.toggle('is-ready', g.phase === 'playing' && !movable && !idle);
        // online: a rival's turn = a dead button that LOOKS dead (the click was already a no-op)
        endBtn.disabled = !!net && g.turnOf !== me();
        if (net && g.phase === 'playing') {
          // online: End Turn only on your own turn, and say WHOSE turn blocks it
          const myTurn = g.turnOf === me();
          endBtn.disabled = !myTurn;
          endBtn.title = myTurn ? '' : `Waiting for ${net.seatNames[g.turnOf] ?? g.factions[g.turnOf]?.name ?? 'another player'} to finish their turn`;
        }
      },
      // a pure spend (positive costs → red "-N" tags sinking off each chip). Used by production.
      flashSpend(cost: number[]): void {
        emitDeltas(cost.map((c) => -c));
      },
      // a signed per-resource change: negatives sink in red, positives rise in green. Used by trades, plunder, etc.
      flashDelta(delta: number[]): void {
        emitDeltas(delta);
      },
    };
    return hud;
  }

  // --- region panel: info + actions (claim / build Market / production + cancel) ---
  function mountRegionPanel(root: HTMLElement): { show: (g: GameState) => void } {
    const p = el('div', 'rpanel is-hidden');
    const closeBtn = el('button', 'panel__close rpanel__close', '✕');
    closeBtn.title = 'Close';
    closeBtn.addEventListener('click', () => selectRegion(null));
    const title = el('h2', 'rpanel__title');
    const owner = el('div', 'rpanel__owner');
    const facts = el('div', 'rpanel__facts');
    const actions = el('div', 'rpanel__actions');
    p.append(closeBtn, title, owner, facts, actions);
    root.appendChild(p);

    const costStr = (cost: number[]): string =>
      cost.map((c, i) => (c > 0 ? `${c}${RES_ICON[i]}` : '')).filter(Boolean).join(' ');

    function afterAction(): void {
      view.render(game);
      hud.refresh(game);
      selectRegion(game.selectedRegion); // refreshes selection, highlights, and the panel
    }

    function show(g: GameState): void {
      const id = g.selectedRegion;
      if (id == null || !g.regions[id]) {
        p.classList.add('is-hidden');
        return;
      }
      p.classList.remove('is-hidden');
      const reg = g.regions[id]!;
      const isl = g.islands[reg.island]!;
      const p0 = me();
      const ownerName = reg.owner === NEUTRAL ? 'Neutral' : g.factions[reg.owner]!.name;
      title.textContent = reg.isCapital ? '★ Capital region' : 'Region';
      owner.textContent = `Held by: ${ownerName}`;
      owner.style.color = reg.owner === NEUTRAL ? '#9fb0c0' : `#${g.factions[reg.owner]!.color.toString(16).padStart(6, '0')}`;
      const res = isl.resources.map((r) => `${RES_ICON[r]} ${RES_NAME[r]}`).join(' · ');
      const capNote = reg.isCapital ? ' ★ Seat of power: +2 income of each good; sacking it plunders the treasury.' : '';
      // a mainland has provinces, not islands: name the ground the way the map reads. The neutral
      // centre isle keeps its map-label name so the panel and the label never disagree
      const where = g.mapKind === 'land' ? (g.islands[reg.island]?.faction === NEUTRAL ? 'The wilds' : `This province`) : (g.islands[reg.island]?.faction === NEUTRAL ? 'The Wilds' : `Island ${reg.island + 1}`);
      facts.textContent = `${where} produces ${res}. Your regions: ${regionCounts(g)[p0] ?? 0}.${capNote}`;

      actions.textContent = '';
      const showUnit = (): void => {
        if (!reg.unit) return;
        const u = reg.unit;
        const line = el('div', 'rpanel__unit', `${UNIT_ICON[u.type]} ${UNIT_NAME[u.type]} · ${u.hp}/${unitMaxHp(u.type)} HP`);
        actions.appendChild(line);
        if (u.owner === p0) {
          // move points: Riders get 2 a turn (2/2 → 1/2 → 0/2), everyone else 1 (1/1 → 0/1). A land move spends one; an attack or a sea crossing spends them all
          const max = unitMoves(u.type);
          const used = !canStillAct(u); // an archer with its arrow unspent is NOT done, even at 0 move points
          const arrow = hasArrow(u); // the Archer's shot: one a round, and it costs no movement
          const chipText = used
            ? `⚑ Move points 0 / ${max} · already acted this turn`
            : u.movesLeft <= 0 && arrow
              ? `🏹 Arrow ready · 0 / ${max} move points, but it can still shoot 2 tiles off`
              : `⚑ Move points ${u.movesLeft} / ${max}${arrow ? ' · 🏹 arrow ready (shooting is free of movement)' : ''} · click a highlighted tile to move or attack`;
          const chip = el('div', `rpanel__moves ${used ? 'is-spent' : 'is-ready'}`, chipText);
          actions.appendChild(chip);
          // it can still act but has NO legal move → explain why (this is the confusing "stuck on an enemy island" case)
          if (!used) {
            const tg = unitTargets(g, reg.id);
            if (tg.move.length + tg.melee.length + tg.ranged.length + tg.sea.length === 0) {
              let why = 'Boxed in by enemy land. Declare war (🤝 Diplomacy) to attack out from next turn';
              if (isCoastal(g, reg) && !islandHasHarbor(g, p0, reg.island)) why += ', or build a ⚓ Harbor here to sail away';
              actions.appendChild(el('div', 'rpanel__hint', `⚠ ${why}.`));
            }
          }
        } else {
          line.textContent += ` · ${g.factions[u.owner]!.name}`;
        }
      };

      // one compact production tile: icon + name + a cost/turns sub-line. `done` renders an inert
      // "already built" chip; otherwise it's a button, dimmed when it can't be afforded/placed.
      const prodItem = (icon: string, name: string, sub: string, opts: { disabled?: boolean; done?: boolean; tip?: string; onClick?: () => void; tut?: string }): HTMLButtonElement => {
        const b = el('button', `rpanel__pitem${opts.done ? ' is-done' : ''}`);
        b.innerHTML = `<span class="rpanel__pitem-ic">${icon}</span><span class="rpanel__pitem-body"><span class="rpanel__pitem-name">${name}</span><span class="rpanel__pitem-sub">${sub}</span></span>`;
        if (opts.done || opts.disabled) b.disabled = true;
        if (opts.tip) setTip(b, opts.tip);
        if (opts.tut) b.dataset.tut = opts.tut; // stable hook for the tutorial spotlight
        if (opts.onClick && !opts.done) b.addEventListener('click', opts.onClick);
        return b;
      };

      if (reg.owner === p0) {
        showUnit();
        // a recruit waiting in the yard because the tile was occupied when it was ready: say who it is and
        // what it is waiting for, so "I trained a soldier and nothing appeared" is never a mystery
        if (reg.reserve != null) {
          const rt = reg.reserve;
          const res = el('div', 'rpanel__prod rpanel__prod--reserve');
          res.appendChild(el('div', 'rpanel__prod-label', `${UNIT_ICON[rt]} ${UNIT_NAME[rt]} in reserve`));
          res.appendChild(
            el('div', 'rpanel__hint', reg.unit ? 'Waiting for this tile to clear. It musters out one round after the defender leaves or falls.' : 'The tile is clear: it musters out at the end of this round.'),
          );
          actions.appendChild(res);
        }
        if (reg.production) {
          const prod = reg.production;
          const label =
            prod.kind === 'building' ? `${BUILDING_ICON[prod.building]} ${BUILDING_NAME[prod.building]}` : `${UNIT_ICON[prod.unitType]} ${UNIT_NAME[prod.unitType]}`;
          const wrap = el('div', 'rpanel__prod');
          wrap.appendChild(el('div', 'rpanel__prod-label', `Producing ${label} · ${prod.turnsLeft} turn${prod.turnsLeft === 1 ? '' : 's'} left`));
          const bar = el('div', 'rpanel__bar');
          const fill = el('div', 'rpanel__bar-fill');
          fill.style.width = `${Math.round(((prod.turnsTotal - prod.turnsLeft) / prod.turnsTotal) * 100)}%`;
          bar.appendChild(fill);
          wrap.appendChild(bar);
          const cancel = el('button', 'rpanel__btn rpanel__btn--cancel', 'Cancel (refund)');
          cancel.dataset.tut = 'cancel-production'; // stable hook for the tutorial's refund lesson
          cancel.addEventListener('click', () => {
            dispatch({ kind: 'cancelProduction', region: id });
            afterAction();
          });
          wrap.appendChild(cancel);
          actions.appendChild(wrap);
        } else {
          // two side-by-side categories: Buildings on the left, Units on the right
          const menu = el('div', 'rpanel__prodmenu');

          const kit = levelKit(); // Conquest Trail 2.0: untaught buildings do not appear on this level
          const bCat = el('div', 'rpanel__cat');
          bCat.appendChild(el('div', 'rpanel__cat-head', '🏗️ Buildings'));
          const bItems = el('div', 'rpanel__cat-items');
          if (kit.market && reg.buildings.includes(BUILDING.Market)) {
            bItems.appendChild(prodItem('🏛️', 'Market', 'Built', { done: true, tip: '<b>Market</b>: already standing here, raising this region’s output.' }));
          } else if (kit.market) {
            const can = canBuildMarket(g, id, p0);
            bItems.appendChild(
              prodItem('🏛️', 'Market', `${costStr(marketCost(g, reg, p0))} · 2t`, {
                disabled: !can,
                tip: `<b>Market</b>: raises this region's output. Each turn it yields +1 of each of this island's goods (+2 with Grand Markets).${can ? '' : '<br><i>Not enough resources.</i>'}`,
                onClick: () => {
                  const spent = marketCost(g, reg, p0);
                  if (dispatch({ kind: 'market', region: id }).ok) hud.flashSpend(spent); // only flash the cost if it was actually spent (not on a rejected off-turn click)
                  afterAction();
                },
              }),
            );
          }
          if (kit.barracks) {
            // the muster ground: +1 army cap, on any map
            if (reg.buildings.includes(BUILDING.Barracks)) {
              bItems.appendChild(prodItem('🛡️', 'Barracks', 'Built', { done: true, tip: '<b>Barracks</b>: troops are raised HERE (or on your capital). It also raises your army cap by <b>+1</b>, and defenders standing on it take <b>1 less damage</b>. You may train even with a soldier standing here: the recruit waits as a <b>reserve</b> and musters out a round after the tile is clear.' }));
            } else {
              const can = canBuildBarracks(g, id, p0);
              bItems.appendChild(
                prodItem('🛡️', 'Barracks', `${costStr(barracksCost(g, reg, p0))} · 2t`, {
                  disabled: !can,
                  tip: `<b>Barracks</b>: a muster ground. Troops can only be trained on a Barracks or on your <b>capital</b>, so this is how your army grows beyond home. It also raises your <b>army cap by +1</b> and shields its defender by <b>1 damage</b>. A muster ground can also hold one recruit in <b>reserve</b> while the tile is occupied.${can ? '' : '<br><i>Not enough resources.</i>'}`,
                  onClick: () => {
                    const spent = barracksCost(g, reg, p0);
                    if (dispatch({ kind: 'barracks', region: id }).ok) hud.flashSpend(spent);
                    afterAction();
                  },
                }),
              );
            }
          }
          if (kit.harbor && reg.buildings.includes(BUILDING.Harbor)) {
            bItems.appendChild(prodItem('⚓', 'Harbor', 'Built', { done: true, tip: '<b>Harbor</b>: a trading port. Units sail from THIS tile (a crossing starts at a port), and its shipping pays <b>+1 Gold</b> per turn.' }));
          } else if (kit.harbor && isCoastal(g, reg)) {
            const can = canBuildHarbor(g, id, p0);
            bItems.appendChild(
              prodItem('⚓', 'Harbor', `${costStr(harborCost(g, reg, p0))} · 2t`, {
                disabled: !can,
                tut: 'build-harbor',
                tip: `<b>Harbor</b>: your gateway across the water. A unit sails only from a tile that HOLDS a port, and the port's trade pays <b>+1 Gold</b> per turn. Build one on any coastal tile.${can ? '' : '<br><i>Not enough resources.</i>'}`,
                onClick: () => {
                  const spent = harborCost(g, reg, p0);
                  if (dispatch({ kind: 'harbor', region: id }).ok) hud.flashSpend(spent);
                  afterAction();
                },
              }),
            );
          }
          // when the kit closes every building, the whole category vanishes (an empty "Fully developed."
          // would be a lie on a level that simply has not taught buildings yet)
          if (!bItems.childElementCount && (kit.market || kit.barracks || kit.harbor)) bItems.appendChild(el('div', 'rpanel__cat-empty', 'Fully developed.'));
          if (bItems.childElementCount) {
            bCat.appendChild(bItems);
            menu.appendChild(bCat);
          }

          const uCat = el('div', 'rpanel__cat');
          const armyCap = unitCap(g, p0);
          const armyNow = unitCount(g, p0);
          const atCap = armyNow >= armyCap;
          const uHead = el('div', `rpanel__cat-head${atCap ? ' is-full' : ''}`, `⚔️ Units · ${armyNow}/${armyCap}`);
          // the count includes troops you cannot see on the board (still building, or at sea), so spell
          // it out: "12/12" with six soldiers in view otherwise reads as a bug
          {
            const fielded = g.regions.filter((r) => r.unit && r.unit.owner === p0).length;
            const building = g.regions.filter((r) => r.owner === p0 && r.production?.kind === 'unit').length;
            const waiting = g.regions.filter((r) => r.owner === p0 && r.reserve != null).length;
            const sailing = g.voyages.filter((v) => v.owner === p0).length;
            const parts = [`${fielded} on the map`];
            if (building) parts.push(`${building} in production`);
            if (waiting) parts.push(`${waiting} in reserve`);
            if (sailing) parts.push(`${sailing} at sea`);
            uHead.title = `${parts.join(' · ')}. Cap: 3 base +1 per Barracks +1 per 3 regions.`;
          }
          uCat.appendChild(uHead);
          const uItems = el('div', 'rpanel__cat-items');
          if (reg.unit && canMuster(reg) && reg.reserve == null) {
            // a soldier is standing here, but a muster ground still takes the order: the recruit waits in
            // the yard. Say so once above the list instead of hiding the list (which is what used to happen).
            uItems.appendChild(el('div', 'rpanel__cat-note', '🛡️ Occupied: a recruit trained here waits in reserve and musters out once the tile is clear.'));
          }
          {
            // display order: the two always-available base units (Warrior, Spearman) first, then the
            // tech-gated Archer & Rider, so Spearman sits right after Warrior, not at the bottom.
            ([0, 3, 1, 2] as UnitType[]).forEach((type) => {
              const ic = UNIT_ICON[type]!;
              if (!unitUnlocked(g, p0, type)) {
                // Archer / Rider are gated behind a tech. Point at the NEXT tech the player can actually
                // research toward it: walk the unlock tech's prereq chain to the first one not yet done
                // (so Rider says "Archery" until that's researched, then "Cavalry", never a still-locked tech).
                let step = TECHS.find((t) => t.unlocks === type);
                while (step && step.req && !g.factions[p0]!.techs.includes(step.req)) {
                  const reqId = step.req;
                  step = TECHS.find((t) => t.id === reqId);
                }
                const techName = step?.name ?? 'more research';
                uItems.appendChild(
                  prodItem(ic, UNIT_NAME[type]!, `🔒 ${techName}`, {
                    disabled: true,
                    tip: `${UNIT_TIP[type]}<br><i>Locked: research <b>${techName}</b> (top bar ⚗️) to unlock this unit.</i>`,
                  }),
                );
                return;
              }
              const can = canTrain(g, id, p0, type);
              const reason = atCap
                ? (g.capitalRule && capitalsHeld(g, p0) === 0 ? 'Army at cap. A realm in exile fields 2 fewer: retake a capital, or build a Barracks (+1).' : 'Army at cap. Build a Barracks (+1) to field more.')
                : reg.reserve != null
                  ? 'The yard is full: one recruit is already in reserve here. It musters out once this tile is clear.'
                  : !canMuster(reg)
                    ? 'No muster ground here. Troops are raised on a Barracks, or on your capital.'
                    : 'Not enough resources.';
              uItems.appendChild(
                prodItem(ic, UNIT_NAME[type]!, `${costStr(unitCost(g, p0, type))} · ${UNIT_TURNS[type] ?? 2}t`, {
                  disabled: !can,
                  tut: type === 0 ? 'train-warrior' : type === 1 ? 'train-archer' : type === 2 ? 'train-rider' : undefined,
                  tip: `${UNIT_TIP[type]}${can ? '' : `<br><i>${reason}</i>`}`,
                  onClick: () => {
                    const spent = unitCost(g, p0, type);
                    if (dispatch({ kind: 'train', region: id, unitType: type }).ok) hud.flashSpend(spent);
                    afterAction();
                  },
                }),
              );
            });
          }
          uCat.appendChild(uItems);
          menu.appendChild(uCat);

          actions.appendChild(menu);
        }
      } else if (reg.terrain === 'wall') {
        actions.appendChild(el('div', 'rpanel__hint', '🏰 Castle wall. No unit can climb it. Break through a gate, or let Archers shoot over it from outside.'));
      } else if (reg.terrain === 'mountain') {
        actions.appendChild(el('div', 'rpanel__hint', '⛰️ Impassable cliffs. Armies go around; the passes and roads are where battles happen.'));
      } else if (reg.owner === NEUTRAL) {
        showUnit();
        actions.appendChild(el('div', 'rpanel__hint', '🚩 Unclaimed land. March one of your units onto it to take it. There is no cost, but it must be reachable this turn.'));
      } else {
        showUnit(); // enemy region, reveal its defender so the player knows what they'd face
      }
    }
    return { show };
  }

  // --- realm overview: a wide panel listing every tile you own, grouped by island, with each tile's
  // production status (turns left / idle), one-click build & cancel orders, and a locate button that
  // flies the camera to the tile and selects it. Opened from the top bar. ---
  function mountOverview(root: HTMLElement): { toggle: () => void; refresh: () => void; close: () => void } {
    let open = false;
    let filter: 'all' | 'idle' | 'busy' = 'all';
    const overlay = el('div', 'ov-overlay is-hidden');
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) setOpen(false);
    });
    const panelEl = el('div', 'ov-panel');
    const header = el('div', 'ov-header');
    const titleWrap = el('div', 'ov-titlewrap');
    titleWrap.append(el('h2', 'ov-title', '🗺️ Your Realm'));
    const sub = el('div', 'ov-sub');
    titleWrap.appendChild(sub);
    const filters = el('div', 'ov-filters');
    const mkFilter = (key: 'all' | 'idle' | 'busy', label: string): HTMLButtonElement => {
      const b = el('button', 'ov-filter', label);
      b.addEventListener('click', () => {
        filter = key;
        render();
      });
      return b;
    };
    const fAll = mkFilter('all', 'All');
    const fIdle = mkFilter('idle', '💤 Idle');
    const fBusy = mkFilter('busy', '⏳ Producing');
    filters.append(fAll, fIdle, fBusy);
    const closeBtn = el('button', 'panel__close', '✕');
    closeBtn.addEventListener('click', () => setOpen(false));
    header.append(titleWrap, filters, closeBtn);
    const body = el('div', 'ov-body');
    panelEl.append(header, body);
    overlay.appendChild(panelEl);
    root.appendChild(overlay);

    const costStr = (cost: number[]): string => cost.map((c, i) => (c > 0 ? `${c}${RES_ICON[i]}` : '')).filter(Boolean).join(' ');

    function afterAction(): void {
      view.render(game);
      hud.refresh(game);
      panel.show(game); // keep the region panel in sync if the acted-on tile happens to be selected
      render();
    }

    // fly to a tile and select it (shows it active), closing the overview first
    function locate(id: RegionId): void {
      setOpen(false);
      void panTo(regionWorld(id).x, regionWorld(id).y, 620).then(() => selectRegion(id));
    }

    // compact inline build buttons for a not-producing owned tile, reuses the engine's start/cost/gate fns
    function buildControls(reg: GameState['regions'][number]): HTMLElement {
      const g = game;
      const p0 = me();
      const wrap = el('div', 'ov-build');
      const bbtn = (icon: string, label: string, cost: number[], ok: boolean, title: string, onClick: () => void, tut?: string): void => {
        const b = el('button', 'ov-bbtn');
        b.innerHTML = `<span class="ov-bbtn-name">${icon} ${label}</span><span class="ov-bbtn-cost">${costStr(cost)}</span>`;
        b.disabled = !ok;
        b.title = title;
        if (tut) b.dataset.tut = tut; // stable hook for the tutorial spotlight
        if (ok) b.addEventListener('click', onClick);
        wrap.appendChild(b);
      };
      if (levelKit().market && !reg.buildings.includes(BUILDING.Market)) {
        const can = canBuildMarket(g, reg.id, p0);
        bbtn('🏛️', 'Market', marketCost(g, reg, p0), can, can ? 'Build a Market here' : 'Not enough resources', () => {
          const s = marketCost(g, reg, p0);
          if (dispatch({ kind: 'market', region: reg.id }).ok) hud.flashSpend(s); // only flash when actually spent (a rejected off-turn click spends nothing)
          afterAction();
        }, 'ov-market');
      }
      if (levelKit().barracks && !reg.buildings.includes(BUILDING.Barracks)) {
        const can = canBuildBarracks(g, reg.id, p0);
        bbtn('🛡️', 'Barracks', barracksCost(g, reg, p0), can, can ? 'Build a Barracks: train troops here, +1 army cap, defenders take 1 less' : 'Not enough resources', () => {
          const s = barracksCost(g, reg, p0);
          if (dispatch({ kind: 'barracks', region: reg.id }).ok) hud.flashSpend(s);
          afterAction();
        }, 'ov-barracks');
      }
      if (levelKit().harbor && !reg.buildings.includes(BUILDING.Harbor) && isCoastal(g, reg)) {
        const can = canBuildHarbor(g, reg.id, p0);
        bbtn('⚓', 'Harbor', harborCost(g, reg, p0), can, can ? 'Build a Harbor here' : 'Not enough resources', () => {
          const s = harborCost(g, reg, p0);
          if (dispatch({ kind: 'harbor', region: reg.id }).ok) hud.flashSpend(s);
          afterAction();
        });
      }
      if (!reg.unit) {
        const atCap = unitCount(g, p0) >= unitCap(g, p0);
        ([0, 3, 1, 2] as UnitType[]).forEach((type) => {
          if (!unitUnlocked(g, p0, type)) return; // hide still-locked units in this compact view
          const can = canTrain(g, reg.id, p0, type);
          bbtn(UNIT_ICON[type] ?? '', UNIT_NAME[type] ?? '', unitCost(g, p0, type), can, can ? `Train a ${UNIT_NAME[type]}` : atCap ? (g.capitalRule && capitalsHeld(g, p0) === 0 ? 'Army at cap. In exile (-2): retake a capital, or build a Barracks (+1)' : 'Army at cap, build a Barracks for +1') : !canMuster(reg) ? 'Troops are raised at a Barracks (or your capital)' : 'Not enough resources', () => {
            const s = unitCost(g, p0, type);
            if (dispatch({ kind: 'train', region: reg.id, unitType: type }).ok) hud.flashSpend(s);
            afterAction();
          });
        });
      }
      if (!wrap.childElementCount) wrap.appendChild(el('div', 'ov-nobuild', reg.unit ? 'Occupied, move the unit out to train here' : 'Fully developed'));
      return wrap;
    }

    function render(): void {
      fAll.classList.toggle('is-active', filter === 'all');
      fIdle.classList.toggle('is-active', filter === 'idle');
      fBusy.classList.toggle('is-active', filter === 'busy');
      const g = game;
      const p0 = me();
      const mine = g.regions.filter((r) => r.owner === p0);
      const busyCount = mine.filter((r) => !!r.production).length;
      const idleCount = mine.length - busyCount;
      sub.textContent = `${mine.length} tile${mine.length === 1 ? '' : 's'} · ${busyCount} producing · ${idleCount} idle`;

      body.textContent = '';
      const homeIsland = g.islands.findIndex((i) => i.faction === p0);
      const islandIds = [...new Set(mine.map((r) => r.island))].sort((a, b) => (a === homeIsland ? -1 : b === homeIsland ? 1 : 0) || a - b);
      let shown = 0;
      for (const islId of islandIds) {
        const rows = mine
          .filter((r) => r.island === islId)
          .filter((r) => (filter === 'all' ? true : filter === 'idle' ? !r.production : !!r.production))
          .sort((a, b) => Number(b.isCapital) - Number(a.isCapital) || a.id - b.id);
        if (!rows.length) continue;
        const group = el('div', 'ov-group');
        const gh = el('div', 'ov-group-head');
        const isl = g.islands[islId]!;
        const goods = isl.resources.map((x) => RES_ICON[x] ?? '').join(' ');
        gh.append(el('span', 'ov-group-name', islandName(g, islId)), el('span', 'ov-group-meta', `${goods} · ${rows.length} tile${rows.length === 1 ? '' : 's'}`));
        group.appendChild(gh);
        for (const reg of rows) {
          shown++;
          const row = el('div', `ov-row${reg.production ? '' : ' is-idle'}`);
          row.dataset.rid = String(reg.id); // stable hook so the tutorial can spotlight ONE tile's row
          const idc = el('div', 'ov-idcell');
          idc.title = 'Show on the map';
          idc.append(el('span', 'ov-tag', reg.isCapital ? '★ Capital' : 'Region'));
          const marks = el('span', 'ov-marks');
          for (const b of reg.buildings) marks.append(el('span', 'ov-mark', BUILDING_ICON[b] ?? ''));
          if (reg.unit) marks.append(el('span', 'ov-mark', `${UNIT_ICON[reg.unit.type] ?? ''} ${reg.unit.hp}/${unitMaxHp(reg.unit.type)}`));
          idc.append(marks);
          idc.addEventListener('click', () => locate(reg.id));
          row.append(idc);

          const st = el('div', 'ov-status');
          if (reg.production) {
            const prod = reg.production;
            const label = prod.kind === 'building' ? `${BUILDING_ICON[prod.building] ?? ''} ${BUILDING_NAME[prod.building] ?? ''}` : `${UNIT_ICON[prod.unitType] ?? ''} ${UNIT_NAME[prod.unitType] ?? ''}`;
            st.append(el('span', 'ov-prod', `⏳ ${label} · ${prod.turnsLeft} turn${prod.turnsLeft === 1 ? '' : 's'} left`));
            const bar = el('div', 'ov-bar');
            const fill = el('div', 'ov-bar-fill');
            fill.style.width = `${Math.round(((prod.turnsTotal - prod.turnsLeft) / prod.turnsTotal) * 100)}%`;
            bar.appendChild(fill);
            st.append(bar);
          } else {
            st.append(el('span', 'ov-idle', reg.reserve != null ? "🛡️ Reserve waiting, musters out when the tile is clear" : '💤 Idle, nothing in production'));
          }
          row.append(st);

          const act = el('div', 'ov-actcell');
          if (reg.production) {
            const cancel = el('button', 'ov-cancel', '✕ Cancel (refund)');
            cancel.addEventListener('click', () => {
              dispatch({ kind: 'cancelProduction', region: reg.id });
              afterAction();
            });
            act.append(cancel);
          } else {
            act.append(buildControls(reg));
          }
          row.append(act);

          const go = el('button', 'ov-go', '📍');
          go.title = 'Fly to this tile on the map';
          go.addEventListener('click', () => locate(reg.id));
          row.append(go);
          group.appendChild(row);
        }
        body.appendChild(group);
      }
      if (!shown) body.appendChild(el('div', 'ov-empty', filter === 'idle' ? 'No idle tiles, every tile is busy.' : filter === 'busy' ? 'Nothing in production right now.' : 'You hold no tiles yet.'));
    }

    function setOpen(v: boolean): void {
      open = v;
      overlay.classList.toggle('is-hidden', !open);
      if (open) render();
    }
    function toggle(): void {
      if (replaying || animating) return;
      setOpen(!open);
    }
    function refresh(): void {
      if (open) render();
    }
    return { toggle, refresh, close: () => setOpen(false) };
  }

  // --- tech tree panel (toggled from the top bar) ---
  // --- unified command menu: Research + Diplomacy are two tabs of one standard overlay ---
  function mountMenu(root: HTMLElement): { toggle: (tab: 'research' | 'diplomacy') => void; refresh: () => void; close: () => void } {
    let open = false;
    let tab: 'research' | 'diplomacy' = 'research';
    let lastMsg = '';
    const overlay = el('div', 'menu-overlay is-hidden');
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) setOpen(false);
    });
    const panel = el('div', 'menu-panel');
    const header = el('div', 'menu-header');
    const tabs = el('div', 'menu-tabs');
    const tabResearch = el('button', 'menu-tab', '🔬 Research');
    const tabDiplo = el('button', 'menu-tab', '🤝 Diplomacy');
    tabResearch.addEventListener('click', () => switchTab('research'));
    tabDiplo.addEventListener('click', () => switchTab('diplomacy'));
    tabs.append(tabResearch, tabDiplo);
    const closeBtn = el('button', 'panel__close', '✕');
    closeBtn.addEventListener('click', () => setOpen(false));
    header.append(tabs, closeBtn);
    const body = el('div', 'menu-body');
    panel.append(header, body);
    overlay.appendChild(panel);
    root.appendChild(overlay);

    const branches: [TechBranch, string][] = [
      ['mil', '⚔️ Military'],
      ['eco', '🏛️ Economy'],
      ['def', '🛡️ Defence'],
    ];
    const swatch = (color: number): HTMLElement => {
      const s = el('span', 'race-swatch');
      s.style.background = `#${color.toString(16).padStart(6, '0')}`;
      return s;
    };
    const goods = (ids: readonly number[]): string => ids.map((r) => RES_ICON[r]).join(' ');
    function afterAction(): void {
      render();
      hud.refresh(game);
      view.render(game); // a declared war / peace recolours the map behind the panel (hostile wash + aura), refresh it now
      requestRender();
    }
    function feedback(result: string, kind: string, name: string): void {
      lastMsg = result === 'formed' ? `${name} accepted your ${kind}.` : result === 'offered' ? `Offer sent to ${name}.` : `${name} declined your ${kind}.`;
    }
    // the display label for a HUMAN rival: its player's name online (net.seatNames), else the faction name
    const humanLabel = (fid: FactionId): string | null => (game.factions[fid]?.isPlayer ? (net?.seatNames[fid] ?? game.factions[fid]!.name) : null);

    function renderResearch(): void {
      const P = me();
      const f = game.factions[P]!;
      const current = el('div', 'techpanel__current');
      if (f.research) {
        const t = techById(f.research.tech);
        current.appendChild(el('div', 'techpanel__cur', `Researching ${t?.name ?? ''} · ${f.research.turnsLeft} turn${f.research.turnsLeft === 1 ? '' : 's'} left`));
        const bar = el('div', 'rpanel__bar');
        const fill = el('div', 'rpanel__bar-fill');
        fill.style.width = `${Math.round(((f.research.turnsTotal - f.research.turnsLeft) / f.research.turnsTotal) * 100)}%`;
        bar.appendChild(fill);
        current.appendChild(bar);
        const cancel = el('button', 'rpanel__btn rpanel__btn--cancel', 'Cancel (refund)');
        cancel.title = 'Cancel this research and refund the Gold you paid';
        cancel.addEventListener('click', () => {
          dispatch({ kind: 'cancelResearch' });
          afterAction();
        });
        current.appendChild(cancel);
      } else {
        current.appendChild(el('div', 'techpanel__cur muted', 'No active research. Pick a tech below.'));
      }
      body.appendChild(current);
      const tree = el('div', 'techpanel__tree');
      for (const [br, label] of branches) {
        const col = el('div', 'techpanel__col');
        col.appendChild(el('div', 'techpanel__col-title', label));
        for (const tech of TECHS.filter((t) => t.branch === br).sort((a, b) => a.tier - b.tier)) {
          const status = techStatus(game, P, tech.id);
          const node = el('div', `techpanel__node is-${status}`);
          node.dataset.tut = tech.id; // stable hook so the tutorial can spotlight ONE tech (e.g. eco1/def1)
          node.appendChild(el('div', 'techpanel__node-name', `${status === 'done' ? '✓ ' : ''}${tech.name}`));
          node.appendChild(el('div', 'techpanel__node-desc', tech.desc));
          node.appendChild(el('div', 'techpanel__node-cost', `🪙${tech.gold} · ${tech.turns} turn${tech.turns === 1 ? '' : 's'}`));
          if (status === 'available') {
            if (canResearch(game, P, tech.id)) {
              node.classList.add('clickable');
              node.addEventListener('click', () => {
                dispatch({ kind: 'research', tech: tech.id });
                afterAction();
              });
            } else {
              // available, but you can't pick it right now (no Gold, or already researching something): dim it
              // like a locked node so it doesn't LOOK clickable. The panel re-renders on every resource change,
              // so the moment your Gold is enough this turn it brightens and becomes clickable again.
              node.classList.add('is-poor');
              node.title = f.research ? 'Already researching something' : `Not enough Gold (need 🪙${tech.gold})`;
            }
          }
          col.appendChild(node);
        }
        tree.appendChild(col);
      }
      body.appendChild(tree);
    }

    function renderDiplomacy(): void {
      const P = me();
      const counts = regionCounts(game);
      const explain = el('div', 'diplo__explain');
      explain.append(
        el('div', 'diplo__explain-row', '⚔️ War: Declare War before you can attack. Your armies mobilise, so they can only strike from the next turn. Once war starts, no peace for 3 turns (and no new war for 3 turns after peace).'),
        el('div', 'diplo__explain-row', '🤝 Trade: a one-time swap. Offer a good you have for a good you want; every rival that would accept is listed, and you pick one to trade with.'),
        el('div', 'diplo__explain-row', '⭐ Alliance: neither side can attack the other while it lasts. If it breaks, you cannot re-ally for 3 turns.'),
      );
      body.appendChild(explain);
      if (lastMsg) body.appendChild(el('div', 'diplo__msg', lastMsg));

      // pending offers you can still answer (hotseat: only the seat holding the device answers its own)
      const myOffers = game.offers.filter((o) => o.to === me());
      if (myOffers.length) {
        body.appendChild(el('div', 'diplo__section-title', 'Pending offers'));
        for (const o of myOffers) body.appendChild(offerRow(o));
      }

      // the general diplomatic picture: every faction's standing
      body.appendChild(el('div', 'diplo__section-title', 'Diplomatic standing'));
      for (const f of game.factions) {
        if (f.eliminated) continue;
        const rels: string[] = [];
        for (const o of game.factions) {
          if (o.id === f.id || o.eliminated) continue;
          if (areAllied(game, f.id, o.id)) rels.push(`⭐ ${o.name}`);
          else if (atWar(game, f.id, o.id)) rels.push(`⚔️ ${o.name}`);
        }
        const row = el('div', 'diplo__standing');
        const nameEl = el('div', 'diplo__standing-name', f.id === P ? `${f.name} (you)` : f.name);
        if (f.id !== P) nameEl.append(el('span', 'diplo__standing-strat', STRAT_LABEL[f.strategy] ?? ''));
        row.append(swatch(f.color), nameEl, el('div', 'diplo__standing-rel', rels.length ? rels.join('   ') : 'at peace with all'));
        body.appendChild(row);
      }

      // one-time trade, broadcast to everyone (pick whoever accepts)
      body.appendChild(el('div', 'diplo__section-title', 'Trade'));
      body.appendChild(tradeForm());

      // per-rival war / peace / alliance
      body.appendChild(el('div', 'diplo__section-title', 'Actions'));
      for (const f of game.factions) {
        if (f.id === P || f.eliminated) continue;
        const ally = areAllied(game, P, f.id);
        const war = atWar(game, P, f.id);
        const row = el('div', 'diplo__faction');
        const info = el('div', 'diplo__faction-info');
        const status = ally ? '⭐ Allied' : war ? '⚔️ At war' : 'Neutral';
        info.append(
          el('div', 'diplo__faction-name', `${f.name} · ${counts[f.id] ?? 0} regions`),
          el('div', 'diplo__faction-status', `${status} · produces ${goods(game.islands[f.id]!.resources)}`),
        );
        const acts = el('div', 'diplo__faction-acts');
        // war / peace / alliance, each gated by the 3-turn cool-down, the wait is shown right on the button
        const plural = (n: number): string => (n === 1 ? '' : 's');
        if (war && !parleys(game, P, f.id)) {
          // this realm never talks (raider bands, doomed last stands): say so instead of offering a
          // button that can only ever be refused
          acts.appendChild(el('div', 'diplo__faction-status', '⚔️ Will not parley. There is no peace here.'));
        } else if (war) {
          const left = peaceCooldownLeft(game, P, f.id);
          const mp = actBtn(left > 0 ? `Make Peace · ${left}t` : 'Make Peace', () => {
            const r = dispatch({ kind: 'makePeace', to: f.id });
            // a human victim gets a consent step (the command layer raised an offer); tell the proposer
            if (r.diplo === 'offered') feedback('offered', 'peace', humanLabel(f.id) ?? f.name);
          });
          mp.dataset.tut = 'make-peace'; // stable hooks (+ faction id) for the tutorial spotlight
          mp.dataset.fid = String(f.id);
          if (left > 0) {
            (mp as HTMLButtonElement).disabled = true;
            mp.title = `Fight ${left} more turn${plural(left)} before you can sue for peace`;
          }
          acts.appendChild(mp);
        } else if (!ally) {
          const left = warCooldownLeft(game, P, f.id);
          const dw = actBtn(
            left > 0 ? `Declare War · ${left}t` : 'Declare War',
            () => {
              if (dispatch({ kind: 'declareWar', to: f.id }).ok) {
                audio.sfx('war');
                lastMsg = `War declared on ${f.name}. Your forces can strike them from this turn.`;
              }
            },
            'rpanel__btn--cancel',
          );
          dw.dataset.tut = 'declare-war'; // stable hook so the tutorial spotlights Declare War, not a trade Reject
          dw.dataset.fid = String(f.id); // ... and THIS rival's button, not any red one
          if (left > 0) {
            (dw as HTMLButtonElement).disabled = true;
            dw.title =
              game.round <= NO_WAR_ROUNDS
                ? `No wars until round ${NO_WAR_ROUNDS + 1}`
                : allyCooldownLeft(game, P, f.id) > 0
                  ? `You just ended the alliance, wait ${left} turn${plural(left)} before you can declare war`
                  : `Too soon after peace: wait ${left} turn${plural(left)}`;
          }
          acts.appendChild(dw);
        }
        // alliance (Propose is gated by the re-ally cool-down after a break)
        if (ally)
          acts.appendChild(
            actBtn(
              'Break Alliance',
              () => {
                if (dispatch({ kind: 'breakAlliance', to: f.id }).ok) {
                  audio.sfx('allybreak'); // the breaker hears the pact snap too
                  lastMsg = `Alliance with ${f.name} is broken. Re-allying is locked for a few turns, and they will remember this.`;
                }
              },
              'rpanel__btn--cancel',
            ),
          );
        else if (!war) {
          const left = allyCooldownLeft(game, P, f.id);
          const pa = actBtn(left > 0 ? `Propose Alliance · ${left}t` : 'Propose Alliance', () => feedback(dispatch({ kind: 'proposeAlliance', to: f.id }).diplo ?? 'rejected', 'alliance', f.name));
          pa.dataset.tut = 'propose-ally'; // stable hooks (+ faction id) for the tutorial spotlight
          pa.dataset.fid = String(f.id);
          // mainland politics: one pact per realm (engine canAlly). Explain WHICH side is pact-bound,
          // instead of letting the engine's refusal read as a mysterious "declined".
          const iAmSworn = game.mapKind === 'land' && game.alliances.some(([a, b]) => a === P || b === P);
          const theyAreSworn = game.mapKind === 'land' && game.alliances.some(([a, b]) => a === f.id || b === f.id);
          if (left > 0) {
            (pa as HTMLButtonElement).disabled = true;
            pa.title = `Too soon after breaking an alliance: wait ${left} turn${plural(left)}`;
          } else if (iAmSworn) {
            (pa as HTMLButtonElement).disabled = true;
            pa.title = 'Mainland politics: one pact at a time. Break your current alliance first.';
          } else if (theyAreSworn) {
            (pa as HTMLButtonElement).disabled = true;
            pa.title = `${f.name} is already sworn to another realm. On the mainland each realm holds ONE alliance.`;
          }
          acts.appendChild(pa);
        }
        row.append(swatch(f.color), info, acts);
        body.appendChild(row);
      }
    }

    function offerRow(o: DiploOffer): HTMLElement {
      const f = game.factions[o.from]!;
      let text = '';
      if (o.kind === 'alliance') text = `${f.name} proposes an alliance ⭐`;
      else if (o.kind === 'peace') text = `${f.name} offers peace 🕊️`;
      else if (o.kind === 'trade' && o.give && o.want) text = `${f.name} offers a one-time trade: you get ${o.give.amt}${RES_ICON[o.give.res]} for ${o.want.amt}${RES_ICON[o.want.res]}`;
      const row = el('div', 'diplo__offer');
      row.append(swatch(f.color), el('div', 'diplo__offer-text', text));
      const btns = el('div', 'diplo__offer-btns');
      const accept = actBtn('Accept', () => {
        const chk = checkTradeOffer(game, o);
        if (!chk.ok) {
          lastMsg =
            chk.playerShort && o.want
              ? `You don't have ${o.want.amt}${RES_ICON[o.want.res]} to complete this trade.`
              : chk.atWar
                ? `You can't trade with ${f.name} while at war.`
                : `${f.name} can no longer honour this trade, they've already spent it.`;
          return;
        }
        const flash = captureStock();
        if (!dispatch({ kind: 'respondOffer', offerId: o.id, accept: true }).ok) return;
        if (net) {
          // online the answer applies when the relay echoes it back (~RTT): disable the button so a
          // slow connection doesn't read as a dead click; the panel re-renders when the echo lands
          (accept as HTMLButtonElement).disabled = true;
          accept.title = 'Answer sent…';
        } else {
          hud.refresh(game); // update the HUD numbers, then float the +gained / -given deltas over them
          flash();
        }
      });
      // a trade neither side can currently pay can't be accepted, disable it, with a reason, rather than no-op
      const chk = checkTradeOffer(game, o);
      if (!chk.ok) {
        (accept as HTMLButtonElement).disabled = true;
        accept.title =
          chk.playerShort && o.want
            ? `You need ${o.want.amt}${RES_ICON[o.want.res]} to accept this trade`
            : chk.atWar
              ? `You are at war with ${f.name}`
              : `${f.name} can no longer honour this trade`;
      }
      accept.dataset.tut = 'trade-accept'; // stable hook so the tutorial spotlights THIS button, not any red one
      btns.append(accept, actBtn('Reject', () => dispatch({ kind: 'respondOffer', offerId: o.id, accept: false }), 'rpanel__btn--cancel'));
      row.appendChild(btns);
      return row;
    }

    function actBtn(label: string, run: () => void, extra = ''): HTMLElement {
      const b = el('button', `rpanel__btn ${extra}`, label);
      b.addEventListener('click', () => {
        if (replaying || animating) return; // no diplomacy while the AI replay / a move is still animating
        run();
        afterAction();
      });
      return b;
    }

    // Broadcast one-time trade: build an offer, list every rival that would take it, pick one to swap with.
    function tradeForm(): HTMLElement {
      const wrap = el('div', 'diplo__trade-form');
      const P = me();
      const giveSel = document.createElement('select');
      const wantSel = document.createElement('select');
      giveSel.className = wantSel.className = 'diplo__res-select';
      RES_ICON.forEach((ic, r) => {
        const g1 = document.createElement('option');
        g1.value = String(r);
        g1.textContent = `${ic} ${RES_NAME[r]}`;
        giveSel.appendChild(g1);
        const w1 = document.createElement('option');
        w1.value = String(r);
        w1.textContent = `${ic} ${RES_NAME[r]}`;
        wantSel.appendChild(w1);
      });
      wantSel.selectedIndex = 1; // default to a different resource than give
      const controls = el('div', 'diplo__trade-controls');
      controls.append(el('span', 'diplo__trade-lbl', 'I give 2'), giveSel, el('span', 'diplo__trade-lbl', 'for 2'), wantSel);
      const takers = el('div', 'diplo__trade-takers');
      const propose = el('button', 'rpanel__btn', 'Offer to all');
      propose.addEventListener('click', () => {
        takers.textContent = '';
        const give = { res: Number(giveSel.value), amt: 2 };
        const want = { res: Number(wantSel.value), amt: 2 };
        if (give.res === want.res) {
          takers.appendChild(el('div', 'diplo__trade-none', 'Pick two different goods.'));
          return;
        }
        if ((game.factions[P]!.stock[give.res] ?? 0) < give.amt) {
          takers.appendChild(el('div', 'diplo__trade-none', `You don't have ${give.amt}${RES_ICON[give.res]} to give.`));
          return;
        }
        // AI takers are filtered by whether they would genuinely accept; HUMAN rivals are listed
        // unconditionally (the deal becomes an OFFER in their inbox, so acceptance is theirs to decide)
        const aiIds = tradeTakers(game, P, give, want);
        const humanIds = game.factions.filter((f) => f.isPlayer && !f.eliminated && f.id !== P).map((f) => f.id);
        const ids = [...new Set([...aiIds, ...humanIds])].sort((a, b) => a - b);
        if (!ids.length) {
          takers.appendChild(el('div', 'diplo__trade-none', 'No rival will take that offer right now.'));
          return;
        }
        takers.appendChild(el('div', 'diplo__trade-lbl', 'Willing to trade, pick one:'));
        for (const id of ids) {
          const f = game.factions[id]!;
          const label = humanLabel(id) ?? f.name;
          const b = el('button', 'rpanel__btn', label);
          b.prepend(swatch(f.color));
          b.addEventListener('click', () => {
            if (replaying || animating) return;
            const r = dispatch({ kind: 'trade', to: id, give, want });
            // a human counterparty receives an ESCROWED offer, nothing has settled yet: say so honestly
            lastMsg = !r.ok
              ? `${label} can no longer take that trade.`
              : r.diplo === 'offered'
                ? `Offer sent to ${label}: your ${give.amt}${RES_ICON[give.res]} is held in escrow until they answer.`
                : `Traded ${give.amt}${RES_ICON[give.res]} to ${label} for ${want.amt}${RES_ICON[want.res]}.`;
            afterAction(); // re-renders + hud.refresh, so the escrowed goods visibly leave the stock
          });
          takers.appendChild(b);
        }
      });
      controls.appendChild(propose);
      wrap.append(controls, takers);
      return wrap;
    }

    function render(): void {
      tabResearch.classList.toggle('is-active', tab === 'research');
      tabDiplo.classList.toggle('is-active', tab === 'diplomacy');
      const myPending = game.offers.filter((o) => o.to === me()).length;
      tabDiplo.textContent = myPending ? `🤝 Diplomacy (${myPending})` : '🤝 Diplomacy';
      body.textContent = '';
      if (tab === 'research') renderResearch();
      else renderDiplomacy();
    }
    function switchTab(t: 'research' | 'diplomacy'): void {
      tab = t;
      lastMsg = '';
      render();
    }
    function setOpen(v: boolean, t?: 'research' | 'diplomacy'): void {
      if (t) tab = t;
      open = v;
      lastMsg = '';
      overlay.classList.toggle('is-hidden', !open);
      if (open) render();
    }
    return {
      toggle(t) {
        if (replaying || animating) return; // don't open onto a mid-replay board the player can't see
        if (open && tab === t) setOpen(false);
        else setOpen(true, t);
      },
      refresh() {
        if (open) render();
      },
      close() {
        setOpen(false);
      },
    };
  }

  // --- turn event feed: what the AI did on its turn (transient toasts) ---
  function mountFeed(root: HTMLElement): { show: (events: GameState['log']) => void } {
    const wrap = el('div', 'feed');
    root.appendChild(wrap);
    return {
      show(events: GameState['log']): void {
        for (const ev of events) {
          const card = el('div', `feed__item feed--${ev.kind}`, ev.text);
          wrap.appendChild(card);
          window.setTimeout(() => {
            card.classList.add('feed__item--out');
            window.setTimeout(() => card.remove(), 300);
          }, 5000);
        }
        while (wrap.childElementCount > 8) wrap.firstElementChild?.remove();
      },
    };
  }

  // --- victory / defeat overlay ---
  // A graceful finish instead of a hard cut: freeze input, ease the camera out to the whole archipelago, then
  // pulse the winner's territory in their colour a few times, THEN raise the Victory / Defeat screen.
  async function playEndSequence(): Promise<void> {
    if (ending || document.querySelector('.gameover-overlay')) return;
    ending = true;
    animating = true;
    view.setSelected(null);
    view.setHighlights([], [], []);
    requestRender();
    // 1. slow zoom-out to fit the whole map
    const b = view.bounds();
    await flyTo((b.minX + b.maxX) / 2, (b.minY + b.maxY) / 2, fitScale(), 1300);
    // 2. pulse the winner's land in the winner's colour (yours on a win, the conqueror's on a loss)
    const counts = regionCounts(game);
    let winner = 0;
    for (let i = 1; i < counts.length; i++) if ((counts[i] ?? 0) > (counts[winner] ?? 0)) winner = i;
    const winTiles = game.regions.filter((r) => r.owner === winner).map((r) => r.id);
    const flashColor = game.factions[winner]?.color ?? 0xffffff;
    for (let i = 0; i < 3; i++) {
      for (const id of winTiles) view.flash(id, flashColor);
      requestRender();
      await delay(300);
      view.clearFlash();
      requestRender();
      await delay(220);
    }
    await delay(280);
  }

  // Hotseat result: winner-centric (two humans share this screen, "You win" would be ambiguous), and no
  // meta writes: local friendly matches never touch the device profile's stats/elmas (easy self-farming).
  // D1 hook shared by the solo + online results cards: what tomorrow's daily reward is worth, at the
  // moment of highest attention. Calls claimDaily() itself first - idempotent per local day - so the
  // number is right even when the session crossed midnight or this render beat the boot-time claim
  // (the rejoin-into-ended-match path). If a claim actually lands here, the player just got paid; the
  // boot-time claim then no-ops.
  const dailyTeaser = (): HTMLElement => {
    claimDaily();
    const prof = loadProfile();
    const nextStreak = Math.min(7, (prof.dailyStreak || 0) + 1);
    return el('div', 'gameover-daily', `🎁 Come back tomorrow: +${20 + (nextStreak - 1) * 10} 💎 daily reward${nextStreak > 2 ? ` (day ${nextStreak} streak)` : ''}`);
  };

  function showGameOverHotseat(): void {
    if (net) sessionStorage.removeItem('wi.activeRoom'); // the match is over; nothing to rejoin
    const w = game.winner != null ? game.factions[game.winner] : null;
    clearSave(); // the game is finished, nothing to resume
    const overlay = el('div', 'gameover-overlay');
    const card = el('div', 'gameover-card');
    card.append(el('h1', 'gameover-title', w ? `🏆 ${w.name} win!` : '💀 Stalemate'));
    if (w) {
      card.append(el('p', 'gameover-text', `${w.isPlayer ? 'Commander of the' : 'The AI'} ${w.name} ${w.isPlayer ? 'takes' : 'take'} the isles.`));
    }
    const stats = el('div', 'gameover-stats');
    const stat = (label: string, value: string): void => {
      const row = el('div', 'gameover-stat');
      row.append(el('span', 'gameover-stat__k', label), el('span', 'gameover-stat__v', value));
      stats.append(row);
    };
    stat('Rounds', String(Math.min(game.round, game.roundLimit))); // a cap finish ends AT the wrap past the last round
    if (w) stat('Regions held', `${regionCounts(game)[w.id] ?? 0} / ${conquerableTotal(game)}`);
    card.append(stats);
    const btn = el('button', 'gameover-btn', 'Play Again ↻');
    btn.addEventListener('click', () => {
      btn.disabled = true;
      window.location.reload();
    });
    card.append(btn);
    overlay.appendChild(card);
    uiRoot!.appendChild(overlay);
  }

  // Online result: the seat is fixed, so the headline states YOUR outcome (unlike hotseat's shared
  // screen), and a named winner line uses the players' lobby names. Elmas is awarded like solo, but
  // recordResult (per-race stats, achievements) is deliberately SKIPPED: those could be farmed by one
  // person playing both seats of a private room from two tabs; they need server-attested results first.
  function showGameOverOnline(session: OnlineStart): void {
    sessionStorage.removeItem('wi.activeRoom'); // the match is over; nothing to rejoin
    clearSave();
    lifecycle.gameplayStop(); // the results screen is not gameplay (CrazyGames lifecycle)
    // Report game-over to the room the moment results render (every client does this). It is NOT an
    // immediate teardown: the room holds a grace window so the group can Rematch and a late rejoin can
    // still see the ending, then tears itself down. This also gates Rematch server-side (it only fires
    // once the whole connected table has reported), and guarantees an abandoned finished room self-cleans
    // even if players just close the tab instead of clicking Leave.
    session.room.send({ t: 'finished' });
    const win = game.winner === session.seat;
    // one payout per match: a finished room can be re-entered by code until the teardown quorum lands,
    // and replaying the ending must never pay twice. Keyed on code + the server's per-match nonce, NOT
    // the code alone: codes are reusable, and a friends-rematch in the same room is a NEW match that
    // must absolutely still pay.
    const AWARDED_KEY = 'wi.mp.rewarded.v1';
    const matchKey = `${session.room.roomCode}:${session.matchId}`;
    let awarded: string[] = [];
    try {
      const a = JSON.parse(localStorage.getItem(AWARDED_KEY) ?? '[]') as unknown;
      if (Array.isArray(a)) awarded = a.filter((x): x is string => typeof x === 'string');
    } catch {
      /* unreadable list - treat as empty */
    }
    const firstResult = !awarded.includes(matchKey);
    if (firstResult) {
      try {
        localStorage.setItem(AWARDED_KEY, JSON.stringify([...awarded, matchKey].slice(-30)));
      } catch {
        /* best-effort */
      }
    }
    // MP achievement tallies, with soft anti-farm guards: only matches that STARTED with 2+ human
    // seats and lived past the opening rounds count (a solo lobby vs AI fill, or an instant bail,
    // earns nothing; identity-backed guards arrive with the CrazyGames integration)
    if (firstResult && Object.keys(session.seatNames).length >= 2 && game.round >= 5) {
      const before = loadProfile();
      const myKills = (game.stats.kills[session.seat] ?? []).reduce((s, n) => s + n, 0);
      const after = recordOnlineResult(win, myKills);
      const gained = newlyUnlocked(before, after);
      if (gained.length) {
        const reward = gained.reduce((s, a) => s + achReward(a), 0);
        if (reward > 0) addElmas(reward);
        showAchievementToasts(uiRoot!, gained);
      }
    }
    const winnerFac = game.winner != null ? game.factions[game.winner] : null;
    const overlay = el('div', 'gameover-overlay');
    const card = el('div', 'gameover-card');
    card.append(el('h1', 'gameover-title', win ? '🏆 Victory!' : winnerFac ? '💀 Defeat' : '💀 Stalemate'));
    if (win) {
      card.append(el('p', 'gameover-text', 'You dominate the isles. Your banner flies over every rival.'));
    } else if (winnerFac) {
      const nm = session.seatNames[winnerFac.id] ?? winnerFac.name;
      card.append(el('p', 'gameover-text', winnerFac.isPlayer ? `${nm} takes the isles.` : `The AI ${winnerFac.name} take the isles.`));
    }
    const stats = el('div', 'gameover-stats');
    const stat = (label: string, value: string): void => {
      const row = el('div', 'gameover-stat');
      row.append(el('span', 'gameover-stat__k', label), el('span', 'gameover-stat__v', value));
      stats.append(row);
    };
    stat('Rounds', String(Math.min(game.round, game.roundLimit))); // a cap finish ends AT the wrap past the last round
    stat('Regions held', `${regionCounts(game)[session.seat] ?? 0} / ${conquerableTotal(game)}`);
    card.append(stats);
    const earned = firstResult ? matchElmas(win) : 0; // win or lose, the outcome pays ONCE (no rewarded-ad top-up online yet)
    if (earned > 0) addElmas(earned);
    const teaserEl = dailyTeaser(); // claims BEFORE the balance snapshot below, so the wallet includes it
    const wallet = el('div', 'gameover-elmas');
    if (earned > 0) wallet.append(el('div', 'gameover-elmas__earn', `💎 +${earned} earned`));
    wallet.append(el('div', 'gameover-elmas__bal', `Balance  💎 ${loadProfile().elmas}`));
    card.append(wallet);
    card.append(teaserEl);
    // round continuation (CrazyGames): a real group can Rematch (the whole table returns to the lobby for
    // another game without leaving), or Leave (vote the room closed and go back to the menu).
    const btns = el('div', 'gameover-btns');
    const multi = Object.keys(session.seatNames).length >= 2;
    if (multi) {
      const rematch = el('button', 'gameover-btn', '⚔ Rematch') as HTMLButtonElement;
      rematch.title = 'Take the whole group back to the lobby for another match';
      rematch.addEventListener('click', () => {
        rematch.disabled = true;
        session.room.send({ t: 'rematch' }); // the room resets to a lobby + broadcasts 'rematch'; the pump reloads us into it
      });
      btns.append(rematch);
    }
    const leave = el('button', `gameover-btn${multi ? ' gameover-btn--ghost' : ''}`, multi ? 'Leave' : 'Play Again ↻') as HTMLButtonElement;
    leave.addEventListener('click', () => {
      leave.disabled = true;
      // the game-over 'finished' report already went out on render (arms the room's teardown); just exit.
      platform?.leftRoom();
      window.location.reload();
    });
    btns.append(leave);
    card.append(btns);
    overlay.appendChild(card);
    uiRoot!.appendChild(overlay);
  }

  function showGameOver(): void {
    if (document.querySelector('.gameover-overlay')) return;
    lifecycle.gameplayStop(); // the results screen is not gameplay (CrazyGames lifecycle)
    if (net) {
      showGameOverOnline(net);
      return;
    }
    if (game.mode === 'mp') {
      showGameOverHotseat();
      return;
    }
    const win = game.result === 'win';
    const pf = game.factions[game.playerFaction]!;
    const raceName = RACES.find((r) => r.key === pf.race)?.name ?? pf.race;
    const held = regionCounts(game)[game.playerFaction] ?? 0;
    const total = conquerableTotal(game);
    const before = loadProfile(); // snapshot BEFORE recording, to diff which achievements this game unlocked
    const prevBest = before.byRace[pf.race]?.bestRound ?? null; // read the old record before overwriting it
    const p = game.playerFaction;
    const tally = { captured: game.stats.captured[p] ?? 0, kills: game.stats.kills[p] ?? [0, 0, 0, 0], built: game.stats.built[p] ?? [0, 0, 0, 0] };
    const endRound = Math.min(game.round, game.roundLimit); // a cap finish ends AT the wrap past the final round
    // chapters are designed never to reach game over, but if one ever does it must not tally the real
    // profile or delete the player's real campaign save: keep the invariant EXPLICIT, not implicit
    const profile = tutorialMode ? before : recordResult(pf.race, win, endRound, tally); // tally + persist this result (drives race mastery + achievements)
    const unlocked = tutorialMode ? [] : newlyUnlocked(before, profile);
    if (!tutorialMode) clearSave(); // the game is finished, nothing to resume
    const rstat = profile.byRace[pf.race] ?? { games: 0, wins: 0, bestRound: null };
    const newRecord = win && (prevBest == null || endRound < prevBest);

    // meta-economy: award elmas for the outcome + any achievements unlocked this game. Guarded by !tutorialMode
    // (the tutorial never reaches here, but keep the invariant explicit). `earned`/`balance` stay mutable so the
    // optional rewarded-ad top-up below can update the on-screen numbers, and `rewardedWatched` gates the interstitial.
    const matchBase = tutorialMode ? 0 : matchElmas(win);
    const achBonus = tutorialMode ? 0 : unlocked.reduce((s, a) => s + achReward(a), 0);
    // Conquest Trail: rate this run (1-3 stars by how fast the win landed) and pay ONLY the stars this run
    // added, so replaying a level for a better time tops up instead of paying the whole rating again.
    const campLevel = campaignIndex != null ? CAMPAIGN[campaignIndex] : null;
    const campStarsNow = campLevel ? starsFor(campLevel, win, endRound) : 0;
    const campGained = campLevel && !tutorialMode ? recordCampaignStars(campaignIndex!, campStarsNow).gained : 0;
    let earned = matchBase + achBonus + campGained * ELMAS_PER_STAR;
    if (earned > 0) addElmas(earned);
    // build (and thereby CLAIM) the daily teaser before the balance snapshot: a session crossing local
    // midnight pays the daily inside dailyTeaser(), and the card's Balance line must include that money
    const teaserEl = tutorialMode ? null : dailyTeaser();
    let balance = loadProfile().elmas;
    let rewardedWatched = false;

    const overlay = el('div', 'gameover-overlay');
    const card = el('div', 'gameover-card');
    card.append(el('h1', 'gameover-title', campLevel ? (win ? `🏴 ${campLevel.name} taken!` : `💀 ${campLevel.name} holds`) : win ? '🏆 Victory!' : '💀 Defeat'));
    // campaign rating: three stars that fill by how fast the win landed, with the next tier spelled out so
    // the player knows exactly what a replay is worth
    if (campLevel) {
      const rate = el('div', 'gameover-camp');
      const row = el('div', 'camp-stars camp-stars--big');
      for (let s = 0; s < 3; s++) row.append(el('span', `camp-star${s < campStarsNow ? ' is-on' : ''}`, '★'));
      rate.append(row);
      rate.append(
        el(
          'div',
          'gameover-camp__hint',
          !win
            ? `Take every region to claim this isle. ⭐⭐⭐ within ${campLevel.gold} rounds.`
            : campStarsNow === 3
              ? 'Perfect run. Every star on this isle is yours.'
              : campStarsNow === 2
                ? `Win within ${campLevel.gold} rounds for the third star.`
                : `Win within ${campLevel.silver} rounds for two stars, ${campLevel.gold} for three.`,
        ),
      );
      if (campGained > 0) rate.append(el('div', 'gameover-camp__earn', `+${campGained} ⭐  ·  +${campGained * ELMAS_PER_STAR} 💎`));
      card.append(rate);
    }
    // say WHY the game ended: "wiped from the isles" on a round-cap points loss (while the stat row
    // right under it shows regions still held) taught brand-new players the wrong loss condition
    const rivalName = game.winner != null && game.winner !== p ? game.factions[game.winner]!.name : null;
    const endText = win
      ? game.round > game.roundLimit
        ? `The sun sets on round ${game.roundLimit} with your banner over the most land. The ${raceName} reign supreme.`
        : `You dominate the isles. The ${raceName} reign supreme.`
      : held === 0
        ? `The ${raceName} have been wiped from the isles.`
        : game.round > game.roundLimit
          ? `The sun set on round ${game.roundLimit}, and ${rivalName ?? 'a rival'} holds the most land.`
          : `${rivalName ?? 'A rival'} seized ${Math.round(game.dominationPct * 100)}% of the isles.`;
    card.append(el('p', 'gameover-text', endText));

    // run recap: the stats a player wants after a match, all from data the engine already tracks
    const stats = el('div', 'gameover-stats');
    const stat = (label: string, value: string): void => {
      const row = el('div', 'gameover-stat');
      row.append(el('span', 'gameover-stat__k', label), el('span', 'gameover-stat__v', value));
      stats.append(row);
    };
    stat('Rounds', String(Math.min(game.round, game.roundLimit))); // a cap finish ends AT the wrap past the last round
    stat('Regions held', `${held} / ${total}`);
    stat(`Wins as ${raceName}`, String(rstat.wins));
    stat('Total wins', `${profile.wins} / ${profile.games}`);
    card.append(stats);
    if (newRecord) card.append(el('div', 'gameover-record', `⭐ New record: fastest ${raceName} victory in ${endRound} rounds`));
    // achievements: the bottom-right toasts (badge + chime) are the celebration; the card keeps only a
    // one-line count. (The full card list made the victory modal scroll like an endless parchment, and
    // every unlock is browsable on the Profile anyway.)
    if (unlocked.length) {
      card.append(el('div', 'gameover-achs__title', `🏅 ${unlocked.length} Achievement${unlocked.length > 1 ? 's' : ''} unlocked! See them on your Profile.`));
      showAchievementToasts(uiRoot!, unlocked);
    }

    // elmas earned this match + an optional rewarded-ad top-up (win: double, loss: +5). Real matches only.
    if (!tutorialMode) {
      const wallet = el('div', 'gameover-elmas');
      const earnLine = el('div', 'gameover-elmas__earn', `💎 +${earned} earned`);
      const balLine = el('div', 'gameover-elmas__bal', `Balance  💎 ${balance}`);
      wallet.append(earnLine, balLine);
      const bonus = matchAdBonus(win);
      const adBtn = el('button', 'gameover-adbtn', win ? `▶ Watch to double  +${bonus} 💎` : `▶ Watch for +${bonus} 💎`) as HTMLButtonElement;
      adBtn.addEventListener('click', () => {
        adBtn.disabled = true;
        void lifecycle
          .rewarded(
            () => audio.duck(true),
            () => audio.duck(false),
          )
          .then((granted) => {
            if (!granted) {
              adBtn.disabled = false; // no ad filled (adblock / local dev): grant nothing, let them try again
              return;
            }
            rewardedWatched = true;
            addElmas(bonus);
            earned += bonus;
            balance += bonus;
            earnLine.textContent = `💎 +${earned} earned`;
            balLine.textContent = `Balance  💎 ${balance}`;
            earnLine.classList.remove('is-pop');
            void earnLine.offsetWidth; // reflow so the pop animation restarts
            earnLine.classList.add('is-pop');
            adBtn.remove(); // one top-up per match
          });
      });
      wallet.append(adBtn);
      card.append(wallet);
    }

    // Two exits, the player picks: ⚔ Rematch boots STRAIGHT into a fresh match (same race + settings,
    // new map - the wi.quickNext flag skips the menu on reload), Main Menu is the classic path back to
    // the title screen. Tutorial lessons keep the single classic button: their flow is chapter-driven.
    const reload = (): void => window.location.reload();
    // one interstitial, between games only, gated so it never plays on the first match of a session, within the
    // min interval, or right after a rewarded ad. When the gate says no, fall straight through to the reload.
    const reloadWithAdGate = (): void => {
      if (shouldShowInterstitial(rewardedWatched)) {
        void lifecycle
          .midroll(
            () => audio.duck(true),
            () => audio.duck(false),
          )
          .finally(reload);
      } else {
        reload();
      }
    };
    if (teaserEl) card.append(teaserEl);
    if (tutorialMode) {
      const btn = el('button', 'gameover-btn', 'Play Again ↻');
      btn.addEventListener('click', () => {
        btn.disabled = true;
        reloadWithAdGate();
      });
      card.append(btn);
    } else if (campLevel) {
      // campaign: keep the trail moving. A win points at the NEXT isle, a loss offers this one again,
      // both booting straight into the match so the run never detours through a menu.
      const btns = el('div', 'gameover-btns');
      const nextIdx = campaignIndex! + 1;
      const hasNext = win && nextIdx < CAMPAIGN.length;
      // the act boundary is the arc's biggest beat: the mainland war is WON and the story boards the
      // boats. Mark it instead of chaining silently into the next level.
      const actTurn = hasNext && CAMPAIGN[campaignIndex!]!.act !== CAMPAIGN[nextIdx]!.act;
      if (actTurn) card.append(el('div', 'popup-note', `🏴 The mainland is yours. ${ACT_NAMES[CAMPAIGN[nextIdx]!.act]} begins.`));
      const go = el('button', 'gameover-btn', hasNext ? (actTurn ? `⛵ To the Isles: ${CAMPAIGN[nextIdx]!.name} ▶` : `Next: ${CAMPAIGN[nextIdx]!.name} ▶`) : win ? '⚔ Rematch' : '↻ Try again') as HTMLButtonElement;
      go.addEventListener('click', () => {
        go.disabled = true;
        if (hasNext) setSessionFlag('wi.campaignLevel', String(nextIdx));
        else if (!win) setSessionFlag('wi.campaignLevel', String(campaignIndex!));
        else {
          const pf2 = game.factions[game.playerFaction];
          if (pf2) setSessionFlag('wi.quickNext', `${pf2.race},${game.factions.length},${game.islands[0]?.regions.length ?? 9},${game.dominationPct},${game.difficulty},${game.mapKind}`);
        }
        reloadWithAdGate();
      });
      const toMenu = el('button', 'gameover-btn gameover-btn--ghost', 'Conquest Trail') as HTMLButtonElement;
      toMenu.addEventListener('click', () => {
        toMenu.disabled = true;
        reloadWithAdGate(); // lands on the menu; the Campaign button shows the updated level + stars
      });
      btns.append(go, toMenu);
      card.append(btns);
    } else {
      const btns = el('div', 'gameover-btns');
      const rematch = el('button', 'gameover-btn', '⚔ Rematch') as HTMLButtonElement;
      rematch.title = 'Same race and settings, fresh map';
      rematch.addEventListener('click', () => {
        rematch.disabled = true;
        const pf = game.factions[game.playerFaction];
        if (pf) setSessionFlag('wi.quickNext', `${pf.race},${game.factions.length},${game.islands[0]?.regions.length ?? 9},${game.dominationPct},${game.difficulty},${game.mapKind}`);
        reloadWithAdGate();
      });
      btns.append(rematch);
      // funnel every anonymous match into the Trail: chained matches lengthen the session TODAY, and a
      // visible "Level N" identity is the reason to come back TOMORROW. Hidden once the Trail is done.
      const stars = loadProfile().campaignStars;
      if (beatenCount(stars) < CAMPAIGN.length) {
        const idx = currentLevel(stars);
        const trail = el('button', 'gameover-btn', `🏴 Trail L${idx + 1}: ${CAMPAIGN[idx]!.name} ▶`) as HTMLButtonElement;
        trail.title = 'Continue the Conquest Trail';
        trail.addEventListener('click', () => {
          trail.disabled = true;
          setSessionFlag('wi.campaignLevel', String(idx));
          reloadWithAdGate();
        });
        btns.append(trail);
      }
      const toMenu = el('button', 'gameover-btn gameover-btn--ghost', 'Main Menu') as HTMLButtonElement;
      toMenu.addEventListener('click', () => {
        toMenu.disabled = true;
        reloadWithAdGate();
      });
      btns.append(toMenu);
      card.append(btns);
    }
    overlay.appendChild(card);
    uiRoot!.appendChild(overlay);
  }

  if (import.meta.env.DEV) {
    (window as unknown as { __wi: unknown }).__wi = {
      app,
      game,
      view,
      camera,
      hud,
      panel,
      selectRegion,
      me,
      dispatch,
      net,
      flags: () => ({ replaying, animating, ending }),
      RACES,
      endTurn,
      runTurnGen,
      regionCounts,
      unitTargets,
      moveOrAttack,
      previewCombat,
      startTrain,
      startHarbor,
      canBuildHarbor,
      islandHasHarbor,
      startMarket,
      startResearch,
      menu,
      offerToasts,
      createGame,
      saveGame,
      loadGame,
      clearSave,
      loadProfile,
      recordResult,
      runAiTurn,
      proposeAlliance,
      proposeTrade,
      tradeTakers,
      executeTrade,
      respondOffer,
      areAllied,
      atWar,
      declareWar,
      makePeace,
      canDeclareWar,
      canMakePeace,
      SEED,
      FACTIONS: DEFAULT_FACTIONS,
      rerender: () => {
        view.render(game);
        hud.refresh(game);
        requestRender();
      },
    };
  }

  // --- opening reveal: hold zoomed in on the capital, announce the war, then smoothly zoom out to the whole
  // map. A full-screen banner blocks all input until the reveal finishes, so nothing can be clicked early.
  // (Runs last, after the DEV globals above are exposed, so tooling stays reachable even while this animates.) ---
  if (net && game.phase === 'over') {
    // rejoined a match that already ENDED: no cinematic, no frozen board - straight to the result
    // (showGameOver's online branch also clears wi.activeRoom, so the Rejoin banner dies with it)
    const bb = view.bounds();
    camera.focusOn((bb.minX + bb.maxX) / 2, (bb.minY + bb.maxY) / 2, fitScale());
    requestRender();
    showGameOver();
  } else if (!resumed && !tutorialMode) {
    animating = true;
    const banner = el('div', 'gamestart');
    // hotseat/online announce the human seats (online: player names; hotseat: dealt race names)
    const humanNames = game.factions.filter((f) => f.isPlayer).map((f) => net?.seatNames[f.id] ?? f.name);
    // campaign levels open on their STORY beat instead of the generic banner: the arc is the reason
    // the trail exists, and this moment (input already blocked for the reveal) is its stage
    const campLv = campaignIndex != null ? CAMPAIGN[campaignIndex] : null;
    const sub = campLv
      ? campLv.story
      : game.mode === 'mp'
        ? net
          ? `${humanNames.join(' vs ')} - you lead the ${game.factions[me()]!.name}`
          : `Hotseat: ${humanNames.join(' vs ')} - pass the device between turns`
        : `You lead the ${game.factions[game.playerFaction]!.name}`;
    banner.append(el('div', 'gamestart__title', campLv ? `Level ${campaignIndex! + 1}: ${campLv.name}` : 'The War Begins'), el('div', 'gamestart__sub', sub));
    // the mission, stated plainly: every level says how it is won before the first move
    if (campLv) banner.append(el('div', 'gamestart__goal', `🎯 ${goalText(campLv.goal)}`));
    uiRoot!.appendChild(banner);
    await delay(1600); // linger on your home island
    const bb = view.bounds();
    await flyTo((bb.minX + bb.maxX) / 2, (bb.minY + bb.maxY) / 2, fitScale(), 1500); // reveal the archipelago
    banner.classList.add('is-leaving');
    await delay(360);
    banner.remove();
    animating = false; // controls live now
  } else {
    // resumed game OR the tutorial: skip the cinematic and snap to a fitted view so play starts immediately
    const bb = view.bounds();
    camera.focusOn((bb.minX + bb.maxX) / 2, (bb.minY + bb.maxY) / 2, fitScale());
    requestRender();
  }
  revealDone = true; // the online countdown chip may run from here on (armTurnTimer skipped it until now)
  // daily reward: auto-claimed on the first load of each calendar day and announced with a card that
  // fades on its own. A concrete "come back tomorrow" hook, which is what D1 retention is made of.
  // Skipped inside the tutorial (a lesson should never be interrupted by meta chrome) and when the
  // first-run nudge is up, so a brand-new player only ever sees one card.
  if (!tutorialMode) {
    const daily = claimDaily();
    if (daily.claimed) {
      const card = el('div', 'firstrun firstrun--daily');
      card.append(
        el('div', 'firstrun__title', `🎁 Daily reward  +${daily.amount} 💎`),
        el('div', 'firstrun__text', daily.streak > 1 ? `${daily.streak} days in a row. Come back tomorrow for more.` : 'Come back tomorrow for a bigger reward.'),
      );
      uiRoot!.appendChild(card);
      window.setTimeout(() => {
        card.classList.add('is-leaving');
        window.setTimeout(() => card.remove(), 260);
      }, 5200);
      refreshPlayerChips(); // the wallet on screen should show the new balance immediately
    }
  }
  // first mainland match on this device: one dismissible primer for the rules the isles never taught
  // (the Barracks supply building, impassable walls/cliffs, gates as the way in). Never in tutorials,
  // never again once seen, and never stacked on top of another first-run card. Conquest Trail 2.0:
  // never on a GUIDED campaign level either - the guide is the teacher there, and this card front-loads
  // systems the level's kit deliberately hides (the exact info-dump the trail exists to remove).
  const guidedLevel = campaignIndex != null && !!CAMPAIGN_GUIDES[CAMPAIGN[campaignIndex]?.id ?? ''];
  if (!tutorialMode && !guidedLevel && game.mapKind === 'land' && !localStorage.getItem('wi.landPrimer.v1')) {
    try {
      localStorage.setItem('wi.landPrimer.v1', '1');
    } catch {
      /* storage may be unavailable; the card just shows again next time */
    }
    const card = el('div', 'firstrun');
    card.append(
      el('div', 'firstrun__title', '⛰️ The mainland'),
      el('div', 'firstrun__text', 'Troops are raised on your capital and on Barracks tiles: each Barracks also adds +1 army cap and shields its defender. A coastal Harbor pays gold and carries troops along the shore. Castle walls and cliffs are impassable: take the gates, or let Archers shoot over the walls.'),
    );
    const acts = el('div', 'firstrun__acts');
    const ok = el('button', 'firstrun__btn firstrun__btn--go', 'Understood');
    ok.addEventListener('click', () => {
      card.classList.add('is-leaving');
      window.setTimeout(() => card.remove(), 260);
    });
    acts.append(ok);
    card.append(acts);
    uiRoot!.appendChild(card);
  }
  // --- match objectives: three micro-goals that give a brand-new player DIRECTION in the first minutes
  // (the exact minutes where sessions die). Delta-based against the state at mount, so a resumed game
  // re-offers them for THIS session and completing them twice never double-pays across reloads more than
  // the 10 elmas each - small, honest, and it teaches the expand/train/fight loop. Solo only: online must
  // stay distraction-free and the tutorial has its own guided goals. ---
  if (!net && !tutorialMode && game.mode !== 'mp') {
    const P0 = game.playerFaction;
    const sum = (a: readonly number[] | undefined): number => (a ?? []).reduce((x, y) => x + y, 0);
    const base = { cap: game.stats.captured[P0] ?? 0, built: sum(game.stats.built[P0]), kills: sum(game.stats.kills[P0]) };
    let goalChipIndex = -1; // which chip is the level's victory objective (clickable: flies there)
    const OBJECTIVES: Array<{ id: string; label: string; target: number; value: () => number }> = [
      { id: 'claim', label: '🚩 Claim 3 regions', target: 3, value: () => (game.stats.captured[P0] ?? 0) - base.cap },
      { id: 'train', label: '🛡 Train 2 units', target: 2, value: () => sum(game.stats.built[P0]) - base.built },
      { id: 'slay', label: '⚔ Slay 2 enemies', target: 2, value: () => sum(game.stats.kills[P0]) - base.kills },
    ];
    // siege levels put their PRIZE on the strip: every walled lord's keep, by name. The chip makes
    // "the castle is the mission" explicit instead of living only in the story banner.
    const goalRegion = game.objective?.region;
    if (goalRegion != null) {
      goalChipIndex = OBJECTIVES.length; // the chip built for this entry gets a click-to-find handler
      OBJECTIVES.push({
        id: 'goal',
        label: `🎯 Take ${game.objective!.name}`,
        target: 1,
        value: () => (game.regions[goalRegion]!.owner === P0 ? 1 : 0),
      });
    }
    const OBJ_REWARD = 10;
    const strip = el('div', 'objectives');
    const chips = OBJECTIVES.map((o, i) => {
      const chip = el('div', 'objectives__chip');
      chip.append(el('span', 'objectives__label', o.label), el('span', 'objectives__count', `0/${o.target}`));
      if (i === goalChipIndex && goalRegion != null) {
        // the prize can be anywhere on a big map: one click on its chip flies the camera to it
        chip.classList.add('objectives__chip--goal');
        chip.title = 'Show me where';
        chip.addEventListener('click', () => {
          const w = regionWorld(goalRegion);
          void panTo(w.x, w.y, 420);
          selectRegion(goalRegion);
        });
      }
      strip.append(chip);
      return chip;
    });
    uiRoot!.appendChild(strip);
    const done = new Set<string>();
    const evalObjectives = (): void => {
      OBJECTIVES.forEach((o, i) => {
        if (done.has(o.id)) return;
        const v = Math.max(0, o.value());
        const count = chips[i]!.querySelector('.objectives__count')!;
        count.textContent = `${Math.min(v, o.target)}/${o.target}`;
        if (v >= o.target) {
          done.add(o.id);
          chips[i]!.classList.add('is-done');
          count.textContent = `✓ +${OBJ_REWARD} 💎`;
          addElmas(OBJ_REWARD);
          refreshPlayerChips();
        }
      });
    };
    const objTimer = window.setInterval(() => {
      // evaluate BEFORE the teardown check: the match-ending move (the kill that wipes the last rival, the
      // capture that completes domination) bumps the stat and flips phase in ONE synchronous engine call,
      // so the completion it delivers must still be paid on this final tick.
      evalObjectives();
      if (game.phase !== 'playing') {
        window.clearInterval(objTimer);
        strip.classList.add('is-leaving');
        window.setTimeout(() => strip.remove(), 400);
      }
    }, 1500);
  }
  // opening: the play order is shuffled at game start, so it is NOT always a human who moves first. When
  // one or more AI seats sit ahead of the first human in the order, they take their FIRST turn now (a
  // short replay) before control is handed over. Skipped on resume, and when a human already opens.
  if (!resumed && !net && turnOrderOf(game).indexOf(me()) > 0) {
    // (online matches skip this: the host drained the opening into the start snapshot itself)
    animating = true;
    const gen = runTurnGen(game, true); // opening = only the AIs before the first human of round 1, no round tick
    const openOffers: number[] = [];
    for (let step = gen.next(); !step.done; step = gen.next()) openOffers.push(step.value);
    await playReplay(); // watch the rivals who moved before you
    await showMessages(); // and any war one of them declared on you
    updateTurnBanner(); // your turn (or, online, whoever the match opens on)
    offerToasts.show(openOffers.filter((id) => game.offers.find((o) => o.id === id)?.to === me()));
    view.render(game);
    hud.refresh(game);
    menu.refresh();
    overview.refresh();
    feed.show(game.log);
    animating = false;
    persist(); // checkpoint at your first turn, so Continue resumes after the opening, not before it
  }
  // hotseat: gate the very first human turn behind a handoff card too, so the match starts with a clear
  // "whose seat is this" moment (also covers a resumed hotseat game: whoever's turn it was, it announces)
  if (game.mode === 'mp' && !net && game.phase === 'playing' && !document.querySelector('.gameover-overlay')) {
    animating = true;
    await showHandoff();
    turnAway = true; // the device changed hands: greet the new seat with the chime
    showYourTurn();
    hud.refresh(game);
    animating = false;
  }
  // online: no handoff cards (each device is one fixed seat); just show whose turn the match opens on
  if (net) {
    updateTurnBanner();
    hud.refresh(game);
  }


  // Conquest Trail 2.0 guidance: a campaign level's FIRST play walks the player through its new system
  // with the same spotlight runner, ON the live level (no classroom world). Replays, star runs, resumes
  // and players who turned guidance off in Settings never see it.
  if (!resumed && !tutorialMode && campaignIndex != null) {
    const levelId = CAMPAIGN[campaignIndex]?.id;
    const guide = levelId ? CAMPAIGN_GUIDES[levelId] : undefined;
    let guidanceOff = false;
    try {
      guidanceOff = localStorage.getItem('wi.guidance') === 'off';
    } catch {
      /* storage blocked: guidance stays on */
    }
    if (guide && levelId && !guidanceOff && !loadProfile().campaignGuided.includes(levelId)) {
      const gDeps = {
        game,
        camera,
        uiRoot: uiRoot!,
        hexSize: HEX_SIZE,
        selectRegion,
        panTo,
        regionWorld,
        overview,
        menu,
        offerToasts,
        refresh: () => {
          view.render(game);
          hud.refresh(game);
          menu.refresh();
          overview.refresh();
          panel.show(game);
          requestRender();
        },
        isBusy: () => replaying || animating,
        onFinish: (_completed: boolean) => {
          // completed OR skipped: the player has met this guide; it must never nag the level again.
          // Also counts as onboarded, so the legacy first-run tutorial offer stops asking.
          markCampaignGuided(levelId);
          markOnboarded();
        },
      };
      startTutorial(gDeps, (api) => guide(gDeps, game, api), { skipLabel: 'Skip guide' });
    }
  }
}


// First-run interstitial: one gentle offer of the guided tutorial before the very first real game.
// Two buttons, primary focused; never shown again either way (the caller marks the device onboarded).

// The "How to Play" chapter list: a full page on the metapage chassis (same as Shop/Profile), opened over
// the main menu. A progress header, then the five chapter cards in ONE column so they read as a path.
// Card states: DONE (muted, gold check, free Replay), UNLOCKED (Play; ONLY the next undone chapter carries
// the gold pulse, so the eye lands on exactly one thing), LOCKED (dimmed but the promise stays readable,
// padlock, deny pulse on click, never navigates). Resolves with the picked chapter id, or null on Back.
// --- Conquest Trail: the campaign level list. Resolves with a level index to play, or null on back.
// Sequential unlock is DERIVED from the stored stars, never stored separately (same rule as chapters).
function showCampaignList(root: HTMLElement): Promise<number | null> {
  return new Promise((resolve) => {
    if (document.querySelector('.metapage--camp')) return resolve(null); // single instance
    const stars = loadProfile().campaignStars;
    let closePage: () => void = () => {};
    const finish = (idx: number | null): void => {
      closePage();
      resolve(idx);
    };
    const { page, content, close } = metaPage(root, '⚔ Conquest Trail', () => finish(null));
    closePage = close;
    page.classList.add('metapage--camp');

    const head = el('div', 'meta-section howto-head');
    head.append(el('div', 'howto-head__count', `${beatenCount(stars)}/${CAMPAIGN.length} battles won  ·  ⭐ ${totalStars(stars)}/${CAMPAIGN.length * 3}`));
    const bar = el('div', 'howto-bar');
    for (let i = 0; i < CAMPAIGN.length; i++) bar.append(el('span', `howto-bar__seg${(stars[i] ?? 0) > 0 ? ' is-fill' : ''}`));
    head.append(bar, el('div', 'howto-hint', `Win fast for more stars. Every new star pays ${ELMAS_PER_STAR} 💎`));
    content.append(head);

    const list = el('div', 'howto-list');
    let pulsed = false;
    let lastAct: number = 0;
    CAMPAIGN.forEach((lv, i) => {
      if (lv.act !== lastAct) {
        // act divider: the story arc's chapter heading (mainland war, then the isles)
        lastAct = lv.act;
        list.append(el('div', 'camp-act', ACT_NAMES[lv.act]));
      }
      const got = stars[i] ?? 0;
      const unlocked = isUnlocked(i, stars);
      const isNext = got === 0 && unlocked && !pulsed;
      if (isNext) pulsed = true;
      const card = el('div', `chapter-card${got > 0 ? ' is-done' : unlocked ? '' : ' is-locked'}${isNext ? ' is-next' : ''}`);
      const body = el('div', 'chapter-card__body');
      const title = el('div', 'chapter-card__title');
      title.append(el('span', 'chapter-card__num', `Level ${i + 1}`), document.createTextNode(lv.name));
      const size = lv.islandSize === 7 ? 'small' : lv.islandSize === 9 ? 'medium' : 'large';
      const ground = lv.mapKind === 'land' ? 'mainland' : 'isles';
      body.append(
        title,
        el('div', 'chapter-card__promise', lv.brief),
        el('div', 'chapter-card__meta', `${lv.factions} realms · ${size} ${ground} · ${lv.difficulty === 'hard' ? 'Hard AI' : 'Normal AI'} · ⭐⭐⭐ within ${lv.gold} rounds`),
      );
      const side = el('div', 'chapter-card__side');
      if (unlocked) {
        const rating = el('div', 'camp-stars');
        for (let s = 0; s < 3; s++) rating.append(el('span', `camp-star${s < got ? ' is-on' : ''}`, '★'));
        const btn = el('button', `chapter-card__btn${got === 0 ? ' chapter-card__btn--play' : ''}`, got === 0 ? 'Play' : 'Replay');
        btn.addEventListener('click', () => finish(i));
        side.append(rating, btn);
      } else {
        side.append(el('div', 'chapter-card__lock', '🔒'));
        card.title = `Beat Level ${i} first`;
        card.addEventListener('click', () => {
          card.classList.remove('is-deny');
          void card.offsetWidth;
          card.classList.add('is-deny');
        });
      }
      card.append(el('div', 'chapter-card__icon', got > 0 ? '🏴' : unlocked ? '⚔' : '🔒'), body, side);
      list.append(card);
    });
    content.append(list);
  });
}


// --- main menu: the title screen shown on first load, over an animated archipelago.
// Returns the player's choice: resume ('continue'), start fresh ('new'), go online, or play a tutorial
// chapter picked on the How to Play list. `openChapters` lands the menu with that list already open
// (the post-chapter reload path). ---
// The Handbook: the standing one-page REFERENCE (Conquest Trail 2.0 keeps teaching inside the
// campaign; this is where a returning player looks something up without replaying a level).
function showHandbook(root: HTMLElement): void {
  if (document.querySelector('.metapage--handbook')) return;
  const { content, page } = metaPage(root, '📖 Handbook');
  page.classList.add('metapage--handbook');
  const sec = (title: string, html: string): void => {
    const box = el('div', 'meta-section');
    box.appendChild(el('div', 'profile-sec', title));
    const body = el('div', 'handbook__body');
    body.innerHTML = html;
    box.appendChild(body);
    content.appendChild(box);
  };
  sec('⚔️ The wheel of war', 'Every unit is strong somewhere: <b>Riders</b> run down Warriors and Archers · <b>Archers</b> outshoot Warriors and Spearmen · <b>Warriors</b> break Spearmen · <b>Spearmen</b> turn Riders. Hover any enemy with a unit selected: the <b>forecast</b> shows both sides of the fight before you commit. Archers loose one free arrow a round without spending their move; Riders move twice a turn.');
  sec('🏗️ Buildings', '<b>Market</b>: raises a region\'s output every round. · <b>Barracks</b>: a muster ground (troops train here or at your capital), +1 army cap, shields its defender by 1; can hold one recruit in reserve while the tile is occupied. · <b>Harbor</b>: units sail from THIS tile; its trade pays +1 Gold. Buildings are paid fully up front; cancelling refunds everything.');
  sec('🏰 Capitals & sieges', 'A capital pays <b>+2 of each home good</b> and its defender takes 1 less; sacking one plunders <b>35% of the owner\'s whole treasury</b>. Castle <b>walls and cliffs are impassable</b>: gates are the only way in for blades, and <b>arrows fly over walls</b>. In custom and online battles every extra crown you hold fields +1 army; a realm holding none fields 2 fewer.');
  sec('⛵ The sea', 'A crossing starts on a <b>Harbor tile</b>: click a far shore, the soldier boards and <b>lands at the start of your next turn</b>. Boats never cross land, and a blocked landing holds station until you re-aim it. Landings only touch the coast: the interior is taken on foot.');
  sec('🕊️ The council', 'War takes a round to mobilise after the declaration; peace and alliances hold for a few rounds once made (no instant take-backs). Trades settle instantly, and a runaway leader will find the whole map banding against them, you included.');
  sec('🏆 Winning', 'Total conquest, the level\'s stated goal, or the round limit (largest realm takes it). Conquest percentage counts only land that can actually be held. Campaign stars reward speed: the faster the win, the more stars.');
}

type MenuChoice = 'new' | 'quick' | 'continue' | 'online' | { kind: 'campaign'; level: number };
function showMainMenu(root: HTMLElement, hasSave: boolean): Promise<MenuChoice> {
  return new Promise((resolve) => {
    const overlay = el('div', 'mainmenu');
    const sky = el('div', 'mainmenu__sky'); // slowly drifting archipelago backdrop (Ken Burns)
    const scrim = el('div', 'mainmenu__scrim'); // dark vignette so the title reads
    const embers = el('div', 'mainmenu__embers'); // rising gold motes for a little life
    for (let i = 0; i < 14; i++) {
      const e = el('span', 'mainmenu__ember');
      e.style.left = `${(i * 37 + 11) % 100}%`; // deterministic spread (no RNG needed here)
      e.style.animationDelay = `${((i * 0.9) % 8).toFixed(2)}s`;
      e.style.animationDuration = `${9 + (i % 5) * 2}s`;
      embers.appendChild(e);
    }
    const card = el('div', 'mainmenu__card');
    const logo = document.createElement('img');
    logo.className = 'mainmenu__logo';
    logo.src = 'art/logo.png';
    logo.alt = 'Warring Isles';
    card.append(logo, el('div', 'mainmenu__tag', 'Rival races. One archipelago. Forge alliances, raise armies, and conquer the isles.'));
    const leave = (choice: MenuChoice): void => {
      overlay.classList.add('is-leaving');
      setTimeout(() => {
        overlay.remove();
        resolve(choice);
      }, 420);
    };
    // menu button with the icon in its own span, so there's a clean gap between glyph and label
    const menuBtn = (cls: string, icon: string, label: string): HTMLButtonElement => {
      const b = el('button', cls);
      b.append(el('span', 'mm-ico', icon), document.createTextNode(label));
      return b;
    };
    // Buttons live in their own wrap: the primary action (Continue/Play, `.mainmenu__play`) spans the full width,
    // every secondary flows two-per-row and a lone last one grows to fill, so the layout stays tidy as buttons are
    // added (Shop, Multiplayer, ...). When a save exists Continue is primary and New Game is secondary; else Play.
    const buttons = el('div', 'mainmenu__buttons');
    if (hasSave) {
      const cont = menuBtn('mainmenu__play', '⮡', 'Continue');
      cont.addEventListener('click', () => leave('continue'));
      buttons.appendChild(cont);
    }
    // PLAY IS THE CAMPAIGN. A newcomer arriving from a portal should never have to work out what
    // "Quick Battle" means or which of three buttons is the game: the big one is the story, and it is
    // labelled with where they left off, because "Level 4/15" is the reason to come back tomorrow that
    // a bag of anonymous skirmishes never gives anyone. The free-play modes keep their own names below.
    const campStars = loadProfile().campaignStars;
    const campIdx = currentLevel(campStars);
    const started = beatenCount(campStars) > 0;
    const play = menuBtn(hasSave ? 'mainmenu__settings' : 'mainmenu__play', '▶', started ? `Play · Level ${campIdx + 1}/${CAMPAIGN.length}` : 'Play');
    play.addEventListener('click', () => {
      // CRAZYGAMES ONLY, first-timers only: Play boots the trail's single unlocked level directly,
      // skipping the list of 14 padlocks (and the campaign branch skips the tutorial-offer dialog),
      // so the portal player is in the story within one click. Off the portal, and for anyone with
      // trail progress, the classic flow stays: the list, then the one-time tutorial offer.
      if (!started && onCrazyGames()) {
        leave({ kind: 'campaign', level: campIdx });
        return;
      }
      void showCampaignList(root).then((level) => {
        if (level != null) leave({ kind: 'campaign', level });
      });
    });
    buttons.appendChild(play);
    // Free play, named for what it actually is. "Custom" sets the board up (race, realms, size, map kind);
    // "Instant" skips every setup screen (random race, default match) for players who just want in fast.
    const custom = menuBtn('mainmenu__settings', '✦', 'Custom Battle');
    custom.addEventListener('click', () => leave('new'));
    buttons.appendChild(custom);
    const quick = menuBtn('mainmenu__settings', '⚔', 'Instant Battle');
    quick.addEventListener('click', () => leave('quick'));
    buttons.appendChild(quick);
    const online = menuBtn('mainmenu__settings', '🌐', 'Multiplayer (beta)');
    online.addEventListener('click', () => leave('online')); // multiplayer lobby (create/join a room)
    buttons.appendChild(online);
    // the trail teaches in-level now (Conquest Trail 2.0); the Handbook is the standing REFERENCE:
    // the counter wheel, buildings, sieges, sea law and the council clocks, one page, no lessons
    const handbook = menuBtn('mainmenu__settings', '📖', 'Handbook');
    handbook.addEventListener('click', () => showHandbook(root));
    buttons.appendChild(handbook);
    const shopBtn = menuBtn('mainmenu__settings', '🛒', 'Shop');
    shopBtn.addEventListener('click', () => showShop(root));
    buttons.appendChild(shopBtn);
    const settingsBtn = menuBtn('mainmenu__settings', '⚙', 'Settings');
    settingsBtn.addEventListener('click', () => showSettingsPopup(root));
    buttons.appendChild(settingsBtn);
    card.appendChild(buttons);
    overlay.append(sky, scrim, embers, card);
    // menu player chip (framed avatar + name + elmas), top-right of the menu. A meta page opened over the menu
    // covers it and shows its OWN header chip instead, so the chip never floats detached in the far corner.
    // Lives inside the overlay, so it is removed with the menu on exit; updates live via refreshPlayerChips.
    const chip = buildPlayerChip(root);
    chip.classList.add('player-chip--menu');
    overlay.appendChild(chip);
    root.appendChild(overlay);
  });
}

// --- online multiplayer lobby (Phase 1): create/join a PartyKit room, ready up, host starts. The host
// builds the match ONCE and broadcasts the full snapshot; every client (host included) hydrates that
// exact state, so the lockstep starts from a verified identical board. Races are auto-dealt in v1. ---
/** Replay ordered room events (cmd / forceEnd / takeover / reclaim) onto a hydrated state - the rejoin
 *  catch-up. Returns how many endTurns were applied, so the caller can advance its per-turn hash counter. */
function applyNetEvents(g: GameState, events: ServerMsg[]): number {
  let ends = 0;
  for (const ev of events) {
    if (ev.t === 'cmd') {
      const ok = applyCommand(g, ev.seat, ev.cmd).ok;
      if (ok && ev.cmd.kind === 'endTurn') ends++;
    } else if (ev.t === 'forceEnd') {
      if (applyCommand(g, ev.seat, { kind: 'endTurn' }).ok) ends++;
    } else if (ev.t === 'takeover') {
      aiTakeover(g, ev.seat);
    } else if (ev.t === 'reclaim') {
      reclaimSeat(g, ev.seat);
    }
  }
  return ends;
}

interface OnlineStart {
  game: GameState;
  seat: FactionId;
  room: NetRoom;
  seatNames: Record<number, string>; // faction seat → player display name (for banners/standings)
  names: Record<string, string>; // player token → display name (names the 'peer' connect/disconnect feed lines)
  matchId: string; // server-minted per-match nonce (codes are reusable; keys the once-per-match reward guard)
  isHost: boolean; // the host also pushes reconnect snapshots after its turns
  timerSec: number; // per-move timer chosen in the lobby (15/30/45)
  initialTurnSeq: number; // 0 on a fresh start; a rejoin resumes the per-turn hash counter mid-match
  callbacks: RoomCallbacks; // mutable holder: the match loop re-points onMessage/onClose to itself
  // the room's authoritative turn clock (see protocol 'clock'): absolute deadline + our measured skew
  // against the room's wall clock. Every client renders THIS, so skipping a replay or sitting in a
  // backgrounded tab can never make the countdown differ between players. Null until the first tick.
  clock: { deadlineAt: number; skewMs: number } | null;
}

function showOnlineLobby(root: HTMLElement, auto?: { join?: string; create?: boolean }): Promise<OnlineStart | null> {
  return new Promise((resolve) => {
    // a launch via CrazyGames Instant Multiplayer / an invite link goes straight into a room, so the
    // entry doors are replaced by a "connecting" line until the auto create/join lands (or fails).
    let autoActive = !!(auto?.create || auto?.join);
    // A full page on the same menu-sky chassis as Shop/Profile, NOT a modal: the lobby is a destination.
    // The chassis mounts ONCE and render() rebuilds only the content column, so the sky drift and the
    // player chip's animated cosmetic frame never restart on lobby broadcasts (joins, readies, picks).
    const { page, content, close } = metaPage(root, 'Online Multiplayer', () => done(null));
    page.classList.add('lobbypage');
    const titleEl = page.querySelector<HTMLElement>('.metapage__title')!;
    const backBtn = page.querySelector<HTMLElement>('.metapage__back')!;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') done(null); // leave the screen (and the room) like any full-page scene
    };
    window.addEventListener('keydown', onKey);
    let room: NetRoom | null = null;
    let lobby: LobbyState | null = null;
    let status = ''; // one reserved line under the content, so errors never shift the layout
    let starting = false;
    let lastCode = ''; // the code the current/last connect targeted (for the stale-Rejoin-banner check)

    let lastCosmetics = ''; // dedupe: refreshPlayerChips fires on every elmas change too
    cosmeticsSync = () => {
      if (!room) return;
      const eq = loadProfile().equipped;
      const key = `${eq.avatar}|${eq.frame}`;
      if (key === lastCosmetics) return;
      lastCosmetics = key;
      room.send({ t: 'cosmetics', avatar: eq.avatar, frame: eq.frame });
    };
    let screenClosed = false; // a late-resolving connect must not leak its socket into a dead closure
    const done = (r: OnlineStart | null): void => {
      screenClosed = true;
      cosmeticsSync = null; // in-match profile is unreachable, so the hook lives only with this screen
      window.removeEventListener('keydown', onKey);
      close(); // animated page-leave
      platform?.hideInvite(); // the CrazyGames invite button lives only with this lobby screen
      if (!r) {
        room?.close();
        platform?.leftRoom(); // fully left multiplayer (a match start keeps us IN the room for the game)
      }
      resolve(r);
    };

    const handleMsg = (msg: ServerMsg): void => {
      if (msg.t === 'welcome' || msg.t === 'lobby') {
        starting = false; // any roster change re-opens the Start gate (a silent latch dead-ends the host)
        // stale banner fix: we remembered a live match for this code, but the room answered as a fresh
        // NOT-started lobby - that match is gone, so the pulsing Rejoin banner must die with it
        if (msg.t === 'welcome' && !msg.lobby.started && sessionStorage.getItem('wi.activeRoom')?.toLowerCase() === lastCode.toLowerCase()) {
          sessionStorage.removeItem('wi.activeRoom');
        }
        lobby = msg.lobby;
        render();
      } else if (msg.t === 'start') {
        const g = restoreSnapshot(msg.snapshot);
        if (hashState(g) !== msg.hash) {
          status = 'Sync error: snapshot hash mismatch (mixed builds?)';
          render();
          return;
        }
        const seat = msg.seats[room!.playerId];
        if (seat === undefined) {
          status = 'No seat assigned';
          render();
          return;
        }
        const seatNames: Record<number, string> = {};
        const names: Record<string, string> = {};
        for (const m of lobby?.members ?? []) {
          names[m.id] = m.name;
          const s = msg.seats[m.id];
          if (s !== undefined) seatNames[s] = m.name;
        }
        platform?.updateRoom(room!.roomCode, false); // match started: not joinable (reconnect only)
        done({ game: g, seat, room: room!, seatNames, names, matchId: msg.match ?? '', isHost: !!lobby?.members.find((m) => m.id === room!.playerId)?.host, timerSec: lobby?.settings.turnTimerSec ?? 60, initialTurnSeq: 0, callbacks, clock: null });
      } else if (msg.t === 'rejoin') {
        // the room recognised our token on a STARTED match: hydrate its snapshot, replay what we missed
        const g = restoreSnapshot(msg.snapshot);
        const ends = applyNetEvents(g, msg.cmds);
        const seat = msg.seats[room!.playerId];
        if (seat === undefined) {
          status = 'No seat to reclaim';
          render();
          return;
        }
        // seat labels come from the rejoin payload's full token→name map (ALL members, even ones still
        // disconnected); the welcome lobby only lists connected members, so it is just the fallback
        const seatNames: Record<number, string> = {};
        const names: Record<string, string> = { ...msg.names };
        for (const m of lobby?.members ?? []) names[m.id] ??= m.name;
        for (const [tok, s2] of Object.entries(msg.seats)) {
          const nm = names[tok];
          if (nm !== undefined) seatNames[s2] = nm;
        }
        platform?.updateRoom(room!.roomCode, false); // rejoining a started match: not joinable
        done({ game: g, seat, room: room!, seatNames, names, matchId: msg.match ?? '', isHost: !!lobby?.members.find((m) => m.id === room!.playerId)?.host, timerSec: lobby?.settings.turnTimerSec ?? 60, initialTurnSeq: msg.turnSeq + ends, callbacks, clock: null });
      } else if (msg.t === 'error') {
        starting = false; // e.g. notReady: someone joined un-ready in the start window; the button must revive
        status = msg.msg;
        render();
      }
    };
    const onClose = (code?: number): void => {
      if (room) {
        room = null;
        lobby = null;
        // distinct close codes tell the player WHAT happened, not just that the socket died
        status = code === CLOSE_KICKED ? 'The host removed you from the room.' : code === CLOSE_REPLACED ? 'This room was opened in another tab.' : 'Connection lost.';
        render();
      }
    };

    const callbacks: RoomCallbacks = { onMessage: handleMsg, onClose }; // handed to the match loop at start
    // `create` marks a freshly-minted code: the room refuses an occupied one ('exists'), and we silently
    // roll a new code up to 3 times, so a collision never lands the creator in a stranger's lobby
    const connect = (code: string, create = false, retries = 3): void => {
      lastCode = code;
      status = 'Connecting…';
      render();
      joinRoom(code, displayName(), callbacks, loadProfile().equipped, undefined, create)
        .then((r) => {
          if (screenClosed) {
            // the player backed out while 'Connecting…' was up: close the socket that just resolved,
            // or they would sit as a silent ghost member in the target room until this tab reloads
            r.close();
            return;
          }
          room = r;
          status = '';
          autoActive = false; // connected: the war room renders now, not the "connecting" line
          // CrazyGames "play with friends": expose the room to the platform's invite button + join UI +
          // the Multiplayer landing page (our room code rides in the invite param)
          platform?.showInvite(r.roomCode);
          platform?.updateRoom(r.roomCode, true);
          render();
        })
        .catch((e: Error & { code?: string }) => {
          room = null;
          if (create && e.code === 'exists' && retries > 0) return connect(makeRoomCode(), true, retries - 1);
          autoActive = false; // auto create/join failed: fall back to the manual entry doors
          if (sessionStorage.getItem('wi.activeRoom') === code) sessionStorage.removeItem('wi.activeRoom'); // that match is gone
          status = e.message;
          render();
        });
    };

    const hostStart = (): void => {
      if (!room || !lobby || starting) return;
      starting = true;
      const members = lobby.members;
      const fc = Math.min(5, Math.max(lobby.settings.factionCount, members.length));
      // a room created by an older worker build (or restored from storage) can still carry a legacy
      // size (7/9/11): normalize BEFORE both the pick and the match, so name, label and world agree
      const size = normalizeIslandSize(lobby.settings.islandSize);
      const hostBoard = pickMapSeed(lobby.settings.mapKind ?? 'isles', size, Math.random()); // host rolls WHICH curated board; the seed itself stays deterministic for every client
      const seed = hostBoard.seed;
      const slots = members.map((m) => ({ human: true, race: m.race ?? undefined })); // lobby picks; unpicked dealt from the seed
      const g = createMatch({ seed, factionCount: fc, islandSize: size, dominationPct: lobby.settings.dominationPct, difficulty: lobby.settings.difficulty, mode: 'mp', mapKind: lobby.settings.mapKind ?? 'isles', cliffs: true, heart: hostBoard.heart ?? false, capitals: true, slots });
      // drain the AI opening (the AIs seated before the first human) BEFORE snapshotting, so the
      // broadcast state is post-opening. Clients must NOT re-run the opening locally (a rejoiner would
      // otherwise re-run it on a mid-game state); online boots skip the opening block entirely.
      for (const _ of runTurnGen(g, true)) void _;
      const seats: Record<string, number> = {};
      members.forEach((m, i) => (seats[m.id] = i));
      room.send({ t: 'start', snapshot: snapshotState(g), seats, hash: hashState(g) });
    };

    const render = (): void => {
      // preserve the join input across re-renders (a status change mid-typing must not eat the code)
      const oldInput = content.querySelector<HTMLInputElement>('.lobby-input');
      const keep = oldInput ? { value: oldInput.value, focus: document.activeElement === oldInput } : null;
      content.textContent = '';
      backBtn.replaceChildren(el('span', 'mm-ico', '\u25c0'), document.createTextNode(room ? 'Leave Room' : 'Main Menu'));
      titleEl.replaceChildren(document.createTextNode(room ? 'War Room' : 'Online Multiplayer'));
      if (!room) titleEl.append(el('span', 'lobby-beta', 'BETA'));

      if (!room && autoActive) {
        // an Instant-Multiplayer / invite launch is mid-connect: skip the manual doors, just a status line
        content.append(el('div', 'lobby-connecting', '⚓ Connecting to your game…'));
      } else if (!room) {
        // ENTRY: two doors of equal weight (host / join). A refreshed player has exactly one job, so a
        // pulsing rejoin banner outranks both doors when a live match is remembered for this tab.
        const lastRoom = sessionStorage.getItem('wi.activeRoom');
        if (lastRoom) {
          const strip = el('div', 'lobby-rejoin');
          const info = el('div', 'lobby-rejoin__info');
          info.append(el('span', undefined, '\u2693 Match in progress'), el('span', 'lobby-rejoin__code', lastRoom.toUpperCase()));
          const btn = el('button', 'intro-start', '\u21bb Rejoin');
          btn.addEventListener('click', () => connect(lastRoom));
          strip.append(info, btn);
          content.append(strip);
        }
        const paths = el('div', 'lobby-paths');
        const host = el('div', 'lobby-path');
        host.append(
          el('div', 'lobby-path__crest', '\ud83c\udff0'),
          el('div', 'lobby-path__title', 'Host a Game'),
          el('p', 'lobby-path__desc', 'Open a new war room and invite up to 4 friends. Empty seats are filled by AI.'),
        );
        const createBtn = el('button', 'intro-start lobby-cta__btn', 'Create Room');
        createBtn.addEventListener('click', () => connect(makeRoomCode(), true));
        host.append(createBtn);
        const join = el('div', 'lobby-path');
        join.append(
          el('div', 'lobby-path__crest', '\u2693'),
          el('div', 'lobby-path__title', 'Join a Friend'),
          el('p', 'lobby-path__desc', 'Enter the room code your friend shared.'),
        );
        const input = document.createElement('input');
        input.className = 'lobby-input';
        input.placeholder = 'room code';
        input.maxLength = 12;
        if (keep) input.value = keep.value;
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' && input.value.trim()) connect(input.value.trim());
        });
        const joinBtn = el('button', 'intro-start lobby-cta__btn', 'Join');
        joinBtn.addEventListener('click', () => {
          if (input.value.trim()) connect(input.value.trim());
        });
        join.append(input, joinBtn);
        paths.append(host, el('div', 'lobby-or', 'or'), join);
        content.append(paths);
      } else if (lobby) {
        const meMember = lobby.members.find((m) => m.id === room!.playerId);
        const isHost = !!meMember?.host;
        // the code is the page headline: sharing it IS the host's job while waiting. Compact plate:
        // the "how to share" hint lives in the tooltip so the page stays inside one viewport height.
        const codeRow = el('button', 'lobby-code lobby-code--hero');
        codeRow.append(el('span', 'lobby-code__label', 'ROOM CODE'), el('span', 'lobby-code__value', room.roomCode.toUpperCase()), el('span', 'lobby-code__copy', '\u29c9 copy'));
        codeRow.title = 'Click to copy. Share it with your friends.';
        codeRow.addEventListener('click', () => {
          void copyText(room!.roomCode).then((ok) => {
            const c = codeRow.querySelector('.lobby-code__copy');
            if (c) c.textContent = ok ? '\u2713 copied' : 'press Ctrl+C';
            if (!ok) {
              // both clipboard paths refused (a locked-down embed): select the visible code so a
              // manual Ctrl+C works, and the label above says exactly that instead of lying
              const val = codeRow.querySelector('.lobby-code__value');
              const sel = window.getSelection();
              if (val && sel) {
                const range = document.createRange();
                range.selectNodeContents(val);
                sel.removeAllRanges();
                sel.addRange(range);
              }
            }
          });
        });
        content.append(codeRow);
        // the table: one seat card per SEAT - joined players (avatar + frame + ready), open seats honest
        const seatCount = Math.min(5, Math.max(lobby.settings.factionCount, lobby.members.length));
        content.append(el('div', 'lobby-count', `COMMANDERS \u00b7 ${lobby.members.length} OF ${seatCount}`));
        const seatsGrid = el('div', 'lobby-seats');
        for (let i = 0; i < seatCount; i++) {
          const m = lobby.members[i];
          const seat = el('div', `lobby-seat${m ? '' : ' lobby-seat--ai'}${m?.id === room.playerId ? ' is-you' : ''}`);
          if (m) {
            const av = framedAvatar(m.avatar ?? 'av_orc', m.frame ?? 'fr_default', 64);
            av.classList.add('lobby-seat__avatar');
            seat.append(av);
            seat.append(el('div', 'lobby-seat__name', `${m.host ? '\ud83d\udc51 ' : ''}${m.name}${m.id === room.playerId ? ' (you)' : ''}`));
            const rdef = m.race ? RACES.find((r) => r.key === m.race) : null;
            const raceChip = el('span', 'lobby-seat__pick');
            if (rdef) {
              const rh = document.createElement('img');
              rh.src = `sprites/head_${rdef.key}.png`;
              rh.alt = '';
              rh.className = 'lobby-seat__pick-img';
              raceChip.append(rh, el('span', undefined, rdef.name));
            } else raceChip.append(el('span', undefined, '\ud83c\udfb2 Random'));
            seat.append(raceChip);
            seat.append(el('span', `lobby-seat__badge${m.ready || m.host ? ' is-on' : ''}`, m.host ? 'HOST' : m.ready ? '\u2713 READY' : 'WAITING'));
            if (isHost && m.id !== room.playerId) {
              const kick = el('button', 'lobby-kick', '\u2715');
              kick.title = 'Kick this player';
              kick.addEventListener('click', () => room!.send({ t: 'kick', playerId: m.id }));
              seat.append(kick);
            }
          } else {
            // an honest open seat (the old "AI READY" copy made hosts think the room was ready)
            seat.append(el('div', 'lobby-seat__ai-face', '\ud83e\udd16'));
            seat.append(el('div', 'lobby-seat__name', 'Open seat'));
            seat.append(el('div', 'lobby-seat__open-sub', 'AI joins if empty at start'));
          }
          seatsGrid.append(seat);
        }
        content.append(seatsGrid);
        // lower deck: your race pick + the host's match rules, as two wood panels
        const deck = el('div', 'lobby-deck');
        const raceSec = el('div', 'meta-section');
        raceSec.append(el('div', 'meta-section__title', 'Your Race'));
        const races = el('div', 'lobby-races');
        const rnd = el('button', `lobby-race${meMember?.race == null ? ' is-on' : ''}`, '\ud83c\udfb2') as HTMLButtonElement;
        rnd.title = 'Random';
        rnd.addEventListener('click', () => room!.send({ t: 'pickRace', race: null }));
        races.append(rnd);
        for (const rc of RACES) {
          const takenBy = lobby.members.find((m) => m.race === rc.key);
          const taken = !!takenBy && takenBy.id !== room.playerId;
          const b = el('button', `lobby-race${meMember?.race === rc.key ? ' is-on' : ''}`) as HTMLButtonElement;
          const rimg = document.createElement('img');
          rimg.src = `sprites/head_${rc.key}.png`;
          rimg.alt = rc.name;
          rimg.className = 'lobby-race__img';
          b.append(rimg);
          b.title = taken ? `${rc.name} (taken by ${takenBy.name})` : rc.name;
          b.disabled = taken;
          b.addEventListener('click', () => room!.send({ t: 'pickRace', race: rc.key }));
          races.append(b);
        }
        raceSec.append(races);
        const rulesSec = el('div', 'meta-section');
        rulesSec.append(el('div', 'meta-section__title', 'Match Rules'));
        const s = lobby.settings;
        const segs = el('div', 'race-setup');
        const seg = (label: string, opts: string[], cur: number, pick: (i: number) => void): HTMLElement => {
          const wrap = el('div', 'setup-group');
          wrap.append(el('span', 'setup-label', label));
          const rowB = el('div', 'setup-opts');
          opts.forEach((o, i) => {
            const b = el('button', `setup-opt${i === cur ? ' is-on' : ''}`, o);
            b.disabled = !isHost;
            b.addEventListener('click', () => pick(i));
            rowB.appendChild(b);
          });
          wrap.appendChild(rowB);
          return wrap;
        };
        const set = (patch: Partial<MatchSettings>): void => room!.send({ t: 'settings', settings: patch });
        segs.append(
          seg('Seats', ['3', '4', '5'], s.factionCount - 3, (i) => set({ factionCount: i + 3 })),
          seg('World', ['🏝 Isles', '⛰ Mainland'], s.mapKind === 'land' ? 1 : 0, (i) => set({ mapKind: i === 1 ? 'land' : 'isles' })),
          seg('Map', ['Small', 'Medium', 'Large'], [8, 10, 12].indexOf(normalizeIslandSize(s.islandSize)), (i) => set({ islandSize: [8, 10, 12][i]! })),
          seg('AI', ['Normal', 'Hard'], s.difficulty === 'hard' ? 1 : 0, (i) => set({ difficulty: i === 1 ? 'hard' : 'normal' })),
          seg('Timer', ['60s', '90s', '120s'], Math.max(0, [60, 90, 120].indexOf(s.turnTimerSec)), (i) => set({ turnTimerSec: [60, 90, 120][i] as 60 | 90 | 120 })),
        );
        rulesSec.append(segs);
        if (!isHost) rulesSec.append(el('p', 'lobby-note', 'Only the host can change these.'));
        deck.append(raceSec, rulesSec);
        content.append(deck);
        // footer: the wait-state sentence + the ONE gold action, so "why are we not starting" is always
        // answered one line above the button
        const cta = el('div', 'lobby-cta');
        const notReady = lobby.members.filter((m) => !m.ready && !m.host);
        const allReady = lobby.members.every((m) => m.ready || m.host);
        const waitText = isHost
          ? lobby.members.length === 1
            ? 'Waiting for friends to join. AI fills empty seats.'
            : allReady
              ? 'All commanders ready'
              : `Waiting for ${notReady[0]!.name} to ready up`
          : meMember?.ready
            ? 'Waiting for the host to start'
            : 'Ready up when you are set';
        cta.append(el('div', `lobby-wait${isHost && lobby.members.length > 1 && allReady ? ' is-go' : ''}`, waitText));
        if (!isHost) {
          const ready = el('button', 'intro-start lobby-cta__btn', meMember?.ready ? '\u2713 Ready' : 'Ready up');
          ready.addEventListener('click', () => room!.send({ t: 'ready', ready: !meMember?.ready }));
          cta.append(ready);
        } else {
          const start = el('button', 'intro-start lobby-cta__btn', 'Start Match \u25b6') as HTMLButtonElement;
          start.disabled = !allReady;
          start.title = allReady ? '' : 'Waiting for everyone to Ready up';
          start.addEventListener('click', hostStart);
          cta.append(start);
        }
        content.append(cta);
      }

      content.append(el('div', 'lobby-status', status)); // always present: reserved height, no layout shift
      if (keep?.focus) content.querySelector<HTMLInputElement>('.lobby-input')?.focus();
    };

    // a launch via CrazyGames Instant Multiplayer / an invite link creates or joins immediately
    if (auto?.create) connect(makeRoomCode(), true);
    else if (auto?.join) connect(auto.join);
    render();
  });
}

// --- a small centred modal popup (dark scrim + wood card); click the backdrop or a button to close ---
function popup(root: HTMLElement, title: string): { card: HTMLElement; overlay: HTMLElement; close: () => void } {
  const overlay = el('div', 'popup-overlay');
  const card = el('div', 'popup-card');
  const close = (): void => overlay.remove();
  const closeX = el('button', 'panel__close', '✕');
  closeX.title = 'Close';
  closeX.addEventListener('click', close);
  card.append(closeX, el('h2', 'popup-title', title));
  overlay.appendChild(card);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  root.appendChild(overlay);
  return { card, overlay, close };
}

// --- achievements ---
// A badge: the InvokeAI art at art/achievements/<key>.png, or the emoji fallback if that image is missing.
function achBadge(familyKey: string, emoji: string): HTMLElement {
  const wrap = el('div', 'ach-badge');
  const img = document.createElement('img');
  img.src = `art/achievements/${familyKey}.png`;
  img.alt = '';
  img.addEventListener('error', () => {
    img.remove();
    wrap.textContent = emoji;
    wrap.classList.add('ach-badge--emoji');
  });
  wrap.appendChild(img);
  return wrap;
}
// One achievement row: badge + name + desc; locked rows (value !== null) also get a progress bar.
// Celebrate freshly unlocked achievements with a stack of bottom-right toasts (badge art + name), each popping
// in with the unlock chime and auto-dismissing after a few seconds. Staggered so several cascade in one after
// another (they pile bottom-right). Reusable, fired from the game-over screen now.
function showAchievementToasts(root: HTMLElement, achs: Achievement[]): void {
  if (!achs.length) return;
  let wrap = root.querySelector<HTMLElement>('.ach-toasts');
  if (!wrap) {
    wrap = el('div', 'ach-toasts');
    root.appendChild(wrap);
  }
  const host = wrap;
  achs.forEach((a, i) => {
    setTimeout(() => {
      const card = el('div', 'ach-toast');
      const text = el('div', 'ach-toast__text');
      text.append(el('div', 'ach-toast__label', 'Achievement unlocked'), el('div', 'ach-toast__name', a.name));
      card.append(achBadge(a.familyKey, a.emoji), text);
      host.appendChild(card);
      requestAnimationFrame(() => card.classList.add('is-in'));
      audio.sfx('achieve');
      setTimeout(() => {
        card.classList.add('is-out');
        setTimeout(() => card.remove(), 440);
      }, 4600);
    }, i * 850); // cascade the pops one after another
  });
}

function achCard(a: Achievement, value: number | null): HTMLElement {
  const row = el('div', value == null ? 'ach-row ach-row--won' : 'ach-row');
  const text = el('div', 'ach-row__text');
  text.append(el('div', 'ach-row__name', a.name), el('div', 'ach-row__desc', a.desc));
  if (value != null) {
    const bar = el('div', 'ach-bar');
    const fill = el('div', 'ach-bar__fill');
    fill.style.width = `${Math.min(100, Math.round((value / a.threshold) * 100))}%`;
    bar.appendChild(fill);
    text.append(bar, el('div', 'ach-row__prog', `${value} / ${a.threshold}`));
  }
  // elmas reward: gold "reward on unlock" for locked, green "earned" for unlocked achievements
  const reward = el('div', `ach-row__reward${value == null ? ' is-earned' : ''}`, `💎 ${a.reward}`);
  reward.title = value == null ? 'Elmas earned' : 'Elmas reward when unlocked';
  row.append(achBadge(a.familyKey, a.emoji), text, reward);
  return row;
}
// The Achievements page: two tabs (Unlocked / Locked). Locked shows only each family's next tier (progressive).
function showAchievements(root: HTMLElement): void {
  if (document.querySelector('.metapage--ach')) return;
  const profile = loadProfile();
  const won = completed(profile);
  const locked = nextLocked(profile);
  const { content, page } = metaPage(root, '🏅 Achievements');
  page.classList.add('metapage--ach');
  const tabs = el('div', 'ach-tabs');
  const tabWon = el('button', 'ach-tab is-active', `Unlocked · ${won.length}`);
  const tabLocked = el('button', 'ach-tab', `Locked · ${locked.length}`);
  tabs.append(tabWon, tabLocked);
  const body = el('div', 'ach-body');
  const emptyState = (msg: string): HTMLElement => {
    const box = el('div', 'ach-empty');
    box.append(el('div', 'ach-empty__icon', '🏅'), el('div', 'ach-empty__text', msg));
    return box;
  };
  const showWon = (): void => {
    body.textContent = '';
    if (!won.length) {
      body.append(emptyState('No achievements yet. Play a few games to start unlocking them.'));
      return;
    }
    const grid = el('div', 'ach-grid');
    for (const a of won) grid.append(achCard(a, null));
    body.append(grid);
  };
  const showLocked = (): void => {
    body.textContent = '';
    if (!locked.length) {
      body.append(emptyState('Every achievement unlocked. Legendary work.'));
      return;
    }
    const grid = el('div', 'ach-grid');
    for (const { ach, value } of locked) grid.append(achCard(ach, value));
    body.append(grid);
  };
  tabWon.addEventListener('click', () => {
    tabWon.classList.add('is-active');
    tabLocked.classList.remove('is-active');
    showWon();
  });
  tabLocked.addEventListener('click', () => {
    tabLocked.classList.add('is-active');
    tabWon.classList.remove('is-active');
    showLocked();
  });
  showWon();
  const sec = el('div', 'meta-section');
  sec.append(body);
  content.append(tabs, sec);
}

// --- meta cosmetics UI ---

/** A full-screen "inner page" built on the SAME archipelago backdrop as the main menu (not a dimming
 *  modal): a drifting sky, a light vignette so the world shows through, a top bar with a Back button +
 *  title (+ optional right slot), and a centered, scrollable content column. Returns the content node and
 *  a close() that pops the page. Stacks cleanly (Profile -> Shop -> Back returns to Profile). */
function metaPage(root: HTMLElement, title: string, onBack?: () => void): { page: HTMLElement; content: HTMLElement; close: () => void } {
  const page = el('div', 'metapage');
  const sky = el('div', 'metapage__sky');
  const scrim = el('div', 'metapage__scrim');
  const bar = el('div', 'metapage__bar');
  const back = el('button', 'metapage__back');
  back.append(el('span', 'mm-ico', '◀'), document.createTextNode('Back'));
  const close = (): void => {
    page.classList.add('is-leaving');
    window.setTimeout(() => page.remove(), 220);
  };
  back.addEventListener('click', () => (onBack ? onBack() : close())); // pages with own teardown intercept Back
  bar.append(back, el('div', 'metapage__title', title), buildPlayerChip(root)); // back (left) | centered title | player chip (content-right)
  const scroll = el('div', 'metapage__scroll');
  const inner = el('div', 'metapage__inner'); // one centered column holds the bar AND the content, so everything aligns
  const content = el('div', 'metapage__content');
  inner.append(bar, content);
  scroll.append(inner);
  page.append(sky, scrim, scroll);
  root.appendChild(page);
  return { page, content, close };
}

/** A circular avatar with its cosmetic frame ring. Reused by the menu chip, Profile, and Shop. */
function framedAvatar(avatarId: string, frameId: string, size: number): HTMLElement {
  const wrap = el('div', 'avatar-frame');
  // Premium 'effect' frames are a real animated Canvas2D element (fire/water/ice/nature) rather than a
  // flat CSS glow. Under reduced-motion we fall back to the static CSS ring so it still reads correctly.
  const fx = motionAllowed() ? frameEffect(frameId) : undefined;
  const fc = frameClass(frameId);
  if (fc && !fx) wrap.classList.add(fc);
  wrap.style.width = `${size}px`;
  wrap.style.height = `${size}px`;
  const img = document.createElement('img');
  img.className = 'avatar-frame__img';
  img.src = avatarSrc(avatarId);
  img.alt = '';
  img.addEventListener('error', () => wrap.classList.add('avatar-frame--noart')); // missing art -> styled placeholder
  wrap.appendChild(img);
  if (fx) attachEffectFrame(wrap, size, fx);
  return wrap;
}

// In-match "Standings" panel (top-left): every realm's framed avatar, tile count, army count, and its
// WORLD-CONTROL % (share of the conquerable tiles, the same domination readout the HUD shows for the player,
// e.g. 33% toward the 60% victory threshold). This is NOT a relative win-chance, so the values do not
// sum to 100% (neutral tiles are excluded). Collapsible (default open, remembered), wood-themed, a pure
// read-out that never touches the engine. Rows are built once and updated/re-sorted in place so animated
// player frames are not recreated on every refresh.
function mountStatsPanel(root: HTMLElement, meOf: () => FactionId, nameOf?: (fid: FactionId) => string | null): { refresh(g: GameState): void } {
  const panel = el('div', 'stats-panel');
  const head = el('div', 'stats-panel__head');
  const toggle = el('button', 'stats-panel__toggle');
  toggle.type = 'button';
  head.append(el('div', 'stats-panel__title', 'Standings'), toggle);
  const body = el('div', 'stats-panel__body');
  panel.append(head, body);
  root.appendChild(panel);

  // collapse state: default open, remembered across matches (UI pref, not the profile)
  const OPEN_KEY = 'wi.statsPanel.open';
  let open = localStorage.getItem(OPEN_KEY) !== '0';
  const applyOpen = (): void => {
    panel.classList.toggle('is-collapsed', !open);
    toggle.textContent = open ? '▾' : '▸';
    toggle.title = open ? 'Collapse' : 'Expand';
  };
  head.addEventListener('click', () => {
    open = !open;
    localStorage.setItem(OPEN_KEY, open ? '1' : '0');
    applyOpen();
  });
  applyOpen();

  interface RowRefs {
    rowEl: HTMLElement;
    tiles: HTMLElement;
    army: HTMLElement;
    win: HTMLElement;
    bar: HTMLElement;
  }
  const rows = new Map<number, RowRefs>();
  let built = false;

  const armyOf = (g: GameState, fid: number): number => g.regions.filter((r) => r.unit?.owner === fid).length;

  const buildRows = (g: GameState): void => {
    const eq = loadProfile().equipped; // the solo human's chosen cosmetic; AIs use their free race default
    for (const f of g.factions) {
      const rowEl = el('div', 'stats-row');
      if (f.id === meOf()) rowEl.classList.add('is-you');
      // hotseat: every seat (humans included) shows race defaults - the humans share one device profile
      const solo = f.isPlayer && g.mode !== 'mp';
      const av = framedAvatar(solo ? eq.avatar : `av_${f.race}`, solo ? eq.frame : 'fr_default', 40);
      av.classList.add('stats-row__av');
      const info = el('div', 'stats-row__info');
      const nameEl = el('div', 'stats-row__name');
      const dot = el('span', 'stats-row__dot');
      dot.style.background = `#${f.color.toString(16).padStart(6, '0')}`;
      // online seats show their player's name; the solo human their nickname; everyone else the race name
      nameEl.append(dot, el('span', 'stats-row__nametext', nameOf?.(f.id) ?? (solo ? displayName() : f.name)));
      const statsEl = el('div', 'stats-row__stats');
      const tiles = el('span', 'stats-chip');
      const army = el('span', 'stats-chip');
      statsEl.append(tiles, army);
      const barWrap = el('div', 'stats-row__bar');
      const bar = el('i');
      barWrap.appendChild(bar);
      info.append(nameEl, statsEl, barWrap);
      const win = el('div', 'stats-row__win');
      rowEl.append(av, info, win);
      body.appendChild(rowEl);
      rows.set(f.id, { rowEl, tiles, army, win, bar });
    }
  };

  const refresh = (g: GameState): void => {
    if (!built) {
      buildRows(g);
      built = true;
    }
    const tileCounts = regionCounts(g);
    const total = conquerableTotal(g) || 1;
    // world-control %: share of the CONQUERABLE tiles (same metric as the HUD's domination readout;
    // cliffs and castle walls can never be owned, so they stay out of the census)
    const share = (fid: number): number => Math.round(((tileCounts[fid] ?? 0) / total) * 100);
    const ordered = g.factions.map((f) => ({ f, t: f.eliminated ? -1 : (tileCounts[f.id] ?? 0) })).sort((a, b) => b.t - a.t);
    for (const { f } of ordered) {
      const refs = rows.get(f.id);
      if (!refs) continue;
      refs.rowEl.classList.toggle('is-out', f.eliminated);
      refs.rowEl.classList.toggle('is-you', f.id === meOf()); // hotseat: the highlight follows the device
      refs.tiles.textContent = `🏝 ${tileCounts[f.id] ?? 0}`;
      refs.army.textContent = `⚔️ ${armyOf(g, f.id)}`;
      const pct = f.eliminated ? 0 : share(f.id);
      refs.win.textContent = f.eliminated ? 'OUT' : `${pct}%`;
      refs.bar.style.width = `${Math.min(100, pct)}%`;
      refs.bar.style.background = `#${f.color.toString(16).padStart(6, '0')}`;
      body.appendChild(refs.rowEl); // re-sort by tiles: appending moves the existing node (keeps its avatar/canvas)
    }
  };

  return { refresh };
}

/** Float a "+N / -N 💎" over an element (gain rises green, loss sinks red): the same spend feedback as the HUD. */
function floatDelta(anchor: HTMLElement, delta: number): void {
  if (!delta) return;
  const r = anchor.getBoundingClientRect();
  const f = el('div', `float-delta ${delta < 0 ? 'float-delta--loss' : 'float-delta--gain'}`, `${delta < 0 ? '' : '+'}${delta} 💎`);
  f.style.left = `${r.left + r.width / 2}px`;
  f.style.top = `${r.top + r.height / 2}px`;
  document.body.appendChild(f);
  f.addEventListener('animationend', () => f.remove());
}

// sword swing: while the mouse is held down the blade glows gold; it returns to steel on release.
// Module-scope: applies everywhere (menu, shop, carousels), and registers exactly once.
const swingOn = (): void => document.documentElement.classList.add('swinging');
const swingOff = (): void => document.documentElement.classList.remove('swinging');
document.addEventListener('pointerdown', swingOn, true);
document.addEventListener('pointerup', swingOff, true);
document.addEventListener('pointercancel', swingOff, true);
window.addEventListener('blur', swingOff); // don't stay lit if focus leaves mid-press

/** The name to show for the player: the CrazyGames username when logged in, else the linked itch.io
 *  account's name, else the assigned guest name. */
function displayName(): string {
  return platform?.getUsername() ?? itchIdentity()?.displayName ?? loadProfile().guestName;
}

/** Copy text to the clipboard, surviving the embedded-iframe reality: the async Clipboard API needs a
 *  clipboard-write permission the portals' iframes may not delegate to the game, so on rejection fall
 *  back to the legacy textarea + execCommand('copy') path (which rides the user gesture instead of the
 *  permission), and REPORT THE TRUTH: resolves true only when one of the two actually copied. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    /* permission denied or API absent: legacy path below */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    return ok;
  } catch {
    return false;
  }
}

/** Compact number for tight UI: 999 -> "999", 1000 -> "1k", 1200 -> "1.2k", 12345 -> "12.3k", 150000 -> "150k",
 *  millions -> "1.2M". Keeps balances/stats from growing long. */
function compactNum(n: number): string {
  if (!Number.isFinite(n)) return '0';
  const abs = Math.abs(n);
  const unit = (v: number, s: string): string => `${v < 100 ? v.toFixed(1).replace(/\.0$/, '') : String(Math.round(v))}${s}`;
  const out = abs >= 1e9 ? unit(abs / 1e9, 'B') : abs >= 1e6 ? unit(abs / 1e6, 'M') : abs >= 1e3 ? unit(abs / 1e3, 'k') : String(abs);
  return n < 0 ? `-${out}` : out;
}

/** Fill (or re-fill) a player-chip element with the current avatar/frame + name + elmas. */
function fillPlayerChip(chip: HTMLElement): void {
  const p = loadProfile();
  chip.textContent = ''; // clears children only; the click handler + title on the chip survive
  chip.append(framedAvatar(p.equipped.avatar, p.equipped.frame, 40));
  const info = el('div', 'player-chip__info');
  info.append(el('div', 'player-chip__name', displayName()), el('div', 'player-chip__elmas', `💎 ${compactNum(p.elmas)}`));
  chip.append(info);
}
/** A clickable player chip (framed avatar + name + elmas) that opens the Profile. Used as the persistent
 *  top-right corner chip on the menu and, since it sits above them, on every meta page opened over the menu. */
function buildPlayerChip(root: HTMLElement): HTMLButtonElement {
  const chip = el('button', 'player-chip');
  chip.title = 'Profile';
  chip.addEventListener('click', () => showProfile(root));
  fillPlayerChip(chip);
  return chip;
}
/** Re-sync every mounted player chip after an equip / purchase / login so the avatar + elmas stay current. */
function refreshPlayerChips(): void {
  document.querySelectorAll('.player-chip').forEach((c) => fillPlayerChip(c as HTMLElement));
  cosmeticsSync?.(); // an open online lobby also pushes the new look to the room (seat cards update live)
}
// While an online room is open, the lobby registers a sync hook here: re-equipping from the Profile
// page (reachable via the top-bar chip even inside the lobby) must update the seat card for everyone.
let cosmeticsSync: (() => void) | null = null;

// An open Shop/Profile registers a refresher here so a live elmas change (the dev gem() helper, or a
// reward that lands while the page is open) instantly re-renders affordability / owned / equipped state.
// Self-cleaning: a refresher whose page has left the DOM removes itself on the next sweep.
const metaRefreshers = new Set<() => void>();
function refreshMetaPages(): void {
  for (const fn of [...metaRefreshers]) fn();
}

// DEV-only debug helpers on the console (never ships to production). To top up elmas for testing the
// shop/frames, open the browser console and run `gem()` (adds 1000) or `gem(5000)`. `gem(-n)` removes.
if (import.meta.env.DEV) {
  const w = window as unknown as { gem: (n?: number) => number };
  w.gem = (n = 1000): number => {
    const p = addElmas(n);
    refreshPlayerChips();
    refreshMetaPages(); // re-enable buy buttons in an open Shop, refresh the Profile balance
    console.log(`[dev] elmas ${n >= 0 ? '+' : ''}${n} -> ${p.elmas}`);
    return p.elmas;
  };
  console.log('[dev] debug elmas ready: run gem() for +1000, gem(5000) for a custom amount');
}

// Profile page (inner page on the archipelago backdrop): a hero (framed avatar + name + elmas), then tabs
// (Avatar / Frame / Skin) each showing the OWNED items with an Equip / Equipped button, plus stats + links.
function showProfile(root: HTMLElement): void {
  if (document.querySelector('.metapage--profile')) return;
  const { content, page } = metaPage(root, '🧑 Profile');
  page.classList.add('metapage--profile');
  const hero = el('div', 'profile-hero');
  const tabs = el('div', 'ach-tabs');
  const tAvatar = el('button', 'ach-tab is-active', 'Avatar');
  const tFrame = el('button', 'ach-tab', 'Frame');
  const tSkin = el('button', 'ach-tab', 'Skin');
  tabs.append(tAvatar, tFrame, tSkin);
  const gridWrap = el('div', 'meta-section');
  const actsSec = el('div', 'meta-section');
  const acts = el('div', 'profile-acts');
  const achBtn = el('button', 'profile-actbtn', '🏅 Achievements');
  achBtn.addEventListener('click', () => showAchievements(root));
  const shopBtn = el('button', 'profile-actbtn', '🛒 Shop');
  shopBtn.addEventListener('click', () => showShop(root));
  acts.append(achBtn, shopBtn);
  actsSec.append(acts);
  content.append(hero, tabs, gridWrap, actsSec);

  let tab: 'avatar' | 'frame' | 'skin' = 'avatar';
  const renderHero = (): void => {
    const p = loadProfile();
    hero.textContent = '';
    hero.append(framedAvatar(p.equipped.avatar, p.equipped.frame, 100));
    const idcol = el('div', 'profile-id');
    idcol.append(el('div', 'profile-name', displayName()), el('div', 'profile-elmas', `💎 ${compactNum(p.elmas)}`));
    if (platform?.accountAvailable() && !platform.getUsername()) {
      const login = el('button', 'profile-login', '🔑 Log in to save your progress');
      login.addEventListener('click', () =>
        void platform!.login().then(() => {
          renderHero();
          refreshPlayerChips();
        }),
      );
      idcol.append(login);
    }
    // itch.io sign-in (everywhere EXCEPT CrazyGames, which has its own account system above): links
    // the cloud profile, so guest progress transfers to the itch account and follows it across devices
    if (!onCrazyGames() && !platform?.getUsername()) {
      const idn = itchIdentity();
      if (!idn) {
        const login = el('button', 'profile-login', '🔑 Sign in with itch.io');
        login.addEventListener('click', () => {
          login.disabled = true;
          login.textContent = 'Waiting for itch.io...';
          void itchLogin().then(async (got) => {
            if (got) {
              login.textContent = 'Transferring your progress...';
              await itchSyncProfile(got);
            } else {
              login.disabled = false;
              login.textContent = '🔑 Sign in with itch.io';
              alert('Sign-in did not complete. If no window opened, allow pop-ups for this game and try again.');
            }
            renderHero();
            refreshPlayerChips();
          });
        });
        idcol.append(login);
      } else {
        const linked = el('div', 'profile-linked', `🔗 itch.io: ${idn.username}`);
        const out = el('button', 'profile-login profile-login--ghost', 'Sign out');
        out.addEventListener('click', () => {
          itchLogout();
          renderHero();
          refreshPlayerChips();
        });
        idcol.append(linked, out);
      }
    }
    hero.append(idcol);
    // lifetime stats listed on the right (the previously-empty space beside the name), divided from the id
    const stats = el('div', 'profile-stats');
    const statItem = (icon: string, value: number, label: string): HTMLElement => {
      const s = el('div', 'profile-stat');
      s.append(el('div', 'profile-stat__v', `${icon} ${compactNum(value)}`), el('div', 'profile-stat__l', label));
      return s;
    };
    stats.append(statItem('🏆', p.wins, 'Wins'), statItem('🎮', p.games, 'Games'), statItem('🚩', p.tilesCaptured, 'Tiles'));
    hero.append(stats);
  };
  const equip = (kind: 'avatar' | 'frame' | 'set', id: string): void => {
    equipCosmetic(kind, id);
    renderHero();
    renderTab();
    refreshPlayerChips();
  };
  let renderedTab: 'avatar' | 'frame' | 'skin' | null = null;
  const renderTab = (): void => {
    // an equip re-render must not yank the band back to the start: keep the scroll position
    const prevRow = gridWrap.querySelector<HTMLElement>('.shop-row');
    const keepScroll = renderedTab === tab && prevRow ? prevRow.scrollLeft : 0;
    renderedTab = tab;
    gridWrap.textContent = '';
    const p = loadProfile();
    // Skins: the always-owned Default (revert to base units) + any owned race sets, each equippable.
    if (tab === 'skin') {
      const owned = SETS_ALL.filter((it) => it.id === DEFAULT_SET.id || p.unlocks.sets.includes(it.id));
      const grid = el('div', 'shop-row shop-row--sets');
      for (const it of owned) {
        grid.append(skinCard(it, p.equipped.skin, true, () => equip('set', it.id), () => el('span')));
      }
      gridWrap.append(carousel(grid));
      if (keepScroll) grid.scrollLeft = keepScroll;
      return;
    }
    const items: readonly CatalogItem[] = tab === 'avatar' ? AVATARS : FRAMES;
    const ownedIds = tab === 'avatar' ? p.unlocks.avatars : p.unlocks.frames;
    const owned = items.filter((it) => ownedIds.includes(it.id));
    if (!owned.length) {
      const box = el('div', 'ach-empty');
      box.append(el('div', 'ach-empty__icon', '📦'), el('div', 'ach-empty__text', 'Nothing here yet.'));
      gridWrap.append(box);
      return;
    }
    const grid = el('div', 'shop-row');
    for (const it of owned) {
      const cell = el('div', 'shop-item');
      cell.append(tab === 'avatar' ? framedAvatar(it.id, 'fr_default', 92) : framedAvatar(p.equipped.avatar, it.id, 92), el('div', 'shop-item__name', it.name));
      const equippedId = tab === 'avatar' ? p.equipped.avatar : p.equipped.frame;
      if (equippedId === it.id) {
        cell.append(el('div', 'shop-item__owned', '✓ Equipped'));
      } else {
        const btn = el('button', 'shop-item__btn shop-item__btn--equip', 'Equip');
        btn.addEventListener('click', () => equip(tab === 'avatar' ? 'avatar' : 'frame', it.id));
        cell.append(btn);
      }
      grid.append(cell);
    }
    gridWrap.append(carousel(grid));
    if (keepScroll) grid.scrollLeft = keepScroll;
  };
  const selectTab = (t: 'avatar' | 'frame' | 'skin', btn: HTMLElement): void => {
    tab = t;
    for (const b of [tAvatar, tFrame, tSkin]) b.classList.remove('is-active');
    btn.classList.add('is-active');
    renderTab();
  };
  tAvatar.addEventListener('click', () => selectTab('avatar', tAvatar));
  tFrame.addEventListener('click', () => selectTab('frame', tFrame));
  tSkin.addEventListener('click', () => selectTab('skin', tSkin));
  renderHero();
  renderTab();
  refreshPlayerChips();
  // keep the hero balance + tabs in sync on a live elmas change (dev gem() / reward); self-unregisters
  const refresher = (): void => {
    if (!page.isConnected) metaRefreshers.delete(refresher);
    else {
      renderHero();
      renderTab();
    }
  };
  metaRefreshers.add(refresher);
}

// A row of 4 unit previews for a skin card. Pass a race to preview that race's War Regalia skin
// sprites; pass null for the Default card, which shows base units across a few races (no skin).
function unitPreviewRow(race: string | null): HTMLElement {
  const row = el('div', 'set-preview');
  const units = ['warrior', 'archer', 'rider', 'spearman'];
  const cells: [string, string][] = race
    ? units.map((u) => [race, u] as [string, string])
    : [
        ['orc', 'warrior'],
        ['elf', 'archer'],
        ['dwarf', 'rider'],
        ['human', 'spearman'],
      ];
  const dir = race ? 'skins' : 'sprites';
  for (const [r, u] of cells) {
    const img = document.createElement('img');
    img.className = 'set-preview__unit';
    img.src = `${dir}/${r}_${u}.png`;
    img.alt = '';
    img.addEventListener('error', () => void (img.style.display = 'none'));
    row.append(img);
  }
  return row;
}

// One skin card (Default or a race's War Regalia): unit preview + name + desc + an Equip/Buy/Equipped
// control. Shared by the Shop Skins tab and the Profile Skin tab.
function skinCard(it: CatalogItem, equippedSkin: string, owned: boolean, onEquip: () => void, buyControl: () => HTMLElement): HTMLElement {
  const isDefault = it.id === DEFAULT_SET.id;
  const cell = el('div', 'set-item');
  cell.append(unitPreviewRow(isDefault ? null : (it.race ?? 'orc')));
  const info = el('div', 'set-item__info');
  info.append(
    el('div', 'shop-item__name', it.name),
    el('div', 'set-item__desc', isDefault ? 'Your units in their original look' : 'Reskins all your units of this race in-match'),
  );
  cell.append(info);
  if (equippedSkin === it.id) {
    cell.append(el('div', 'shop-item__owned', '✓ Equipped'));
  } else if (owned) {
    const eq = el('button', 'shop-item__btn shop-item__btn--equip', 'Equip');
    eq.addEventListener('click', onEquip);
    cell.append(eq);
  } else {
    cell.append(buyControl());
  }
  return cell;
}

/** Wrap a horizontal .shop-row in a carousel shell: edge arrows, mouse-wheel-to-horizontal scrolling,
 *  drag panning, and edge fades + arrows that appear only when there is more to see on that side. */
function carousel(row: HTMLElement): HTMLElement {
  const shell = el('div', 'shop-carousel');
  const left = el('button', 'shop-arrow shop-arrow--left', '‹');
  const right = el('button', 'shop-arrow shop-arrow--right', '›');
  left.title = 'Scroll left';
  right.title = 'Scroll right';
  const update = (): void => {
    const max = row.scrollWidth - row.clientWidth;
    shell.classList.toggle('at-start', row.scrollLeft <= 2);
    shell.classList.toggle('at-end', row.scrollLeft >= max - 2);
  };
  left.addEventListener('click', () => row.scrollTo({ left: row.scrollLeft - row.clientWidth * 0.8, behavior: 'smooth' }));
  right.addEventListener('click', () => row.scrollTo({ left: row.scrollLeft + row.clientWidth * 0.8, behavior: 'smooth' }));
  row.addEventListener('scroll', update, { passive: true });
  // a vertical wheel scrolls the row sideways (the natural desktop gesture for a horizontal band)
  row.addEventListener(
    'wheel',
    (e) => {
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        e.preventDefault();
        row.scrollLeft += e.deltaY;
      }
    },
    { passive: false },
  );
  // drag to pan; never starts on a button, so Buy/Equip keep their normal clicks
  let startX = 0;
  let startScroll = 0;
  let panning = false;
  row.addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).closest('button')) return;
    panning = true;
    startX = e.clientX;
    startScroll = row.scrollLeft;
    row.setPointerCapture(e.pointerId);
  });
  row.addEventListener('pointermove', (e) => {
    if (panning) row.scrollLeft = startScroll - (e.clientX - startX);
  });
  const stopPan = (): void => {
    panning = false;
  };
  row.addEventListener('pointerup', stopPan);
  row.addEventListener('pointercancel', stopPan);
  shell.append(left, row, right);
  requestAnimationFrame(update); // measure once it is laid out
  return shell;
}

// Shop page (inner page on the archipelago backdrop, not a modal): buy cosmetic frames / avatars with
// elmas. Guests are prompted to log in at purchase time on CrazyGames; off-platform the buy proceeds
// locally. A purchase plays the coin chime + floats the -price over the balance in the top bar.
function showShop(root: HTMLElement): void {
  if (document.querySelector('.metapage--shop')) return;
  const { content, page } = metaPage(root, '🛒 Shop');
  page.classList.add('metapage--shop');
  const tabs = el('div', 'ach-tabs');
  const tabFrames = el('button', 'ach-tab is-active', 'Frames');
  const tabAvatars = el('button', 'ach-tab', 'Avatars');
  const tabSkins = el('button', 'ach-tab', 'Skins');
  tabs.append(tabFrames, tabAvatars, tabSkins);
  const gridWrap = el('div', 'meta-section shop-pane'); // a fixed wood frame; the ITEMS scroll inside it
  content.append(tabs, gridWrap);
  const buy = (item: CatalogItem, kind: 'frames' | 'avatars' | 'sets', done: () => void): void => {
    const doBuy = (): void => {
      const before = loadProfile(); // snapshot so the purchase can pay out any achievement it unlocks
      const after = purchaseCosmetic(kind, item.id, item.price);
      if (!after) return; // guarded; button is disabled when unaffordable
      // 'Big Spender' progresses on itemsBought, which ONLY changes right here - the game-over choke
      // point can never see this diff (its next 'before' snapshot already includes it), so the unlock
      // must be paid + celebrated at the purchase itself or its elmas is silently lost forever
      const gained = newlyUnlocked(before, after);
      if (gained.length) {
        const reward = gained.reduce((s, a) => s + achReward(a), 0);
        if (reward > 0) addElmas(reward);
        showAchievementToasts(root, gained);
      }
      audio.sfx('coin');
      const chipElmas = page.querySelector('.player-chip__elmas');
      if (chipElmas) floatDelta(chipElmas as HTMLElement, -item.price); // float the -price over the header chip's balance
      refreshPlayerChips();
      done();
    };
    // guest gate: on CrazyGames, require login before spending; off-platform, buy locally
    if (platform?.accountAvailable() && !platform.getUsername()) {
      void platform.login().then((ok) => {
        if (ok) doBuy();
      });
      return;
    }
    doBuy();
  };
  const priceBtn = (it: CatalogItem, kind: 'frames' | 'avatars' | 'sets', onDone: () => void): HTMLButtonElement => {
    const btn = el('button', 'shop-item__btn', `💎 ${it.price}`) as HTMLButtonElement;
    if (loadProfile().elmas < it.price) {
      btn.disabled = true;
      btn.title = 'Not enough elmas';
    }
    btn.addEventListener('click', () => buy(it, kind, onDone));
    return btn;
  };
  let currentKind: 'frames' | 'avatars' | 'sets' = 'frames';
  const renderGrid = (kind: 'frames' | 'avatars' | 'sets'): void => {
    // a buy/equip re-render must not yank the pane back to the top: keep the scroll position
    const keepScroll = currentKind === kind ? gridWrap.scrollTop : 0;
    currentKind = kind;
    gridWrap.textContent = '';
    const p = loadProfile();
    // skin sets: cards with the 4-unit preview + Equip/Buy. Default (free) reverts to the base units.
    if (kind === 'sets') {
      const grid = el('div', 'set-grid');
      for (const it of SETS_ALL) {
        const owned = it.id === DEFAULT_SET.id || p.unlocks.sets.includes(it.id);
        grid.append(
          skinCard(
            it,
            p.equipped.skin,
            owned,
            () => {
              equipCosmetic('set', it.id);
              renderGrid('sets');
            },
            () => priceBtn(it, 'sets', () => renderGrid('sets')),
          ),
        );
      }
      gridWrap.append(grid);
      gridWrap.scrollTop = keepScroll;
      refreshPlayerChips();
      return;
    }
    const items = kind === 'frames' ? FRAMES : AVATARS;
    const grid = el('div', 'shop-grid');
    for (const it of items) {
      const owned = p.unlocks[kind].includes(it.id);
      const cell = el('div', 'shop-item');
      cell.append(kind === 'frames' ? framedAvatar(p.equipped.avatar, it.id, 84) : framedAvatar(it.id, 'fr_default', 84));
      cell.append(el('div', 'shop-item__name', it.name));
      if (it.comingSoon) {
        cell.classList.add('is-soon');
        cell.append(el('div', 'shop-item__soon', 'Coming soon'));
      } else if (owned) {
        const equippedId = kind === 'frames' ? p.equipped.frame : p.equipped.avatar;
        if (equippedId === it.id) {
          cell.append(el('div', 'shop-item__owned', '✓ Equipped'));
        } else {
          const eq = el('button', 'shop-item__btn shop-item__btn--equip', 'Equip');
          eq.addEventListener('click', () => {
            equipCosmetic(kind === 'frames' ? 'frame' : 'avatar', it.id);
            renderGrid(kind);
          });
          cell.append(eq);
        }
      } else {
        cell.append(priceBtn(it, kind, () => renderGrid(kind)));
      }
      grid.append(cell);
    }
    gridWrap.append(grid);
    gridWrap.scrollTop = keepScroll;
    refreshPlayerChips(); // keep the corner chip in sync after buying / equipping
  };
  const selTab = (t: 'frames' | 'avatars' | 'sets', btn: HTMLElement): void => {
    for (const b of [tabFrames, tabAvatars, tabSkins]) b.classList.remove('is-active');
    btn.classList.add('is-active');
    renderGrid(t);
  };
  tabFrames.addEventListener('click', () => selTab('frames', tabFrames));
  tabAvatars.addEventListener('click', () => selTab('avatars', tabAvatars));
  tabSkins.addEventListener('click', () => selTab('sets', tabSkins));
  renderGrid('frames');
  // re-render on a live elmas change (dev gem() / reward) so buy buttons enable; unregisters when closed
  const refresher = (): void => {
    if (!page.isConnected) metaRefreshers.delete(refresher);
    else renderGrid(currentKind);
  };
  metaRefreshers.add(refresher);
}

// Settings popup: audio controls (music / SFX volume + mute), persisted via the audio manager.
function showSettingsPopup(root: HTMLElement): void {
  const { card, close } = popup(root, '⚙ Settings');
  const slider = (label: string, get: () => number, set: (v: number) => void): HTMLElement => {
    const row = el('div', 'set-row');
    const head = el('div', 'set-head');
    const val = el('span', 'set-val', `${Math.round(get() * 100)}%`);
    head.append(el('span', 'set-label', label), val);
    const range = document.createElement('input');
    range.type = 'range';
    range.min = '0';
    range.max = '100';
    range.step = '1';
    range.className = 'set-range';
    range.value = String(Math.round(get() * 100));
    range.addEventListener('input', () => {
      set(Number(range.value) / 100);
      val.textContent = `${range.value}%`;
    });
    row.append(head, range);
    return row;
  };
  card.append(
    slider('🎵 Music', () => audio.getMusicVol(), (v) => audio.setMusicVol(v)),
    slider('🔊 Sound effects', () => audio.getSfxVol(), (v) => audio.setSfxVol(v)),
  );
  const mute = el('button', 'popup-btn', audio.isMuted() ? '🔇  Sound is muted' : '🔈  Mute all sound');
  mute.addEventListener('click', () => {
    const m = audio.toggleMuted();
    mute.textContent = m ? '🔇  Sound is muted' : '🔈  Mute all sound';
  });
  card.append(mute);
  // Conquest Trail 2.0: the in-level guidance can be turned off outright (veterans on a new device).
  // First plays of guided levels then run clean; turning it back on only affects levels not yet guided.
  const guideRead = (): boolean => {
    try {
      return localStorage.getItem('wi.guidance') !== 'off';
    } catch {
      return true;
    }
  };
  const guideLabel = (on: boolean): string => (on ? '🧭  Level guidance is on' : '🧭  Level guidance is off');
  const guidance = el('button', 'popup-btn', guideLabel(guideRead()));
  guidance.title = 'Campaign levels walk you through their new mechanic on first play. Turn off to explore unaided.';
  guidance.addEventListener('click', () => {
    try {
      const on = !guideRead();
      if (on) localStorage.removeItem('wi.guidance');
      else localStorage.setItem('wi.guidance', 'off');
      guidance.textContent = guideLabel(on);
    } catch {
      /* storage blocked: leave the label as-is */
    }
  });
  card.append(guidance);
  const btn = el('button', 'popup-btn popup-btn--primary', 'Close');
  btn.addEventListener('click', close);
  card.append(btn);
}

// In-game menu popup: Resume, Settings, Main Menu (save + leave, resumable), or Surrender (abandon the
// run; a confirm step guards it, and the caller decides what leaving means - clear the save locally, or
// close the room online).
function openGameMenu(root: HTMLElement, quitToMenu: () => void, doSurrender: () => void, onResume?: () => void): void {
  const { card, overlay, close } = popup(root, '☰ Menu');
  // every path that lands BACK IN PLAY reports it (CrazyGames gameplayStart); quit/surrender reload instead
  const resumePlay = (): void => {
    close();
    onResume?.();
  };
  // the popup's own ✕ and backdrop-click also just dismiss the menu: they must resume too
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay || (e.target as HTMLElement).closest('.panel__close')) onResume?.();
  });
  const resume = el('button', 'popup-btn popup-btn--primary', '▶  Resume');
  resume.addEventListener('click', resumePlay);
  const settings = el('button', 'popup-btn', '⚙  Settings');
  settings.addEventListener('click', () => {
    resumePlay();
    showSettingsPopup(root);
  });
  const toMenu = el('button', 'popup-btn', '🏰  Main Menu');
  toMenu.title = 'Save and return to the main menu, resume later with Continue';
  toMenu.addEventListener('click', quitToMenu); // saves the current game, then returns to the menu (offered as "Continue")
  const surrender = el('button', 'popup-btn popup-btn--danger', '🏳  Surrender');
  surrender.title = 'Abandon this game for good';
  surrender.addEventListener('click', () => {
    close();
    const confirm = popup(root, '🏳 Surrender?');
    // dismissing the confirm any way other than the red button means "keep fighting": resume play
    confirm.overlay.addEventListener('click', (e) => {
      if (e.target === confirm.overlay || (e.target as HTMLElement).closest('.panel__close')) onResume?.();
    });
    confirm.card.append(el('p', 'popup-text', 'Abandon this match for good? There is no way back in.'));
    const yes = el('button', 'popup-btn popup-btn--danger', '🏳  Surrender');
    yes.addEventListener('click', () => {
      confirm.close();
      doSurrender();
    });
    const no = el('button', 'popup-btn popup-btn--primary', 'Keep fighting');
    no.addEventListener('click', () => {
      confirm.close();
      onResume?.();
    });
    confirm.card.append(yes, no);
  });
  card.append(resume, settings, toMenu, surrender);
  // D1 hook at the OTHER exit door: most players leave mid-match from here, not from the results screen,
  // so tell them what tomorrow is worth and that their conquest keeps (claimDaily is idempotent per day).
  claimDaily();
  const streakNext = Math.min(7, (loadProfile().dailyStreak || 0) + 1);
  card.append(el('div', 'popup-note', `🎁 Tomorrow's daily reward: +${20 + (streakNext - 1) * 10} 💎  ·  your conquest saves automatically`));
}

// Each island's proper name = the home race's homeland (island.faction === island.id). Shown on the map
// and as the group header in the realm overview, so "my tile on Grimskar" is unambiguous.
// One mainland, one name. Picked from the seed's own shape so a level always reads the same, and it
// never borrows a race's homeland (the continent belongs to nobody until someone takes it).
const CONTINENT_NAMES = ['Aldermarch', 'Greyholt', 'Thornreach', 'Eastvale', 'Highmoor', 'Karnvold'] as const;
function continentName(g: GameState): string {
  const key = g.regions.length * 7 + g.factions.length * 13;
  return CONTINENT_NAMES[key % CONTINENT_NAMES.length]!;
}

function islandName(g: GameState, islandId: number): string {
  const isl = g.islands[islandId];
  if (!isl) return `Island ${islandId + 1}`;
  if (isl.faction === NEUTRAL) return 'The Wilds'; // the unclaimed ground: the mainland wilds, or the neutral centre isle every archipelago carries
  const fac = g.factions[isl.faction];
  const race = fac ? RACES.find((r) => r.key === fac.race) : undefined;
  return race?.homeland ?? `Island ${islandId + 1}`;
}

// How-to-play tips, shown on the race-confirm screen (static, so they live outside the game).
const INTRO_TIPS: ReadonlyArray<readonly [string, string]> = [
  ['🚩 Grow', 'March a unit onto a neighbouring neutral tile to take it (no cost). Build 🏛️ Markets for more income.'],
  ['⚓ Sail', 'A crossing starts at a PORT: move a unit onto a tile that holds a Harbor, then pick a coast. It boards a boat and lands at the start of your next turn.'],
  ['⚔️ Fight', 'Select a unit, then a highlighted tile: yellow to move, red to attack, orange for a sea assault. Archers shoot from 2 tiles and defenders fight back, so units have health (pips) and pick their battles.'],
  ['🔬 Research', 'Open the tech tree (top bar) for stronger units. Archery unlocks 🏹 Archers, Cavalry unlocks 🐎 Riders, plus economy and defence upgrades.'],
  ['🤝 Diplomacy', 'Ally, trade or make war with rivals. Answer their offers there too.'],
  ['▶ End Turn', 'When you are done, End Turn. Rivals act, then it is your turn again.'],
];

// --- race selection: a race grid, then a per-race confirm/how-to screen with Back (change race) + Play ---
function selectRace(root: HTMLElement): Promise<{ race: string; factions: number; islandSize: number; dominationPct: number; difficulty: Difficulty; humans: number; mapKind: MapKind } | null> {
  return new Promise((resolve) => {
    const overlay = el('div', 'race-overlay');
    root.appendChild(overlay);
    const profile = loadProfile(); // per-race mastery (wins + fastest victory) shown on each card, a reason to replay
    let players = DEFAULT_FACTIONS; // match setup: total player count (you + the AIs), 3 by default
    let mapIdx = DEFAULT_MAP_INDEX;
    let difficulty: Difficulty = 'normal'; // AI difficulty: Normal (today's tuning) or Hard (AIs press harder)
    let humans = 1; // local (hotseat) human players sharing this device; 1 = classic solo
    let mapKind: MapKind = 'isles'; // world shape: the classic archipelago, or one connected mainland
    // a small segmented toggle: a labelled row of pick-one buttons
    const toggle = (label: string, opts: string[], cur: number, onPick: (i: number) => void): HTMLElement => {
      const wrap = el('div', 'setup-group');
      wrap.append(el('span', 'setup-label', label));
      const row = el('div', 'setup-opts');
      opts.forEach((o, i) => {
        const b = el('button', `setup-opt${i === cur ? ' is-on' : ''}`, o);
        b.addEventListener('click', () => onPick(i));
        row.appendChild(b);
      });
      wrap.appendChild(row);
      return wrap;
    };

    const showGrid = (): void => {
      overlay.textContent = '';
      const card = el('div', 'race-card');
      card.append(el('h1', 'race-title', 'Choose your race'));
      const grid = el('div', 'race-grid');
      for (const race of RACES) {
        const btn = el('button', 'race-opt');
        const head = document.createElement('img');
        head.className = 'race-head';
        head.src = `sprites/head_${race.key}.png`;
        head.alt = '';
        head.style.borderColor = `#${race.color.toString(16).padStart(6, '0')}`;
        const txt = el('div', 'race-opt-txt');
        txt.append(el('div', 'race-name', race.name), el('div', 'race-passive', race.passive));
        const st = profile.byRace[race.key];
        if (st && st.wins > 0) {
          const badge = el('div', 'race-mastery');
          badge.textContent = st.bestRound != null ? `🏆 ${st.wins}  ·  ⚡ ${st.bestRound}r best` : `🏆 ${st.wins} win${st.wins === 1 ? '' : 's'}`;
          txt.append(badge);
        }
        btn.append(head, txt);
        btn.addEventListener('click', () => showConfirm(race));
        grid.appendChild(btn);
      }
      card.appendChild(grid);
      // a way back to the main menu, so "New Game" isn't a one-way door (mirrors the confirm screen's back button)
      const acts = el('div', 'race-actions');
      const back = el('button', 'intro-back', '◀ Main Menu');
      back.addEventListener('click', () => {
        overlay.remove();
        resolve(null);
      });
      acts.appendChild(back);
      card.appendChild(acts);
      overlay.appendChild(card);
    };

    const showConfirm = (race: RaceDef): void => {
      overlay.textContent = '';
      // wide two-column card (settings left, how-to-play right) so the whole thing fits one
      // viewport height with no scrolling; collapses back to one column on narrow screens
      const card = el('div', 'intro-card intro-card--confirm');
      card.append(el('h2', undefined, `You lead the ${race.name}`));
      const colorLine = el('div', 'intro-color');
      const sw = el('span', 'intro-color__swatch');
      sw.style.background = `#${race.color.toString(16).padStart(6, '0')}`;
      colorLine.append(sw, el('span', 'intro-color__txt', `Your banner is ${race.colorName}. That's you on the map.`));
      card.append(colorLine);
      // match setup: how many rivals, and how big the islands are (both re-render the card, keeping the choice)
      const setupRow = el('div', 'race-setup');
      setupRow.append(
        toggle('Players', ['3', '4', '5'], players - 3, (i) => {
          players = i + 3;
          humans = Math.min(humans, players); // never more local humans than seats
          showConfirm(race);
        }),
        toggle('Humans', ['1', '2', '3'], humans - 1, (i) => {
          humans = Math.min(i + 1, players); // hotseat: extra humans share this device, passing it each turn
          showConfirm(race);
        }),
        toggle('World', ['🏝 Isles', '⛰ Mainland'], mapKind === 'land' ? 1 : 0, (i) => {
          mapKind = i === 1 ? 'land' : 'isles';
          showConfirm(race);
        }),
        toggle('Map size', [...MAP_SIZES.map((m) => m.label)], mapIdx, (i) => {
          mapIdx = i;
          showConfirm(race);
        }),
        toggle('AI', ['Normal', 'Hard'], difficulty === 'hard' ? 1 : 0, (i) => {
          difficulty = i === 1 ? 'hard' : 'normal';
          showConfirm(race);
        }),
      );
      const goal = el(
        'p',
        'confirm-goal',
        mapKind === 'land'
          ? 'Goal: total conquest. One mainland, no sea: march on your rivals and wipe them out. Mainland politics: each realm holds ONE alliance at a time.'
          : 'Goal: total conquest. Take every region, or wipe out every rival.',
      );
      const ul = document.createElement('ul');
      for (const [k, v] of INTRO_TIPS) {
        const li = document.createElement('li');
        const b = document.createElement('b');
        b.textContent = `${k}: `;
        li.append(b, document.createTextNode(v));
        ul.appendChild(li);
      }
      const cols = el('div', 'confirm-cols');
      const leftCol = el('div', 'confirm-cols__left');
      leftCol.append(setupRow, goal);
      const rightCol = el('div', 'confirm-cols__right');
      rightCol.append(ul);
      cols.append(leftCol, rightCol);
      card.append(cols);
      const acts = el('div', 'intro-actions');
      const back = el('button', 'intro-back', '◀ Change race');
      back.addEventListener('click', showGrid);
      const play = el('button', 'intro-start', 'Play ▶');
      play.addEventListener('click', () => {
        overlay.remove();
        resolve({ race: race.key, factions: players, islandSize: MAP_SIZES[mapIdx]!.size, dominationPct: 1, difficulty, humans, mapKind });
      });
      acts.append(back, play);
      card.append(acts);
      overlay.appendChild(card);
    };

    showGrid();
  });
}

// --- tiny DOM helper ---
function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

// AI personality labels shown in the diplomatic standing (so the player can read who to court).
const STRAT_LABEL: Record<string, string> = {
  aggressive: '⚔️ Warlike',
  defensive: '🛡️ Guarded',
  opportunist: '🦊 Opportunist',
  diplomat: '🕊️ Diplomatic',
};

// What each unit is for (asymmetric matchup + range + health), shown when hovering a Train button.
// Riders beat Warriors & Archers · Archers beat Warriors & Spearmen · Warriors beat Spearmen · Spearmen beat Riders.
const UNIT_TIP: readonly string[] = [
  `<b>⚔️ Warrior</b>: cheap, durable line infantry (${unitMaxHp(0)} HP). Strong vs 🔱 Spearmen; weak vs 🐎 Riders and 🏹 Archers.`,
  `<b>🏹 Archer</b>: fragile ranged skirmisher (${unitMaxHp(1)} HP) that strikes from 2 tiles away without being hit back. An arrow lands <b>1 lighter</b> than a blade, the price of safety. It is <b>free of movement</b>: it can march and still shoot (or shoot and still march), <b>one arrow a round</b>. The exception is another 🏹 Archer, which shoots back the moment you loose. Strong vs ⚔️ Warriors and 🔱 Spearmen; weak vs 🐎 Riders.`,
  `<b>🐎 Rider</b>: fast shock cavalry (${unitMaxHp(2)} HP) that runs down infantry and skirmishers. Strong vs ⚔️ Warriors and 🏹 Archers; weak vs 🔱 Spearmen.`,
  `<b>🔱 Spearman</b>: cheap, durable anti-cavalry (${unitMaxHp(3)} HP): a braced wall the Rider breaks on. Strong vs 🐎 Riders; weak vs ⚔️ Warriors and 🏹 Archers.`,
];

// A single shared hover tooltip box, positioned next to the pointer. Pass rich HTML per element.
let tipBox: HTMLDivElement | null = null;
const hideTip = (): void => tipBox?.classList.remove('is-on');
function setTip(target: HTMLElement, html: string): void {
  const move = (e: MouseEvent): void => {
    if (!tipBox) {
      tipBox = el('div', 'tooltip');
      document.body.appendChild(tipBox);
      // Any click re-renders the region panel and destroys the hovered button, so its pointerleave
      // never fires, hide the shared box on any pointerdown so it can't get stuck on screen.
      window.addEventListener('pointerdown', hideTip, true);
    }
    tipBox.innerHTML = html;
    tipBox.classList.add('is-on');
    const pad = 14;
    const r = tipBox.getBoundingClientRect();
    let x = e.clientX + pad;
    let y = e.clientY + pad;
    if (x + r.width > window.innerWidth - 8) x = e.clientX - r.width - pad;
    if (y + r.height > window.innerHeight - 8) y = e.clientY - r.height - pad;
    tipBox.style.left = `${Math.max(8, x)}px`;
    tipBox.style.top = `${Math.max(8, y)}px`;
  };
  target.addEventListener('pointerenter', move);
  target.addEventListener('pointermove', move);
  target.addEventListener('pointerleave', () => tipBox?.classList.remove('is-on'));
}

void main();
